// src/common.ts
import { z } from "zod";
var isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD").brand();
var isoDateTime = z.string().datetime({ offset: true }).brand();
var monthNumber = z.int().min(1).max(12);
var yearNumber = z.int().min(2e3).max(2100);
var yearMonth = z.object({
  year: yearNumber,
  month: monthNumber
});
var collectionQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
  sort: z.string().optional(),
  q: z.string().optional()
});
var paginated = (item) => z.object({
  items: z.array(item),
  total: z.int().min(0),
  page: z.int().min(1),
  pageSize: z.int().min(1)
});
var activeFilter = z.union([z.boolean(), z.enum(["true", "false", "all"])]).optional().transform(
  (value) => value === void 0 || value === "all" ? void 0 : value === true || value === "true"
);
var apiError = z.object({
  statusCode: z.int(),
  message: z.string(),
  code: z.string().optional(),
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
  /** Present when a request failed validation, one entry per bad field. */
  issues: z.array(
    z.object({
      path: z.string(),
      message: z.string(),
      /** `validation.<zod code>[.<origin>]`, a key of `ERRORS`. */
      code: z.string().optional(),
      params: z.record(z.string(), z.union([z.string(), z.number()])).optional()
    })
  ).optional()
});
var agreementForm = z.enum(["uop", "zlecenie", "b2b", "inne"]);
var departmentNames = z.array(z.string());
var rentedMargin = z.number().min(0).max(500).multipleOf(0.01);
var RENTABLE_FORMS = ["uop", "zlecenie"];
var isRentableForm = (form) => RENTABLE_FORMS.includes(form);
var decimalString = z.string().regex(/^-?\d+(\.\d+)?$/, 'expected a decimal number as a string, e.g. "12.30"');
var fiscalDocumentId = z.string().min(1).max(55);
var ingestFiscalDocument = z.object({
  id: fiscalDocumentId,
  /** `FHSYGNATURA` — R, K, G, P, S, I, ZAL, VAT, H, DW, NH, … */
  series: z.string().min(1).max(10),
  /** `FHTYP` — PAS, FVS, and the corrections KPAS, KFVS. */
  type: z.string().min(1).max(10),
  /** `FHNUMER`. */
  number: z.number().int(),
  /** `FHDATAWYST`. The document belongs to this date. */
  issuedOn: isoDate,
  /**
   * `FHKIEDYUTWORZONA`. Month-end invoices are created up to two weeks after
   * their issue date, which is why the collector re-reads past days.
   */
  createdAt: z.iso.datetime({ offset: true }),
  /** `FHNETTO`. */
  nett: decimalString,
  /** `FHBRUTTO`. For a correction, the corrected value. */
  gross: decimalString,
  /**
   * Set on a correction: `FHKOREKTADO` and the value it replaces
   * (`FHKNETTO`, `FHKBRUTTO`). The correction counts as `gross − corrects.gross`.
   */
  corrects: z.object({ id: fiscalDocumentId, nett: decimalString, gross: decimalString }).nullable(),
  /**
   * `FHDZIENNIKINTERNALID` — the receipt an invoice was issued for, or the
   * invoice a receipt was turned into. Set on both sides.
   */
  linkedId: fiscalDocumentId.nullable(),
  /**
   * Printed on the fiscal printer. Optional: a collector that predates it
   * omits it, and the cash desks then take a receipt as fiscal.
   */
  isFiscal: z.boolean().optional(),
  /**
   * Who it was issued to — `FHKODKLIENTA`, `FHNAZWA`, `FHNIP`, as Izzy holds
   * them; null when it names nobody, which a receipt nearly never does.
   * Optional: a collector that predates it omits it.
   */
  buyer: z.object({
    code: z.string().max(21).nullable(),
    name: z.string().max(500).nullable(),
    nip: z.string().max(20).nullable()
  }).nullable().optional(),
  /**
   * The hotel's only: `FHREZ`, the reservations (`VHINTERNALID`) the document
   * is for — Izzy lists several with `;`; empty when it names none.
   * Optional: a collector that predates it omits it, and the stored ones stay.
   */
  reservations: z.array(z.string().min(1).max(55)).max(50).optional()
});
var ingestFiscalDocumentPayment = z.object({
  documentId: fiscalDocumentId,
  /** `FHPLATNOSC`. */
  type: z.string().min(1).max(20),
  /** `FHKWOTA`. */
  amount: decimalString
});
var productCode = z.string().min(1).max(20);
var ingestFiscalDocumentLine = z.object({
  documentId: fiscalDocumentId,
  /**
   * Order within the document, from 1, numbered by the collector. `FEINDEX`
   * is not unique within a document.
   */
  position: z.number().int().positive(),
  /** `FEKOD`. */
  productCode,
  /** `PROD.PAGRUPA` when the line was read. */
  productGroup: productCode.nullable(),
  /**
   * `FENAZWA`. Kept because it is not always the product's name: a deposit
   * line reads "Zadatek za wesele w dniu 11.09.2027", and that date is the
   * event's.
   */
  name: z.string().max(100),
  /** `FEILE`. */
  quantity: decimalString,
  /** `FECENA` — unit price, nett. */
  price: decimalString,
  /** `FEBRUTTO` — unit price, gross. The line's total is `round(quantity × gross, 2)`. */
  gross: decimalString,
  /** `FEVAT` — the rate in percent; 0 for both 0 % and exempt. */
  vatRate: z.number().int()
});
var salesLine = z.object({
  code: z.string(),
  name: z.string().nullable(),
  /** The product group's name; its code where the module keeps no names. */
  groupName: z.string().nullable(),
  quantity: decimalString,
  /** One item's gross. */
  price: decimalString,
  /** The line's gross: `round(quantity × price, 2)`. */
  gross: decimalString,
  /** Taken off a bill at the till: shown struck through. */
  cancelled: z.boolean(),
  /** In percent. */
  vatRate: z.number().int().nullable()
});
var ingestHeartbeat = z.object({
  /** When the collector read the database. */
  extractedAt: z.iso.datetime({ offset: true }),
  collectorVersion: z.string().max(32).optional()
});
var weatherCondition = z.enum([
  "clear",
  "partly",
  "cloudy",
  "fog",
  "drizzle",
  "rain",
  "snow",
  "showers",
  "thunderstorm"
]);
var weatherDay = z.object({
  date: isoDate,
  /** The raw WMO 4677 code. Kept so `condition` can be checked, never rendered. */
  code: z.int().min(0).max(99),
  condition: weatherCondition,
  /** Degrees Celsius. The maximum is what a screen shows — the day's temperature as people say it. */
  tempMean: z.number(),
  tempMax: z.number(),
  tempMin: z.number(),
  /** Hours of sun, and rain in mm; null when the provider has none. */
  sunshineHours: z.number().nullable(),
  precipitationMm: z.number().nullable()
});
var company = z.object({
  id: z.int(),
  name: z.string(),
  /**
   * The "PŁATNIK:" line of an Optima payroll PDF, normalised. An import from a
   * PDF carrying it resolves to this company. Null until linked.
   */
  optimaPayerName: z.string().nullable(),
  /** NIP. */
  taxId: z.string().nullable(),
  address: z.string().nullable(),
  /** Switched off: no main desks are added for it. What names it keeps it, and HR's lists still show it. */
  isActive: z.boolean(),
  position: z.int()
});
var sourceKind = z.enum(["izzy_rest", "izzy_hotel", "card_reader"]);
var source = z.object({
  id: z.int(),
  kind: sourceKind,
  name: z.string(),
  /** Switched off: its token is refused and no new binding offers it. */
  isActive: z.boolean()
});
var derivingModule = z.enum(["gastronomy", "hotel", "catering"]);

// src/permissions.ts
import { z as z2 } from "zod";
var PERMISSIONS = {
  "admin.users": { module: "admin", scope: null, write: true, dated: false },
  "admin.settings": { module: "admin", scope: null, write: true, dated: false },
  // What the API logged: errors, warnings and each request, with who made it.
  "admin.logs": { module: "admin", scope: null, write: false, dated: false },
  "hr.read": { module: "hr", scope: "costCentre", write: false, dated: true },
  // The employee record beyond their days: agreements, company, card.
  "hr.employees.read": { module: "hr", scope: "costCentre", write: false, dated: true },
  "hr.attendance.correct": { module: "hr", scope: "costCentre", write: true, dated: true },
  // With `hr.attendance.correct`: correct a month that has ended. Without it only the current month.
  "hr.attendance.correct-past": { module: "hr", scope: null, write: true, dated: true },
  "hr.employees.write": { module: "hr", scope: "costCentre", write: true, dated: true },
  "hr.history.read": { module: "hr", scope: "costCentre", write: false, dated: true },
  "hr.costs.read": { module: "hr", scope: null, write: false, dated: true },
  // The twelve-month reports beside the dashboard; still narrowed by `hr.read`.
  "hr.reports.read": { module: "hr", scope: null, write: false, dated: true },
  // A payroll row is reached through the departments of its agreement in force that month.
  "hr.payroll.read": { module: "hr", scope: "costCentre", write: false, dated: true },
  "hr.payroll.write": { module: "hr", scope: "costCentre", write: true, dated: true },
  // Close, reopen, restore and import act on the whole month.
  "hr.payroll.close": { module: "hr", scope: null, write: true, dated: true },
  "hr.reference.write": { module: "hr", scope: null, write: true, dated: false },
  "hr.recalculate": { module: "hr", scope: null, write: true, dated: true },
  "hr.readers.manage": { module: "hr", scope: null, write: true, dated: false },
  "hotel.read": { module: "hotel", scope: null, write: false, dated: true },
  "hotel.settings": { module: "hotel", scope: null, write: true, dated: false },
  "gastronomy.read": { module: "gastronomy", scope: "revenueCentre", write: false, dated: true },
  "gastronomy.settings": { module: "gastronomy", scope: null, write: true, dated: false },
  // Putting a remark in a split variant files a sale; it moves no figure.
  "gastronomy.remarks": { module: "gastronomy", scope: null, write: false, dated: false },
  "catering.read": { module: "catering", scope: "canteen", write: false, dated: true },
  // The month's card report (card and employee numbers) and the companies the employer bills.
  "catering.settle": { module: "catering", scope: "canteen", write: true, dated: true },
  "catering.settings": { module: "catering", scope: null, write: true, dated: false },
  "cashier.read": { module: "cashier", scope: "cashDesk", write: false, dated: true },
  "cashier.report": { module: "cashier", scope: "cashDesk", write: true, dated: true },
  "cashier.accept": { module: "cashier", scope: "cashDesk", write: true, dated: true },
  "cashier.main-desk": { module: "cashier", scope: "cashDesk", write: true, dated: true },
  // Cash pay out of a stash B desk: shows names and B pay, which payroll otherwise shows only under `hr.payroll.read`.
  "cashier.payouts": { module: "cashier", scope: "cashDesk", write: true, dated: true },
  "cashier.overview": { module: "cashier", scope: null, write: false, dated: true },
  "cashier.settings": { module: "cashier", scope: null, write: true, dated: false },
  "cashier.month-reopen": { module: "cashier", scope: null, write: true, dated: true },
  // The four-eyes exception: accept a report or transfer one did oneself.
  "cashier.accept-own": { module: "cashier", scope: null, write: true, dated: false }
};
var permission = z2.enum(Object.keys(PERMISSIONS));
var accessScopeType = z2.enum(["costCentre", "revenueCentre", "canteen", "cashDesk"]);
var SYSTEM_ROLES = {
  admin: Object.keys(PERMISSIONS),
  hr_manager: [
    "hr.read",
    "hr.employees.read",
    "hr.attendance.correct",
    "hr.employees.write",
    "hr.history.read",
    "hr.costs.read",
    "hr.reports.read"
  ],
  hr_administrator: [
    "hr.attendance.correct",
    "hr.attendance.correct-past",
    "hr.payroll.read",
    "hr.payroll.write",
    "hr.payroll.close",
    "hr.reference.write",
    "hr.recalculate",
    "hr.costs.read"
  ],
  hr_viewer: ["hr.read", "hr.employees.read", "hr.costs.read", "hr.reports.read", "hr.payroll.read"],
  department_supervisor: ["hr.read", "hr.attendance.correct"],
  gastronomy_manager: ["gastronomy.read", "gastronomy.settings", "gastronomy.remarks"],
  gastronomy_viewer: ["gastronomy.read", "gastronomy.remarks"],
  catering_manager: ["catering.read", "catering.settle", "catering.settings"],
  catering_site_manager: ["catering.read", "catering.settle"],
  catering_viewer: ["catering.read"],
  hotel_manager: ["hotel.read", "hotel.settings"],
  hotel_viewer: ["hotel.read"],
  cashier_admin: [
    "cashier.read",
    "cashier.report",
    "cashier.accept",
    "cashier.main-desk",
    "cashier.payouts",
    "cashier.overview",
    "cashier.settings",
    "cashier.month-reopen"
  ],
  cash_desk_manager: ["cashier.read", "cashier.accept"],
  main_cashier: ["cashier.main-desk", "cashier.payouts", "cashier.overview"],
  cashier: ["cashier.read", "cashier.report"],
  cashier_viewer: ["cashier.read", "cashier.overview"]
};
var systemRoleKey = z2.enum(Object.keys(SYSTEM_ROLES));
var scopeTypesOf = (permissions) => accessScopeType.options.filter((type) => permissions.some((key) => PERMISSIONS[key].scope === type));
var EVERYWHERE_ROLE = "admin";
var scopeTypesOfRole = (role) => role.key === EVERYWHERE_ROLE ? [] : scopeTypesOf(role.permissions);
var takesHistoryWindow = (role) => role.key !== EVERYWHERE_ROLE && role.permissions.some((key) => PERMISSIONS[key].dated);
var HISTORY_MONTHS_MAX = 120;
var historyMonths = z2.int().min(0).max(HISTORY_MONTHS_MAX);
var historyFloor = (months, today) => {
  if (months === null) return null;
  const [year, month] = today.split("-").map(Number);
  const index = year * 12 + (month - 1) - months;
  const floorMonth = index % 12 + 1;
  return `${Math.floor(index / 12)}-${String(floorMonth).padStart(2, "0")}-01`;
};
var grantPiece = z2.object({
  all: z2.boolean(),
  ids: z2.array(z2.int()),
  historyMonths: historyMonths.nullable(),
  from: isoDate.nullable()
});
var permissionGrant = z2.object({
  permission,
  all: z2.boolean(),
  ids: z2.array(z2.int()),
  pieces: z2.array(grantPiece)
});

// src/me.ts
import { z as z3 } from "zod";
var appModule = z3.enum(["hr", "hotel", "gastronomy", "catering", "cashier", "admin"]);
var capabilities = z3.object({
  /** `hr.costs.read`: pay rates and cost figures. */
  canSeeCosts: z3.boolean(),
  /** `hr.employees.write`: create and edit employees and agreements. */
  canEditEmployees: z3.boolean(),
  /** `hr.employees.read`: a person's agreements, company and card, and the company filter. */
  canSeeEmployeeRecords: z3.boolean(),
  /** `hr.reports.read`: the reports tab beside the dashboard. */
  canSeeReports: z3.boolean(),
  /** `hr.readers.manage`: card reader devices and their synchronise. */
  canManageReaders: z3.boolean(),
  /** `hr.reference.write`: departments and positions. */
  canManageReferenceData: z3.boolean(),
  /** `hr.recalculate`: the work-day and work-log rebuilds — whole-month and destructive. */
  canRecalculate: z3.boolean(),
  /**
   * `hr.attendance.correct`: add, edit and delete work days, card reads and
   * absences, for the employees in its cost centres. Not the month rebuild.
   */
  canCorrectAttendance: z3.boolean(),
  /** `hr.attendance.correct-past`: the same in a month that has ended. */
  canCorrectPastAttendance: z3.boolean(),
  /**
   * `hr.payroll.read`: payroll is reachable, its rows narrowed to the person's
   * cost centres. Each write has its own permission on the server.
   */
  canManagePayroll: z3.boolean(),
  /** `hr.payroll.write`: edit a payroll row and mark it ready, in its cost centres. */
  canEditPayroll: z3.boolean(),
  /** `hr.payroll.close`: close, reopen, restore and import a whole month. */
  canClosePayroll: z3.boolean(),
  /** `hr.employees.write`: assign a card to an employee. */
  canRegisterCards: z3.boolean(),
  /** `admin.users`: who signs in and what they may do. */
  canManageUsers: z3.boolean(),
  /** `admin.settings`: what every module shares — the companies, the sites, the collectors' tokens and the weather location. */
  canManageSettings: z3.boolean(),
  /** `admin.logs`: what the API logged. */
  canReadLogs: z3.boolean(),
  /** `hotel.settings`: what "sellable" means — rooms, rents, blocks, meal rates. */
  canEditHotelSettings: z3.boolean(),
  /** `hotel.settings`: qualify a night on the price-gap panel. */
  canQualifyHotelNights: z3.boolean(),
  /** `gastronomy.settings`: outlets, what counts where, menu categories. */
  canEditGastronomySettings: z3.boolean(),
  /** `catering.settings`: the sites catering reads, and which reason is the employee card. */
  canEditCateringSettings: z3.boolean(),
  /** `catering.settle`: a site's month — the card report and the gross per company the employer bills. */
  canSettleCatering: z3.boolean(),
  /** `cashier.settings`: desks, main desks, series, printers, terminals, payment types. */
  canEditCashierSettings: z3.boolean(),
  /** `cashier.report`: close a desk's day and send its cash. */
  canReportCash: z3.boolean(),
  /** `cashier.accept`: accept, return and reopen reports, and plan handovers. */
  canAcceptCashReports: z3.boolean(),
  /** `cashier.main-desk`: keep a main desk — its cash in, its book, its month end. */
  canKeepMainDesk: z3.boolean(),
  /** `cashier.payouts`: pay cash pay from payroll out of a stash B desk. */
  canPayOut: z3.boolean(),
  /** Any of `cashier.read`, `.report`, `.accept`, `.main-desk`: the cashier screens. */
  canSeeCashier: z3.boolean(),
  /** `cashier.overview`: every main desk's cash. */
  canSeeCashOverview: z3.boolean(),
  /** `gastronomy.remarks`: put a sale's remark in a split variant. */
  canAssignGastronomyRemarks: z3.boolean()
});
var meResponse = z3.object({
  /** Cognito `sub`. Stable across a username change; the audit trail's actor. */
  id: z3.string(),
  username: z3.string(),
  /** From the user's access record; null when unset. */
  displayName: z3.string().nullable(),
  /** The names of the roles the person holds. For display. Do not branch on it. */
  roles: z3.array(z3.string()),
  /**
   * Every permission the person holds, and where. The rule the server enforces;
   * `capabilities` and the fields below are read from it.
   */
  grants: z3.array(permissionGrant),
  /**
   * The cost centres (departments) `hr.read` is narrowed to. Empty and
   * unscoped: every one. Empty and scoped: none. `isScoped` says which.
   */
  departmentIds: z3.array(z3.int()),
  /** True when the user is restricted to `departmentIds`. */
  isScoped: z3.boolean(),
  /**
   * True when this user may read everything their modules hold and write nothing.
   *
   * Beside `isScoped` rather than inside `capabilities`, because it is the same
   * shape of fact: a global qualifier on the user, not a per-feature flag.
   *
   * True when the person holds no write permission (`PERMISSIONS[key].write`).
   * Most write buttons already disappear on their own, so this is for the
   * gaps: screens that render an
   * editor with no capability gate at all, because until now everybody who could
   * open them could write. It also lets the account screen say *why* the buttons
   * are missing, instead of leaving somebody to ask.
   */
  isReadOnly: z3.boolean(),
  capabilities,
  /**
   * Which applications this user may open, in display order.
   *
   * ⚠️ **Empty means none, and that is the opposite of `departmentIds`.** An
   * empty department list means "unrestricted"; an empty module list means the
   * user has been given no application at all and must be told so. Getting these
   * two the same way round is how a newly invited user comes to see a working
   * screen with nothing in it — the failure CLAUDE.md records under "an empty
   * scope means nothing, not everything".
   *
   * This decides **which nav bar renders**, exactly as `capabilities` decides
   * which nav item renders. Every route is gated by its own permission on the
   * server (`@Requires`).
   */
  modules: z3.array(appModule)
});

// src/timesheet.ts
import { z as z4 } from "zod";
var workDayWarning = z4.enum([
  /** Open shift on a date that is not today — someone never scanned out. */
  "no_exit",
  /** Open shift today. Not a defect: they are still at work. */
  "is_working",
  /** Closed, longer than {@link LONG_HOURS_SECONDS}. */
  "long_hours",
  /** Closed, shorter than {@link SHORT_HOURS_SECONDS}. */
  "short_hours",
  /**
   * A counted work day and an absence expansion claim the same calendar day.
   * The work day wins on the grid; this flag is how the disagreement is shown.
   */
  "conflict"
]);
var LONG_HOURS_SECONDS = 60 * 60 * 14;
var SHORT_HOURS_SECONDS = 60 * 60;
var MAX_SHIFT_SECONDS = 60 * 60 * 15;
var workDayType = z4.enum(["auto", "manual", "holiday", "sick", "care"]);
var WORK_DAY_TYPE_BY_VALUE = ["auto", "manual", "holiday", "sick", "care"];
var openShift = z4.object({
  workDayId: z4.int(),
  employeeId: z4.int(),
  employeeName: z4.string(),
  date: isoDate,
  startTime: isoDateTime,
  /**
   * Seconds between `startTime` and the response's `asOf`, computed by the
   * server.
   *
   * The old dashboard ran `countdown(new Date(startTime).getTime(), null, …)`
   * against the browser clock, so a workstation with a skewed clock displayed a
   * wrong elapsed time and nothing looked broken. Pairing this with `asOf` lets
   * the client tick a counter forward without ever consulting its own clock for
   * the baseline.
   */
  elapsedSeconds: z4.int(),
  /**
   * From the agreement in force today, or the most recent one. Null when the
   * person has no agreement at all.
   *
   * On the wire rather than looked up per row by the client: the dashboard shows
   * the position under the name, and a second request to resolve it would let the
   * two disagree about who holds which job.
   */
  positionName: z4.string().nullable(),
  /** The same agreement's departments, heaviest share first. Empty when there is none. */
  departmentNames,
  warning: workDayWarning
});
var closedShift = z4.object({
  workDayId: z4.int(),
  employeeId: z4.int(),
  employeeName: z4.string(),
  date: isoDate,
  startTime: isoDateTime,
  endTime: isoDateTime,
  durationSeconds: z4.int(),
  /**
   * From the agreement in force today, or the most recent one. Null when the
   * person has no agreement at all.
   *
   * On the wire rather than looked up per row by the client: the dashboard shows
   * the position under the name, and a second request to resolve it would let the
   * two disagree about who holds which job.
   */
  positionName: z4.string().nullable(),
  /** The same agreement's departments, heaviest share first. Empty when there is none. */
  departmentNames,
  warning: workDayWarning.nullable()
});
var todayResponse = z4.object({
  /**
   * The `Europe/Warsaw` calendar date the server treated as today. Returned so
   * a client near midnight, or in another zone, renders the day the data is
   * actually about rather than its own.
   */
  date: isoDate,
  /** When the server computed this. The baseline for every `elapsedSeconds`. */
  asOf: isoDateTime,
  counts: z4.object({
    /** Currently clocked in — open shifts dated today. */
    inNow: z4.int(),
    /** Shifts that started and closed today. */
    closedToday: z4.int(),
    /** Open shifts dated before today. Each one is a `no_exit`. */
    staleOpen: z4.int(),
    /** Today's closed shifts carrying a `long_hours` or `short_hours` warning. */
    anomaliesToday: z4.int()
  }),
  inNow: z4.array(openShift),
  /**
   * Shifts that started and closed today.
   *
   * The list behind `counts.closedToday` and `anomaliesToday`, so the
   * "Finished today" tile has a panel under its number.
   */
  closedToday: z4.array(closedShift),
  /**
   * Open shifts from previous days — the ones that need fixing.
   *
   * The `no_exit` rule: an unclosed shift dated before today. It needs no
   * separate threshold constant.
   */
  staleOpen: z4.array(openShift),
  anomaliesToday: z4.array(closedShift)
});

// src/month.ts
import { z as z6 } from "zod";

// src/payroll.ts
import { z as z5 } from "zod";
var monthStatus = z5.enum(["open", "closed"]);
var IMPORTED_FIELDS = [
  "grossPay",
  "socialBase",
  "pensionEmployer",
  "disabilityEmployer",
  "accidentEmployer",
  "labourFund",
  "guaranteedBenefitsFund",
  "contributionsTotal",
  "employerCost",
  "benefitsReimbursed",
  "invoices",
  "netPay"
];
var FLAGGED_FIELDS = ["grossPay", "netPay", "employerCost"];
var FREE_FIELDS = ["payB", "payBBonus", "serviceCharge"];
var EMPLOYMENT_PAY_FIELDS = ["netPay", "grossPay", "employerCost"];
var isPayrollFieldEditable = (form, field) => {
  if ((form === "b2b" || form === "inne") && EMPLOYMENT_PAY_FIELDS.includes(field)) {
    return false;
  }
  if ((form === "uop" || form === "zlecenie") && field === "invoices") {
    return false;
  }
  return true;
};
var amount = z5.number();
var nullableAmount = amount.nullable();
var payrollComponent = z5.object({
  name: z5.string().min(1).max(255),
  taxableAmount: amount,
  nontaxableAmount: amount
});
var isBailiffComponent = (name2) => /(^|[^\p{L}])kom/iu.test(name2);
var bailiffSeizureOf = (components) => {
  let total = 0;
  for (const component of components) {
    if (!isBailiffComponent(component.name)) {
      continue;
    }
    const value = component.taxableAmount !== 0 ? component.taxableAmount : component.nontaxableAmount;
    total += Math.abs(value);
  }
  return Math.round(total * 100) / 100;
};
var isSickBenefitComponent = (name2) => /(^|[^\p{L}])zas(i[łl]ek|\.)/iu.test(name2);
var sickBenefitOf = (components) => {
  let total = 0;
  for (const component of components) {
    if (!isSickBenefitComponent(component.name)) {
      continue;
    }
    const value = component.taxableAmount !== 0 ? component.taxableAmount : component.nontaxableAmount;
    total += Math.abs(value);
  }
  return Math.round(total * 100) / 100;
};
var payrollHoursBreakdown = z5.object({
  /** Real time at work, from the card reader. No absence contributes. */
  workedHours: amount,
  /** Payable holiday (urlop) days falling in this month. */
  holidayDays: z5.int().min(0),
  /** `holidayDays × 8`. Holiday is paid whole. */
  holidayHours: amount,
  /**
   * Days the absence spanned that nobody is paid for — weekends and statutory
   * holidays.
   *
   * For the screen rather than the arithmetic. A fortnight of leave paying ten
   * days reads as a miscount until the four weekend days are named, and that
   * question gets asked while looking at the figure, not in a tooltip.
   */
  holidaySkippedDays: z5.int().min(0),
  /** Payable sick (chorobowe) + care (opieka) days falling in this month. */
  sickCareDays: z5.int().min(0),
  /** `sickCareDays × 8 × 0.8`. Deliberately not a whole number of hours. */
  sickCareHours: amount,
  /** The same as `holidaySkippedDays`, for sick and care leave. */
  sickCareSkippedDays: z5.int().min(0),
  /** The three summed. What Pay B multiplies by. */
  payableHours: amount
});
var payrollEntry = z5.object({
  id: z5.int(),
  employeeId: z5.int(),
  /** `lastName firstName`, as the grid sorts. Denormalised for display only. */
  employeeName: z5.string(),
  /**
   * The two halves of that name, separately.
   *
   * Sent as well as the joined string rather than instead of it, because the grid
   * sorts by surname and splitting `employeeName` back apart cannot do that
   * reliably — a two-part surname puts the boundary in the wrong place, and there
   * is no spelling of the join that survives the round trip for every name.
   *
   * Added for the payroll screen's sort control, which offers first name and
   * surname as separate orders. Display still reads `employeeName`: two fields
   * concatenated at each of the several places that draw a name is how the grid
   * and the payslip come to render it differently.
   */
  firstName: z5.string(),
  lastName: z5.string(),
  /** Whether the person is still employed. An inactive person can still have a month. */
  isActive: z5.boolean(),
  /**
   * Which agreement this row is the payroll for.
   *
   * **Part of the row's identity, not a description of it.** A person holding a UoP
   * and a Zlecenie in one month is paid twice and gets two payslips, so they get two
   * rows — keyed `(month, employee, form, company)` — and they appear under both
   * contract groups on screen. The two carry different money and are summed once
   * each, so the per-group subtotals still add up to the month's total.
   *
   * Authoritative, unlike `agreementForms` below: it says which agreement produced
   * *these figures*, which is a fact about the row rather than a lookup against the
   * calendar. The badge on screen reads this.
   */
  form: agreementForm,
  /**
   * Which company this payslip is for — part of the row's identity alongside `form`.
   *
   * One person can have agreements with multiple companies in one month, and each
   * gets its own payroll row. Denormalised name for display.
   */
  companyId: z5.int(),
  companyName: z5.string(),
  /**
   * The job title of the agreement that produced these figures.
   *
   * **Per row, not per person.** Position lives on the agreement, so somebody
   * holding a UoP as a cook and a Zlecenie as a waiter has a different title on each
   * of their two rows — and the row is the payroll for one agreement. A single
   * "current" title on both would name a job on a row whose contract never covered
   * it.
   *
   * Null when there is no agreement to read it from: a leaver's final month, or a
   * correction landing after they left. The UI renders nothing at all rather than a
   * dash — the name is what identifies the row, and an absent job title is not a
   * fact worth its own line.
   */
  positionName: z5.string().nullable(),
  /**
   * The form(s) of agreement actually in force for this person in this month.
   *
   * Kept alongside `form` for one job: telling a leaver's row apart from a current
   * one. An **empty array with a `form` set** is the row of somebody paid in a month
   * where no agreement covers them — a final month, or a correction landing after
   * they left — and that is a real state worth showing rather than a missing value.
   *
   * Not what the badge renders. It is a span-overlap lookup and `form` is the row's
   * own answer; where they disagree, `form` is the one that was paid.
   */
  agreementForms: z5.array(agreementForm),
  // Imported. An edit away from `importedValues` is flagged on screen.
  grossPay: amount,
  socialBase: amount,
  pensionEmployer: amount,
  disabilityEmployer: amount,
  accidentEmployer: amount,
  labourFund: amount,
  guaranteedBenefitsFund: amount,
  contributionsTotal: amount,
  employerCost: amount,
  /** A **reimbursement**. Subtracts from the total — see `payrollTotal`. */
  benefitsReimbursed: amount,
  /** B2B invoices. Adds. */
  invoices: amount,
  /**
   * Live suggestion from the B2B agreement rate. Zero on non-B2B forms and when
   * there is no rate to price from.
   */
  invoicesSuggested: amount,
  /**
   * When true, `invoices` is frozen and not overwritten on read. Editing
   * `invoices` while unlocked sets this true. Same pattern as `payBLocked`.
   */
  invoicesLocked: z5.boolean(),
  /**
   * How `invoicesSuggested` was computed. The grid does not render it; a cell
   * tip can (B2B rows have no payslip).
   */
  invoicesExplanation: z5.discriminatedUnion("kind", [
    z5.object({ kind: z5.literal("none"), amount }),
    z5.object({
      kind: z5.literal("monthly_rate"),
      rate: amount,
      coveredDays: amount,
      daysInMonth: amount,
      amount
    }),
    z5.object({
      kind: z5.literal("hourly_rate"),
      rate: amount,
      hours: amount,
      amount
    })
  ]),
  /**
   * Net pay — *Do wypłaty* / Razem. The design's Netto column.
   *
   * Always present, unlike the employee-side ZUS figures: a payslip that names
   * somebody always says what they were paid, and a pre-created row of zeros
   * has 0 rather than "does not apply".
   */
  netPay: amount,
  /** Cash portion of net (*GOT*). Null when the source did not state a split. */
  netCash: nullableAmount,
  /** Transfer portion of net (*ROR*). Null when the source did not state a split. */
  netTransfer: nullableAmount,
  // Employee-side ZUS. Null = does not apply to this contract (a Zlecenie with
  // no voluntary chorobowe), 0 = applies and is zero.
  pensionEmployee: nullableAmount,
  disabilityEmployee: nullableAmount,
  sicknessEmployee: nullableAmount,
  healthContribution: nullableAmount,
  taxAdvance: nullableAmount,
  taxDeductibleCosts: nullableAmount,
  taxRelief: nullableAmount,
  taxRelinquished: nullableAmount,
  /**
   * Variable salary elements, in file order. Empty when the source carried none
   * — never null, for the same reason as `departmentNames`.
   */
  components: z5.array(payrollComponent),
  payslipSymbol: z5.string().max(64).nullable(),
  periodFrom: isoDate.nullable(),
  periodTo: isoDate.nullable(),
  // Free-text, never imported, never flagged — unless unlocked, in which case
  // `payB` tracks the live suggestion from the agreement B schema / overtime.
  payB: amount,
  payBBonus: amount,
  /** Company's own figure, like `payBBonus`. Never imported, never flagged. */
  serviceCharge: amount,
  /** Live suggestion from the agreement schema (or overtime fallback). */
  payBSuggested: amount,
  /**
   * When true, `payB` is frozen and not overwritten on read. Editing `payB`
   * while unlocked sets this true.
   */
  payBLocked: z5.boolean(),
  /**
   * How `payBSuggested` was computed — schema inputs, Netto, overtime pieces.
   * The payslip renders this; the grid does not.
   */
  payBExplanation: z5.discriminatedUnion("kind", [
    z5.object({ kind: z5.literal("none"), amount }),
    z5.object({
      kind: z5.literal("fixed_compensation_month"),
      value: amount,
      netPay: amount,
      amount
    }),
    z5.object({
      kind: z5.literal("fixed_bonus_month"),
      value: amount,
      amount
    }),
    z5.object({
      kind: z5.literal("fixed_compensation_hour"),
      value: amount,
      /**
       * The hours multiplied — `hours.payableHours`, not time at a desk.
       *
       * Kept as a plain number **as well as** the breakdown below, because it is
       * the figure in the multiplication and the sentence has to state what was
       * multiplied. The breakdown says where it came from.
       */
      hours: amount,
      /**
       * The three components behind `hours`, so the sentence can show its working.
       *
       * Optional for the estimate path, which prices an agreement rather than a
       * settled row and has no absence data to split. A reader gets the short
       * sentence there and the full one on the payroll screen.
       */
      hoursBreakdown: payrollHoursBreakdown.optional(),
      target: amount,
      netPay: amount,
      amount
    }),
    z5.object({
      kind: z5.literal("overtime"),
      hours: amount,
      monthNorm: amount,
      excessHours: amount,
      hourlyRate: amount,
      rateUnit: z5.enum(["hour", "month"]),
      amount
    })
  ]),
  /**
   * Hours from the HR export (the import). Not editable — the grid shows it
   * next to the timesheet figure so a reviewer can see the gap.
   *
   * For somebody whose hours nothing records — no card — this is **filled from
   * their norm** rather than left at zero, and `hoursAutoFilled` says so. A real
   * imported figure always wins.
   */
  hoursWorkedPayroll: amount,
  /**
   * True when `hoursWorkedPayroll` was derived rather than imported.
   *
   * Published so the screen can mark the figure instead of presenting a computed
   * number as though the HR system had sent it. That distinction is the whole
   * reason the flag exists: every other number in this row came from somewhere
   * authoritative, and this one is the house's own arithmetic.
   */
  hoursAutoFilled: z5.boolean(),
  /**
   * This row's own hours norm, once part-time and disability are applied.
   *
   * **Per row, not per month.** The month's Art. 130 figure is still published on
   * `payrollMonth.monthNorm`, but it is only what a full-time, non-disabled post
   * owes; a half-time contract owes half of it and a disabled employee seven
   * eighths. Null when the month's own norm is unknown — the holiday table having
   * run out is not a thing to scale.
   */
  effectiveNorm: amount.nullable(),
  /**
   * Why this row's norm differs from the month's, for the badge beside the name.
   *
   * Absent when it does not differ. Carries the parts rather than a sentence, so
   * the wording stays in the UI catalogues and out of the API.
   */
  normBasis: z5.object({
    etatFraction: z5.number(),
    isDisabled: z5.boolean()
  }).optional(),
  /**
   * **Hours actually worked** — card-reader time, rounded to the nearest whole
   * hour. Computed on read, never stored. Same for every form-row of the same
   * person: the timesheet is not split by contract.
   *
   * Absences are **not** in this figure: an absence prices itself at 8 h per
   * calendar day, so summing every `work_day` row would make a fortnight of leave
   * indistinguishable from a fortnight at a desk. The absence hours are published
   * beside it in `hours`, where they can be read as what they are.
   */
  hoursWorkedTimesheet: amount,
  /**
   * The month's payable hours, split by where they came from.
   *
   * **Three components that are paid differently**, which is the reason they are
   * published apart rather than summed on the server: worked time is worked time,
   * holiday is paid whole, and sick or care leave is paid at 80%. A single total
   * hides which of the three a month is made of, and those are not interchangeable
   * to anyone reading a payslip.
   *
   * `payableHours` is what Pay B multiplies by — see `payBExplanation`.
   */
  hours: payrollHoursBreakdown,
  /**
   * The percent margin this row was **rented** at, or null if the person was not
   * rented this month.
   *
   * Copied from the employee's default when the row was created and editable
   * afterwards, so it records what this month actually used rather than what the
   * person's setting happens to be now.
   *
   * Null means not rented; 0 is a valid margin meaning "reimbursed at cost". Only
   * ever non-null on a UoP or Zlecenie row.
   *
   * Nullable but **not optional**, unlike the same field on an employee. The whole
   * payroll surface is `general_manager`-only — the route answers 403 rather than
   * omitting fields — so there is no reader here to hide a cost figure from, and
   * making it optional would invent a third state the server never sends.
   */
  rentedMargin: rentedMargin.nullable(),
  /**
   * What the renting company pays back, as a **negative** figure — it is revenue.
   *
   * Derived from the margin and the row's cost, never stored. Zero when the row
   * is not rented.
   *
   * **This is what the `invoices` column shows on a rented row**, in place of
   * `invoices` above, which is always 0 there. Published separately as well so
   * the grid does not have to re-derive it and the two cannot disagree.
   */
  rentedReimbursement: amount,
  /**
   * The **derived** employer cost. `employerCost − benefitsReimbursed + invoices +
   * payB + payBBonus + serviceCharge`, where a rented row's `invoices` term is
   * its `rentedReimbursement` instead — so a rented person's total is negative,
   * and nets correctly into every sum that already exists.
   *
   * Computed on read, never stored. There is no `suma_wynagrodzenia` column: a
   * stored total is a second answer to a question the five columns already
   * answer, and it goes stale the moment one of them is edited — which is the
   * whole purpose of this screen.
   */
  payrollTotal: amount,
  /**
   * What the import wrote, for the fields it wrote.
   *
   * Null for a row created by hand rather than by an import — which is not the
   * same as an empty object, and the difference is visible on screen: no import
   * means nothing to diverge *from*, so nothing is flagged. An empty object would
   * mean "imported, and it set nothing", flagging every value the user typed.
   *
   * The free-field keys never appear here, which is what makes them
   * structurally unflaggable rather than flaggable-but-excluded-by-a-condition.
   *
   * **Partial**, not every key. Today's import writes all twelve, but a row
   * imported before a column was added to this list would hold fewer — and the
   * comparison a reader does is per key, so a missing one means "no imported value
   * to diverge from" rather than zero. Requiring every key would make an old row
   * fail its own schema on read.
   */
  importedValues: z5.partialRecord(z5.enum(IMPORTED_FIELDS), amount).nullable(),
  importedAt: isoDateTime.nullable(),
  /**
   * Whether somebody has checked this row.
   *
   * The gate on closing the month: the close refuses until every active
   * employee with an agreement in force is ready. It is a human judgement, not a
   * validation — there is no rule a machine could apply that means "an accountant
   * has looked at this".
   */
  isReady: z5.boolean(),
  /**
   * *Forma płatności*: true when the net pay goes out in cash from the
   * company's stash A, false by bank transfer. Chosen before the row is ready,
   * from the same row the month before; the cash desk lists it once ready.
   */
  payACash: z5.boolean()
});
var payrollTotals = z5.object({
  grossPay: amount,
  netPay: amount,
  employerCost: amount,
  benefitsReimbursed: amount,
  invoices: amount,
  /**
   * The rented reimbursements in this set, summed. Negative, or zero when nobody
   * in the group is rented. Already included in `invoices` above — this is the
   * part of that figure the reader may want to see on its own, not an extra term
   * to add.
   */
  rentedReimbursement: amount,
  payB: amount,
  payBBonus: amount,
  serviceCharge: amount,
  payrollTotal: amount,
  hoursWorkedPayroll: amount,
  hoursWorkedTimesheet: amount,
  /** Sum of `taxAdvance` across rows; null on a row counts as zero. */
  taxAdvance: amount
});
var payrollMonth = z5.object({
  year: yearNumber,
  month: monthNumber,
  status: monthStatus,
  closedAt: isoDateTime.nullable(),
  closedBy: z5.string().nullable(),
  reopenedAt: isoDateTime.nullable(),
  reopenedBy: z5.string().nullable(),
  /**
   * The rows the reader may see: those whose agreement in force this month has a
   * department in their `hr.payroll.read` scope. `readiness`, `totals` and
   * `byForm` count these rows only, so a scoped reader sees their part of the month.
   */
  entries: z5.array(payrollEntry),
  /**
   * The statutory hours norm for this month — Art. 130 Kodeksu pracy.
   *
   * `(full weeks × 40) + (leftover Mon–Fri × 8) − (8 × holidays not on a Sunday)`.
   * Per month rather than per row, because it is a property of the calendar and not
   * of a person: everybody on a full-time UoP shares it.
   *
   * **Nullable, and the null means "not known" rather than "zero".** The holiday
   * table is seeded a few years ahead and will one day run out; when it does, this
   * is null and the UI shows no norm. A confident 176 for a year nobody loaded is
   * worse than a blank, because on screen it looks exactly like a correct one.
   *
   * Only meaningful against a UoP. A Zlecenie is paid for the hours worked with no
   * statutory monthly norm to fall short of, and a B2B has no hours concept at all —
   * so the UI shows it on that group alone rather than implying the other two are
   * behind.
   */
  monthNorm: z5.number().nullable(),
  /**
   * How close the month is to closable.
   *
   * `active` counts employees who are active **and** have an agreement in force
   * in this month — the same population the close guard uses. Anyone else
   * cannot block a close, so counting them would show a ratio that never reaches
   * its denominator.
   */
  readiness: z5.object({
    ready: z5.int().min(0),
    active: z5.int().min(0)
  }),
  totals: payrollTotals,
  /**
   * The same totals again, once per contract form present in the month.
   *
   * **Summed on the server, beside the grand total, by the same function.** The
   * screen groups its rows by form and shows a subtotal band under each group, and
   * a browser-side sum of the rows it happens to have rendered would be a second
   * answer to a question this response already answers — the two disagreeing while
   * a refetch is in flight after an edit, which is exactly when somebody is looking.
   * One function produces both, so a subtotal and the total cannot use different
   * arithmetic.
   *
   * Only forms that have rows. An empty group is absent rather than a band of
   * zeros: nobody on a B2B this month is a fact about the month, not a figure.
   *
   * The subtotals add up to `totals` because the rows are keyed
   * `(month, employee, form)` and each belongs to exactly one group — somebody
   * holding two contracts contributes their UoP money to `uop` and their Zlecenie
   * money to `zlecenie`, and neither figure is counted twice.
   */
  byForm: z5.array(
    z5.object({
      form: agreementForm,
      /** Rows in the group, which is people only where nobody holds two contracts. */
      count: z5.int().min(0),
      totals: payrollTotals
    })
  )
});
var roundMoney = (value) => Math.round(value * 100) / 100;
var PAYROLL_FORM_ORDER = ["uop", "zlecenie", "b2b", "inne"];
var payrollTotalsOf = (entries) => {
  const sum = (pick) => roundMoney(entries.reduce((total, entry) => total + pick(entry), 0));
  return {
    grossPay: sum((entry) => entry.grossPay),
    netPay: sum((entry) => entry.netPay),
    employerCost: sum((entry) => entry.employerCost),
    benefitsReimbursed: sum((entry) => entry.benefitsReimbursed),
    invoices: sum((entry) => entry.invoices),
    /*
     * A part of `invoices` above, not a term beside it — the rented rows'
     * contribution to that column, shown on its own because a footer reading
     * "invoices: −8 800" with no B2B contractor in the month is otherwise
     * unreadable. Adding both would double-count.
     */
    rentedReimbursement: sum((entry) => entry.rentedReimbursement),
    payB: sum((entry) => entry.payB),
    payBBonus: sum((entry) => entry.payBBonus),
    serviceCharge: sum((entry) => entry.serviceCharge),
    payrollTotal: sum((entry) => entry.payrollTotal),
    hoursWorkedPayroll: sum((entry) => entry.hoursWorkedPayroll),
    hoursWorkedTimesheet: sum((entry) => entry.hoursWorkedTimesheet),
    taxAdvance: sum((entry) => entry.taxAdvance ?? 0)
  };
};
var payrollByForm = (entries) => PAYROLL_FORM_ORDER.flatMap((form) => {
  const rows = entries.filter((entry) => entry.form === form);
  return rows.length === 0 ? [] : [{ form, count: rows.length, totals: payrollTotalsOf(rows) }];
});
var payrollSummarySplit = (totals) => {
  const netPay = totals.netPay;
  const taxAdvance = totals.taxAdvance;
  const rentedReimbursement = totals.rentedReimbursement;
  const invoices = roundMoney(totals.invoices - rentedReimbursement);
  const payB = totals.payB;
  const payBBonus = totals.payBBonus;
  const serviceCharge = totals.serviceCharge;
  const contributions = roundMoney(
    totals.employerCost - netPay - taxAdvance - totals.benefitsReimbursed
  );
  const total = roundMoney(
    netPay + taxAdvance + contributions + invoices + rentedReimbursement + payB + payBBonus + serviceCharge
  );
  return {
    total,
    netPay,
    taxAdvance,
    contributions,
    invoices,
    rentedReimbursement,
    payB,
    payBBonus,
    serviceCharge
  };
};
var payrollImportRow = z5.object({
  payrollId: z5.string().max(64).optional(),
  firstName: z5.string().max(255).optional(),
  lastName: z5.string().max(255).optional(),
  /**
   * Which agreement the row is the payroll for — the file's `Umowa` column.
   *
   * **Optional, and the absence is handled rather than defaulted.** A row without it
   * is attached to the employee's sole form where they hold exactly one, and reported
   * `form-mismatch` where they hold two: an export predating this column still
   * imports for everybody with one contract, and only the genuinely ambiguous people
   * need the file corrected. A default would write somebody's Zlecenie money onto
   * their UoP row, which is the one failure this whole key exists to prevent.
   *
   * A word the parser does not recognise arrives here as `undefined` rather than a
   * guess — see `toForm` in `payroll-import-file.ts`.
   */
  form: agreementForm.optional(),
  grossPay: amount.default(0),
  socialBase: amount.default(0),
  pensionEmployer: amount.default(0),
  disabilityEmployer: amount.default(0),
  accidentEmployer: amount.default(0),
  labourFund: amount.default(0),
  guaranteedBenefitsFund: amount.default(0),
  contributionsTotal: amount.default(0),
  employerCost: amount.default(0),
  benefitsReimbursed: amount.default(0),
  invoices: amount.default(0),
  hoursWorkedPayroll: amount.default(0),
  netPay: amount.default(0),
  netCash: nullableAmount.optional(),
  netTransfer: nullableAmount.optional(),
  pensionEmployee: nullableAmount.optional(),
  disabilityEmployee: nullableAmount.optional(),
  sicknessEmployee: nullableAmount.optional(),
  healthContribution: nullableAmount.optional(),
  taxAdvance: nullableAmount.optional(),
  taxDeductibleCosts: nullableAmount.optional(),
  taxRelief: nullableAmount.optional(),
  taxRelinquished: nullableAmount.optional(),
  components: z5.array(payrollComponent).default([]),
  payslipSymbol: z5.string().max(64).optional(),
  periodFrom: isoDate.optional(),
  periodTo: isoDate.optional()
});
var importUnmatchedReason = z5.enum([
  "no-match",
  "ambiguous-name",
  "no-identifier",
  "form-mismatch"
]);
var importMatchedBy = z5.enum(["payrollId", "name", "manual"]);
var payrollImportPreview = z5.object({
  year: yearNumber,
  month: monthNumber,
  /** False when the month is closed — the preview still computes, the confirm refuses. */
  canApply: z5.boolean(),
  status: monthStatus,
  matched: z5.array(
    z5.object({
      employeeId: z5.int(),
      employeeName: z5.string(),
      /**
       * Which of the person's agreements this row was attached to.
       *
       * On the preview and not only on the applied row, because a two-contract
       * person legitimately appears **twice** in this list — and without the form
       * beside each, two lines naming the same employee read as a duplicate the
       * import is about to apply twice.
       */
      form: agreementForm,
      matchedBy: importMatchedBy,
      /** True when this employee already has a row for this form — confirming overwrites it. */
      overwrites: z5.boolean(),
      /**
       * True when the existing row is approved, so the import will **not** write it.
       *
       * An approved row is read-only everywhere else — `updateEntry` refuses one with
       * `payroll.entryLocked` — and an import that overwrote it would be the one way round
       * that rule, wiping a figure somebody signed off without anybody asking. So the
       * row is matched, listed, and discarded: reported rather than applied, for the
       * same reason `unmatched` is reported rather than guessed at.
       *
       * It stays in `matched` instead of moving to `unmatched` because it *did* match
       * a person. Moving it would say the file is wrong, when what is true is that the
       * destination is closed to it.
       */
      locked: z5.boolean(),
      row: payrollImportRow
    })
  ),
  unmatched: z5.array(
    z5.object({
      row: payrollImportRow,
      reason: importUnmatchedReason
    })
  ),
  /**
   * Active employees with an agreement in force and no row in the file.
   *
   * Not an error — a person on unpaid leave for the whole month legitimately has
   * none. It is reported because the alternative is that their absence from the
   * file is invisible, and there is no way to tell that from a file that is
   * simply short.
   */
  missing: z5.array(
    z5.object({
      employeeId: z5.int(),
      employeeName: z5.string(),
      /**
       * Which agreement has no row in the file.
       *
       * Per form, not per person: somebody holding two contracts whose file covers
       * one of them is half-missing, and reporting them as simply "missing" would
       * be wrong while reporting nothing would hide the gap entirely.
       */
      form: agreementForm
    })
  ),
  /**
   * Whether the file carried a variable component list.
   *
   * A PDF always can; a CSV never does. Reported so the wizard can say "this
   * file has no elements" rather than showing an empty table that looks like
   * every payslip had none.
   */
  hasComponents: z5.boolean(),
  /**
   * People a wizard row can be assigned to. Active employees only, sorted by
   * display name (last name then first). Matching still considers leavers; the
   * select does not list them.
   */
  assignees: z5.array(
    z5.object({
      employeeId: z5.int(),
      employeeName: z5.string(),
      forms: z5.array(agreementForm),
      payrollId: z5.string().nullable()
    })
  ),
  /** Header facts from a PDF list, or null for a spreadsheet. */
  source: z5.object({
    symbol: z5.string().nullable(),
    opis: z5.string().nullable(),
    periodFrom: isoDate.nullable(),
    periodTo: isoDate.nullable(),
    /**
     * Optima "PŁATNIK:" line from the PDF top. Null for spreadsheets or when
     * the PDF carried none.
     */
    payerName: z5.string().nullable()
  }).nullable(),
  /**
   * Resolved company from `source.payerName`, or null when not linked yet.
   *
   * Null means: the payer string is not linked to a company, or this is a
   * spreadsheet import where the user must pick. The wizard shows the picker
   * when this is null.
   */
  company: z5.object({ id: z5.int(), name: z5.string() }).nullable(),
  /**
   * Companies for the wizard picker — all of them, with their Optima payer link
   * where set.
   *
   * Sent even when `company` resolved, so the wizard can show the match and let
   * the user override it.
   */
  companyChoices: z5.array(
    z5.object({
      id: z5.int(),
      name: z5.string(),
      optimaPayerName: z5.string().nullable()
    })
  ),
  /**
   * Which contract this whole list is for. One Optima sheet is one form — every
   * row is stamped with this before matching. The wizard can override it.
   */
  form: agreementForm,
  /**
   * What the file suggested before any override. Null when the file carried no
   * form signal (a spreadsheet without an Umowa column). Shown so the user can
   * see the derivation and change it.
   */
  formDerived: agreementForm.nullable()
});
var payrollImportRequest = z5.object({
  rows: z5.array(payrollImportRow).min(1).max(2e3),
  /**
   * Which company this import is for. Optional on preview (spreads let the user
   * pick later); required to apply.
   */
  companyId: z5.int().positive().optional(),
  /**
   * When set with `companyId`, remember this Optima payer string on the company
   * — the wizard's first-time link. Ignored without `companyId`.
   */
  optimaPayerName: z5.string().trim().min(1).max(255).optional(),
  /**
   * Force every row onto this contract form before matching. One sheet is one
   * form; the wizard sends this when the user overrides the file's suggestion.
   */
  form: agreementForm.optional()
});
var payrollImportConfirm = payrollImportRequest.extend({
  expectedMatched: z5.int().min(0),
  /**
   * Parallel to `rows`. `employeeId` attaches that row to that person;
   * `null` discards it. Omit the whole array to keep auto-matching — the
   * JSON-rows endpoint and every existing test do that.
   *
   * When present it must be the same length as `rows`. The server does not
   * re-match those rows: the wizard *is* the authority on who a row belongs
   * to, and re-running the matcher would throw away the correction.
   */
  assignments: z5.array(z5.object({ employeeId: z5.int().nullable() })).optional(),
  /** Required to apply. Redeclared from optional on request. */
  companyId: z5.int().positive(),
  /** Required to apply — one sheet is one form. */
  form: agreementForm
}).refine((body) => body.assignments === void 0 || body.assignments.length === body.rows.length, {
  message: "payroll.assignmentsPerRow",
  path: ["assignments"]
});
var payrollImportResult = z5.object({
  /**
   * Rows written.
   *
   * **Not equal to the confirmed `expectedMatched`** — approved rows match
   * and are then discarded, so `applied + locked` is what echoes that count. The
   * guard still compares `expectedMatched` against the matched total, because that
   * is the number the wizard displayed.
   */
  applied: z5.int().min(0),
  /** Of those, ones that replaced an existing row rather than creating one. */
  overwritten: z5.int().min(0),
  /**
   * Matched rows discarded because the row they would have written is approved.
   *
   * Reported separately from `skipped`: an unmatched row is a file the import could
   * not read, and this is a file it read and refused to act on. Telling the user
   * "12 applied" while three of their figures silently did not move is the failure
   * this count exists to prevent.
   */
  locked: z5.int().min(0),
  skipped: z5.int().min(0)
});
var updatePayrollEntry = z5.object({
  grossPay: amount.optional(),
  socialBase: amount.optional(),
  pensionEmployer: amount.optional(),
  disabilityEmployer: amount.optional(),
  accidentEmployer: amount.optional(),
  labourFund: amount.optional(),
  guaranteedBenefitsFund: amount.optional(),
  contributionsTotal: amount.optional(),
  employerCost: amount.optional(),
  benefitsReimbursed: amount.optional(),
  invoices: amount.optional(),
  invoicesLocked: z5.boolean().optional(),
  payB: amount.optional(),
  payBBonus: amount.optional(),
  serviceCharge: amount.optional(),
  netPay: amount.optional(),
  payBLocked: z5.boolean().optional(),
  /** How the net pay goes out: true cash from stash A, false bank transfer. */
  payACash: z5.boolean().optional()
}).refine((body) => Object.keys(body).length > 0, {
  message: "validation.noFields"
});
var setPayrollReady = z5.object({
  isReady: z5.boolean()
});
var payrollMonthParams = z5.object({
  year: z5.coerce.number().pipe(yearNumber),
  month: z5.coerce.number().pipe(monthNumber)
});
var payrollClosePreview = z5.object({
  year: yearNumber,
  month: monthNumber,
  status: monthStatus,
  /** Employees active with an agreement in force this month — the denominator. */
  activeCount: z5.int().min(0),
  /** How many of those have a row marked ready. */
  readyCount: z5.int().min(0),
  /** True when `readyCount === activeCount` and the month is not already closed. */
  canClose: z5.boolean(),
  /**
   * Who is holding the close up.
   *
   * Covers two distinct cases that look the same to a user and must not be
   * conflated in the code: a person with a row nobody has ticked, and a person
   * with **no row at all** — never imported, or joined after the import ran.
   * `hasEntry` tells them apart, because the fix differs: one is a tick, the
   * other is an import or a hand-entered row.
   */
  notReady: z5.array(
    z5.object({
      employeeId: z5.int(),
      employeeName: z5.string(),
      /**
       * Which of the person's agreements is holding the close up.
       *
       * A two-contract person with one row ticked and one not appears **once**, for
       * the unticked form — and without this the list would name them with no way
       * to tell which of their two rows to go and look at.
       */
      form: agreementForm,
      hasEntry: z5.boolean()
    })
  )
});
var payrollCloseRequest = z5.object({
  expectedReady: z5.int().min(0)
});
var payrollReopenRequest = z5.object({
  reason: z5.string().trim().min(10).max(500)
});
var monthLockState = z5.object({
  status: monthStatus,
  closedAt: isoDateTime.nullable(),
  closedBy: z5.string().nullable()
});
var payrollRestorePreview = z5.object({
  year: yearNumber,
  month: monthNumber,
  status: monthStatus,
  /** True when the month is open. Closed months cannot be wiped. */
  canRestore: z5.boolean(),
  /**
   * Rows that will be deleted — imports and hand edits included, approved rows not.
   *
   * This is the number the confirm echoes, so it counts what the button destroys
   * rather than what the month holds. A count of every row would make the guard
   * trip whenever a row was approved between the preview and the confirm, which is
   * the safe direction but reports a conflict where there is none.
   */
  entryCount: z5.int().min(0),
  /** Approved rows, left exactly as they are. */
  keptCount: z5.int().min(0),
  /** Rows `ensureRows` will recreate from active employees with an agreement. */
  expectedCount: z5.int().min(0)
});
var payrollRestoreRequest = z5.object({
  expectedEntryCount: z5.int().min(0)
});

// src/month.ts
var absenceSpan = z6.object({
  startDate: isoDate,
  endDate: isoDate,
  /** True on the day the absence is recorded on — false on every day after it. */
  isFirstDay: z6.boolean()
});
var monthCellLog = z6.object({
  id: z6.int(),
  time: isoDateTime,
  type: z6.enum(["entry", "exit"]),
  /**
   * True when this read is the work day's `startId` or `endId`.
   *
   * False for debounce leftovers and other scans that did not pair into the day —
   * still shown so the strip can explain a strange duration, struck through.
   */
  used: z6.boolean(),
  /**
   * How many days after the cell it is drawn under this read was scanned.
   *
   * A night shift's exit is scanned after midnight, so it belongs to the day it
   * closes and is filed there — `1` says it was actually scanned the following
   * morning, and the strip marks it `+1`. The client cannot derive this: it is
   * given a time, not the date the time fell on.
   */
  dayOffset: z6.int().min(0).max(1).default(0),
  /**
   * True when this is not a card read at all, but the corrected day's own start
   * or end time.
   *
   * A hand-corrected day's hours are deliberately not where its readings are —
   * that mismatch is usually why somebody corrected it — so the strip showed
   * struck-through scans and nothing that matched the figure in the cell. These
   * two carry the times the day is actually billed at, and are drawn apart from
   * the reads so nobody mistakes one for something the reader recorded.
   */
  synthetic: z6.boolean().default(false)
});
var monthDayLogs = z6.object({
  day: z6.int().min(1).max(31),
  logs: z6.array(monthCellLog)
});
var monthCell = z6.object({
  /** Day of the month, 1-based. */
  day: z6.int().min(1).max(31),
  workDayId: z6.int(),
  type: workDayType,
  isDayOff: z6.boolean(),
  durationSeconds: z6.int(),
  /** Null on an absence, which has no meaningful clock times. */
  startTime: isoDateTime.nullable(),
  endTime: isoDateTime.nullable(),
  warning: workDayWarning.nullable(),
  description: z6.string().nullable(),
  /**
   * Present only on a cell belonging to an absence of more than one day, on
   * every day that absence covers — including days in a neighbouring month.
   */
  absenceSpan: absenceSpan.optional()
});
var isCountedCell = (cell) => cell.absenceSpan?.isFirstDay !== false;
var monthEmployeeRow = z6.object({
  employeeId: z6.int(),
  /** Display name, `firstName lastName`. What the grid's row header shows. */
  employeeName: z6.string(),
  /**
   * The name parts, separately, because the grid sorts by them.
   *
   * `employeeName` alone was not enough and could not be made enough. The sort
   * offered as "Nazwisko" compared the joined string, so it ordered by first
   * name under a label promising the opposite, and the obvious client-side
   * repair — split on the last space — is wrong for a double surname and for
   * anybody whose record carries a space it did not expect. The parts are
   * columns on the employee table; sending them is cheaper than guessing them
   * back out of a string this endpoint built.
   */
  firstName: z6.string(),
  lastName: z6.string(),
  positionName: z6.string().nullable(),
  /**
   * The same agreement's departments, heaviest share first. On the grid so the
   * name cell can show them without a second request.
   */
  departmentNames,
  isActive: z6.boolean(),
  /**
   * Sparse and ascending by day. Days with no record are not represented, and a
   * multi-day absence is represented on every day it covers — see `absenceSpan`.
   */
  cells: z6.array(monthCell),
  totals: z6.object({
    workedSeconds: z6.int(),
    /**
     * Absence hours by kind — one per column in the grid's aggregate block,
     * beside worked hours.
     *
     * Hours rather than days because they sit beside `workedSeconds` in a block
     * meant to be read across, and a row that mixes units cannot be. An absence
     * is stored as 8 h per calendar day it covers
     * (`WorkDayEntity.calculateTimes`), so `holidaySeconds` of 115 200 is
     * fourteen days off.
     *
     * All of an absence's hours count on the day its row is dated, exactly as
     * `workedSeconds` and `daysOff` do — the days it merely runs through carry
     * zero, so an absence crossing a month boundary is counted whole in the
     * month it started. Deliberately: splitting it would move historical payroll
     * figures.
     *
     * The three exhaust the absence kinds `workDayType` allows, which is what
     * makes the block's arithmetic checkable: every absence hour the grid draws
     * is in exactly one of them.
     */
    sickSeconds: z6.int(),
    holidaySeconds: z6.int(),
    careSeconds: z6.int(),
    daysWorked: z6.int(),
    /**
     * Absence **records**, not days absent: a fortnight's holiday counts one.
     * That is the figure payroll reads, and it is why the cells a multi-day
     * absence spans are excluded here.
     */
    daysOff: z6.int(),
    /** Days carrying any warning — the row worth looking at first. */
    daysWithWarnings: z6.int(),
    /**
     * This person's employer cost for the month, to date.
     *
     * From the estimate engine — their agreement's form, rate and rate unit
     * against the hours on this row — not a sum of the cells, which carry no
     * cost. A salaried person's figure therefore moves with the calendar
     * rather than with their attendance, which is what a salary does.
     *
     * **Omitted**, not nulled, for a user without `canSeeCosts`, so a client
     * cannot tell "no cost" from "not allowed to see cost" by shape alone.
     */
    estimatedCost: z6.number().optional()
  })
});
var absenceDays = (totals) => Math.round((totals.sickSeconds + totals.holidaySeconds + totals.careSeconds) / (8 * 3600));
var monthDay = z6.object({
  day: z6.int().min(1).max(31),
  date: isoDate,
  /** 1 = Monday … 7 = Sunday, ISO. */
  weekday: z6.int().min(1).max(7),
  isWeekend: z6.boolean(),
  /** After today in `Europe/Warsaw`. The old day cells skipped these entirely. */
  isFuture: z6.boolean(),
  isToday: z6.boolean()
});
var monthDayTotal = z6.object({
  day: z6.int().min(1).max(31),
  workedSeconds: z6.int(),
  peopleWorked: z6.int(),
  /**
   * People on an absence that covers this day — including days a multi-day
   * absence merely runs through. One person, one count; the footer shows it
   * next to the hours so a column of empty cells that are all holidays is not
   * mistaken for a quiet day.
   */
  peopleAbsent: z6.int()
});
var costSource = z6.enum(["estimate", "payroll"]);
var money = z6.object({
  /** What the people are paid, before employer contributions. */
  gross: z6.number(),
  /** What it costs the employer: gross × the multiplier for the agreement's form. */
  cost: z6.number()
});
var monthCosts = z6.object({
  source: costSource,
  /**
   * The day the month-to-date figures are counted through.
   *
   * A past month passes its last day and a future month passes 1, so `toDate`
   * never reads as zero for a month that has not started yet.
   */
  asOfDay: z6.int().min(1).max(31),
  toDate: money,
  /**
   * The whole month. For a salary that is the full rate; for hourly work it is
   * month-to-date scaled by the fraction of the agreement's month elapsed —
   * crude, and it assumes the rest of the month looks like the part that has
   * happened.
   */
  forecast: money,
  /**
   * Employer cost by department, following each agreement's weights.
   *
   * Sums to `toDate.cost` / `forecast.cost` **less** anything belonging to an
   * agreement with no department at all, which is a state the schema permits.
   * Descending by `toDate`.
   */
  byDepartment: z6.array(
    z6.object({
      departmentId: z6.int(),
      name: z6.string(),
      toDate: z6.number(),
      forecast: z6.number()
    })
  )
});
var monthResponse = z6.object({
  year: yearNumber,
  month: monthNumber,
  daysInMonth: z6.int().min(28).max(31),
  days: z6.array(monthDay),
  employees: z6.array(monthEmployeeRow),
  dayTotals: z6.array(monthDayTotal),
  monthTotals: z6.object({
    workedSeconds: z6.int(),
    /** Absence hours by kind, summed over the rows — as on a row's totals. */
    sickSeconds: z6.int(),
    holidaySeconds: z6.int(),
    careSeconds: z6.int(),
    daysWorked: z6.int(),
    /** Absence records, as on a row's totals — a fortnight's holiday counts one. */
    daysOff: z6.int(),
    /**
     * People with a cell in the grid.
     *
     * Includes someone whose only record here is an absence that started in the
     * previous month and runs into this one: they are in the month, and a count
     * that omitted them would disagree with the rows underneath it.
     */
    employeeCount: z6.int(),
    estimatedCost: z6.number().optional()
  }),
  costs: monthCosts.optional(),
  /**
   * Card reads by day — only on one-person months.
   *
   * The employee strip draws every scan under its calendar day, including days
   * with no work-day cell and scans that did not pair into a day. The full grid
   * omits this: multiplying it across hundreds of people would dwarf the payload
   * for a display that has nowhere to put it.
   */
  dayLogs: z6.array(monthDayLogs).optional(),
  /**
   * Whether this month is frozen, and by whom.
   *
   * **Not optional, and not behind `canManagePayroll`.** Everybody can see the
   * timesheet, so everybody has to be told why the edit affordances are gone —
   * hiding them without saying reads as a permissions problem or a broken screen.
   * The state carries no money, so there is nothing to omit here.
   *
   * It is advisory only. The server refuses a write to a closed month in the
   * domain layer whatever the client believes, and this field exists so the UI
   * does not have to *discover* that by being refused.
   */
  lock: monthLockState
});
var monthQuery = z6.object({
  year: z6.coerce.number().pipe(yearNumber),
  month: z6.coerce.number().pipe(monthNumber),
  companyId: z6.coerce.number().int().optional(),
  departmentId: z6.coerce.number().int().optional(),
  positionId: z6.coerce.number().int().optional(),
  /**
   * Matches the start of a first or last name, like the employee list's box.
   *
   * The same filter, spelled the same way, because "find Kowalski's month" was
   * otherwise a scroll through 651 rows — and the answer to "where is this
   * person" on a screen that is a wall of names has to be a search.
   */
  q: z6.string().trim().max(255).optional(),
  /**
   * Whether this person's hours are recorded at all.
   *
   * **Replaces `hasCard`**, which asked a question one step removed from the one
   * that matters. Having a card was a proxy for "does anything record this
   * person's attendance" — a good proxy, since a card is how attendance is
   * recorded, but a proxy: `employee.counts_hours` now states it outright, and a
   * person can have that flag off for reasons other than a missing card.
   *
   * **Defaults to `true`, and that is a deliberate reversal.** `hasCard` defaulted
   * to `all` on the argument that a grid hiding rows by default is how two numbers
   * on one screen come to disagree. That argument does not hold here: a
   * person whose hours nothing records has no timesheet to read, and every day of
   * theirs is blank by construction. Showing them by default does not add
   * information — it adds a block of empty rows that look like a fault and are
   * not. The reason it is safe is that the hiding is *not* silent: the filter
   * counts as active, so the screen shows it is filtered and offers to clear it.
   */
  countsHours: z6.enum(["true", "false", "all"]).default("true")
});
var employeeMonthQuery = z6.object({
  year: z6.coerce.number().pipe(yearNumber),
  month: z6.coerce.number().pipe(monthNumber)
});
var referenceOption = z6.object({
  id: z6.int(),
  name: z6.string()
});
var positionOption = referenceOption.extend({
  departmentIds: z6.array(z6.int())
});
var filterOptions = z6.object({
  companies: z6.array(referenceOption),
  departments: z6.array(referenceOption),
  positions: z6.array(positionOption)
});

// src/corrections.ts
import { z as z7 } from "zod";
var workLogType = z7.enum(["entry", "exit"]);
var WORK_LOG_TYPE_BY_VALUE = ["entry", "exit"];
var workLog = z7.object({
  id: z7.int(),
  employeeId: z7.int(),
  time: isoDateTime,
  type: workLogType,
  deviceId: z7.int().nullable(),
  /** True when this log opens the work day, or closes it. */
  isStart: z7.boolean(),
  isEnd: z7.boolean()
});
var auditEntry = z7.object({
  id: z7.string(),
  actorName: z7.string(),
  action: z7.enum(["create", "update", "delete"]),
  /**
   * What changed, when a history mixes kinds.
   *
   * Absent where the subject is implied — the work-day panel shows one day's
   * history and every entry is about that day. The employee screen merges the
   * person's own changes with their agreements', where "rate 24 → 26" is
   * meaningless without knowing it was an agreement.
   *
   * `cognito-user` and `department-group` are subjects with no row in this
   * database: a user is keyed by their Cognito `sub`, a group by its name. They
   * appear in no history panel that exists today and are listed because this
   * enum is the shared vocabulary for `audit_log.entity` — a value the server can
   * write but the contract cannot name is a response that fails its own schema.
   *
   * `payroll-entry` and `payroll-month` are here for exactly that reason. Neither
   * appears in a history panel yet either, and both are written from the moment
   * somebody imports a month — so omitting them would make the employee screen's
   * history 500 for anybody whose audit trail happened to include a payroll edit.
   *
   * `position-departments` is the same case again: it is written the moment an
   * admin narrows a position to a department, appears in no panel, and its absence
   * here would be a 500 rather than a missing row.
   *
   * `data-source` is written when a data source changes or a token is issued
   * for it. The TypeScript union in
   * `audit-log.entity.ts` and this enum are two lists of the same vocabulary,
   * and extending only one is how a response comes to fail its own schema.
   *
   * `hotel-rent`, `hotel-block`, `hotel-meal-rate`, `hotel-room-group`,
   * `hotel-room` and `hotel-night-qualification` are the Ustawienia / price-gap
   * writes — same reason as payroll entries: they rewrite figures and must name
   * who did it.
   *
   * `company` is a company on Administracja → Firmy: its legal data is what
   * every module prints.
   *
   * The `cashier-*` kinds are the cashier settings, a report's lifecycle,
   * transfers, the main desk's book and the payouts of cash pay. `cashier-planned-handover` entries
   * stay readable in the log; nothing writes one.
   */
  entity: z7.enum([
    "work-day",
    "work-log",
    "employee",
    "agreement",
    "device",
    "app-user",
    "access-role",
    "position-departments",
    "payroll-entry",
    "payroll-month",
    "data-source",
    "gastronomy-outlet",
    "gastronomy-point-of-sale",
    "gastronomy-series",
    "gastronomy-category",
    "gastronomy-category-group",
    "gastronomy-category-product",
    "gastronomy-group-move",
    "gastronomy-product-move",
    "gastronomy-split-product",
    "catering-card-reason",
    "catering-settlement",
    "catering-outlet",
    "catering-point-of-sale",
    "catering-series",
    "catering-category",
    "catering-category-group",
    "catering-category-product",
    "hotel-rent",
    "hotel-block",
    "hotel-meal-rate",
    "hotel-room-group",
    "hotel-room",
    "hotel-night-qualification",
    "company",
    "cashier-desk",
    "cashier-desk-source",
    "cashier-desk-series",
    "cashier-printer",
    "cashier-terminal",
    "cashier-payment-type",
    "cashier-thresholds",
    "cashier-book-category",
    "cashier-report",
    "cashier-planned-handover",
    "cashier-transfer",
    "cashier-book-entry",
    "cashier-month",
    "cashier-payout"
  ]).optional(),
  at: isoDateTime,
  /** Only the fields that changed. Null on a create (before) or delete (after). */
  before: z7.record(z7.string(), z7.unknown()).nullable(),
  after: z7.record(z7.string(), z7.unknown()).nullable()
});
var workDayDetail = z7.object({
  id: z7.int(),
  employeeId: z7.int(),
  employeeName: z7.string(),
  date: isoDate,
  /**
   * The last calendar day of an absence. Null on a worked day.
   *
   * An absence is one row spanning a range (see `days-off.ts`), and its clock
   * times are an artefact of the normalisation — which is why `startTime` and
   * `endTime` below are reported null for one. Without this the correction panel
   * could see the day an absence *starts* and had no way to learn where it ends,
   * so it could only offer clock times for a record that has none: the edit was
   * silently normalised back to 08:00–16:00 and the typed time vanished.
   */
  endDate: isoDate.nullable(),
  startTime: isoDateTime.nullable(),
  endTime: isoDateTime.nullable(),
  durationSeconds: z7.int(),
  type: workDayType,
  isDayOff: z7.boolean(),
  description: z7.string().nullable(),
  warning: workDayWarning.nullable(),
  /*
   * **No `estimatedCost`.** `work_day.rate` is frozen — never written — so a
   * per-day cost from it would read 0 for new days while older days showed a
   * figure. A number that is right for historical rows and silently zero for new
   * ones is worse than no number, because nothing on the screen distinguishes
   * them.
   *
   * There is also nothing honest to replace it with. A salaried person's day has
   * no cost of its own: the salary is paid whether or not they worked that
   * Thursday, so dividing it by days invents a figure. Cost is a month-level
   * quantity now — `MonthResponse.costs` — derived from agreements and hours
   * together.
   */
  /** The card reads on this employee-day, whether or not they bound to the day. */
  workLogs: z7.array(workLog),
  /**
   * The change history, newest first.
   *
   * Empty means "nothing recorded", which for a day predating the audit log is
   * not the same as "never changed" — `auditedFrom` lets the UI say so rather
   * than implying the record is pristine.
   */
  history: z7.array(auditEntry),
  /** When auditing began. Anything older has no history by construction. */
  auditedFrom: isoDateTime.nullable()
});
var updateWorkDay = z7.object({
  startTime: isoDateTime.optional(),
  endTime: isoDateTime.nullable().optional(),
  type: workDayType.optional(),
  isDayOff: z7.boolean().optional(),
  description: z7.string().max(50).nullable().optional()
}).refine((value) => Object.keys(value).length > 0, {
  message: "validation.noFields"
});
var createWorkDay = z7.object({
  employeeId: z7.int(),
  startTime: isoDateTime,
  endTime: isoDateTime.nullable().optional(),
  /**
   * Defaults to `manual`, not `auto`.
   *
   * Without a default, a form that sends no type would reach `calculateTimes`
   * with `undefined`, which stores an absence normalised to 08:00–16:00 hidden
   * from the list filtering `isDayOff: false`. A hand-entered day must also be
   * `manual` so recalculation leaves it alone.
   */
  type: workDayType.default("manual"),
  description: z7.string().max(50).nullable().optional()
});
var updateWorkLog = z7.object({
  time: isoDateTime.optional(),
  type: workLogType.optional()
});
var recalculatePreview = z7.object({
  year: z7.int(),
  month: z7.int(),
  from: isoDate,
  to: isoDate,
  /** Work days stored in the range. */
  storedDays: z7.int(),
  /** Of those, derived from card reads — the ones a rebuild may discard. */
  autoDays: z7.int(),
  /** Of those, hand-entered or hand-edited — untouched by a rebuild. */
  manualDays: z7.int(),
  /** Absences in range. Untouched. */
  dayOffDays: z7.int(),
  /** Card reads in range, the input a rebuild works from. */
  workLogs: z7.int(),
  employeesAffected: z7.int(),
  /**
   * True when the preview (and the rebuild it describes) is limited to the
   * employees matching the same filters as the timesheet grid.
   */
  scoped: z7.boolean()
});
var recalculateEmployeeFilters = {
  companyId: z7.coerce.number().int().positive().optional(),
  departmentId: z7.coerce.number().int().positive().optional(),
  positionId: z7.coerce.number().int().positive().optional(),
  q: z7.string().trim().max(255).optional(),
  /** Mirrors the timesheet's filter — a rebuild acts on what the screen showed. */
  countsHours: z7.enum(["true", "false", "all"]).optional()
};
var recalculatePreviewQuery = z7.object({
  year: z7.coerce.number().pipe(yearNumber),
  month: z7.coerce.number().pipe(monthNumber),
  ...recalculateEmployeeFilters
});
var recalculateRequest = z7.object({
  year: z7.int().min(2e3).max(2100),
  month: z7.int().min(1).max(12),
  /**
   * Must repeat the AUTO-day count the preview reported.
   *
   * Not ceremony: it makes the client prove it showed the user the number it is
   * about to act on. If the data moved between the preview and the confirmation —
   * someone else edited the month, a card was scanned — the counts disagree and
   * the request is refused rather than silently destroying more than was shown.
   */
  confirmAutoDays: z7.int().min(0),
  companyId: z7.int().positive().optional(),
  departmentId: z7.int().positive().optional(),
  positionId: z7.int().positive().optional(),
  q: z7.string().trim().max(255).optional(),
  countsHours: z7.enum(["true", "false", "all"]).optional()
});
var recalculateResult = z7.object({
  rebuiltDays: z7.int(),
  from: isoDate,
  to: isoDate
});

// src/work-logs.ts
import { z as z8 } from "zod";
var workLogItem = z8.object({
  id: z8.int(),
  employeeId: z8.int(),
  employeeName: z8.string(),
  time: isoDateTime,
  type: workLogType,
  cardId: z8.string(),
  /** Null for the 936 hand-entered reads, which carry `device_id = 0`. */
  deviceId: z8.int().nullable(),
  /** Null when the device is hand-entry, or has since been deleted. */
  deviceName: z8.string().nullable(),
  /**
   * Soft-deleted reads stay in the table so a rebuild can see what was ignored,
   * so the list can show them — greyed, and only when asked for.
   */
  isDeleted: z8.boolean(),
  /**
   * The work day this read is bound to, or null.
   *
   * Null covers two different situations and the UI says which: a read on a day
   * with no work day at all, and a read on a day whose work day was built from
   * two *other* reads — the third scan of the morning. Both are why this screen
   * exists.
   */
  workDayId: z8.int().nullable()
});
var workLogList = paginated(workLogItem);
var workLogQuery = z8.object({
  page: z8.coerce.number().int().min(1).default(1),
  pageSize: z8.coerce.number().int().min(1).max(500).default(50),
  sort: z8.enum(["-time", "time", "employeeName", "-employeeName"]).default("-time"),
  employeeId: z8.coerce.number().int().positive().optional(),
  /** A reader, for auditing one box on a wall. `0` selects hand-entered reads. */
  deviceId: z8.coerce.number().int().min(0).optional(),
  /**
   * A date window, inclusive, in Warsaw.
   *
   * Both optional and independent. A reader that stopped reporting is
   * found by looking at a week, not at a day.
   */
  from: isoDate.optional(),
  to: isoDate.optional(),
  includeDeleted: z8.union([z8.boolean(), z8.enum(["true", "false"])]).optional().transform((value) => value === true || value === "true")
});
var logsPreview = z8.object({
  from: isoDate,
  to: isoDate,
  /** Reads in range, excluding soft-deleted ones. The rebuild ignores those too. */
  logs: z8.int().min(0),
  entries: z8.int().min(0),
  exits: z8.int().min(0),
  employeesAffected: z8.int().min(0),
  /** Work days whose date falls in the range — what a later rebuild would revisit. */
  workDays: z8.int().min(0)
});
var recalculateLogsRequest = z8.object({
  from: isoDate,
  to: isoDate,
  employeeId: z8.int().positive().optional(),
  confirmLogs: z8.int().min(0)
}).refine((value) => value.from <= value.to, {
  message: "hr.rangeReversed",
  path: ["to"]
});
var recalculateLogsResult = z8.object({
  /**
   * Reads whose direction changed.
   *
   * Zero is the good answer and the commonest one: it means every scan in the
   * range was already labelled the way the rule would label it.
   */
  updated: z8.int().min(0),
  from: isoDate,
  to: isoDate
});

// src/dashboard.ts
import { z as z9 } from "zod";
var inactiveCandidate = z9.object({
  employeeId: z9.int(),
  employeeName: z9.string(),
  /** From the agreement in force today, or the most recent. Null when there is none. */
  positionName: z9.string().nullable(),
  /** The same agreement's departments, heaviest share first. */
  departmentNames,
  /**
   * The agreement in force today, if there is one.
   *
   * Present means deactivating this person also ends an agreement, so the confirm
   * dialog has to ask for a date. Null means it does not. The client decides which
   * form to render from this field alone rather than fetching the person first.
   */
  currentAgreementId: z9.int().nullable(),
  /**
   * The last work day on record, ever — not within the window. Null if they never
   * had one.
   *
   * Over all time on purpose: "no rows in the window" is the reason they are
   * listed, so reporting the window's own emptiness back would say nothing. What a
   * reader needs is *how long* it has been, and whether it is "since March" or
   * "never".
   */
  lastWorkDay: isoDate.nullable(),
  /** The last month carrying a payroll amount, `YYYY-MM`. Null if never paid. */
  lastPaidMonth: z9.string().regex(/^\d{4}-\d{2}$/, "expected YYYY-MM").nullable()
});
var inactiveCandidates = z9.object({
  /**
   * The window examined, inclusive: the two complete calendar months before the
   * current one.
   *
   * Returned rather than left for the client to recompute. The server's month is
   * the Warsaw one and the browser's may not be, and a panel headed "no activity
   * since June" that disagrees with the query behind it is worse than one that
   * says nothing.
   */
  from: isoDate,
  to: isoDate,
  candidates: z9.array(inactiveCandidate)
});
var deactivateEmployee = z9.object({
  /**
   * The day the agreement ends.
   *
   * **Required when an agreement is in force** and rejected when it is before that
   * agreement's `dateFrom`. Absent is only valid for somebody with no current
   * agreement — the API answers 400 rather than picking a date, because the date
   * is a payroll fact and guessing it is how a month closes wrong.
   */
  agreementEndDate: isoDate.optional()
});

// src/companies.ts
import { z as z10 } from "zod";
var adminCompany = company.extend({
  usage: z10.object({
    agreements: z10.int().min(0),
    payrollEntries: z10.int().min(0),
    desks: z10.int().min(0)
  })
});
var adminCompanyList = z10.object({ items: z10.array(adminCompany) });
var companyName = z10.string().trim().min(2).max(255);
var optionalText = (max) => z10.string().trim().max(max).nullable().transform((value) => value === "" ? null : value);
var createCompany = z10.object({
  name: companyName,
  /** Normalised by the server: trimmed, one space, upper case. */
  optimaPayerName: optionalText(255).default(null),
  taxId: optionalText(20).default(null),
  address: optionalText(500).default(null),
  isActive: z10.boolean().default(true),
  position: z10.int().min(0).default(0)
});
var updateCompany = z10.object({
  name: companyName.optional(),
  optimaPayerName: optionalText(255).optional(),
  taxId: optionalText(20).optional(),
  address: optionalText(500).optional(),
  isActive: z10.boolean().optional(),
  position: z10.int().min(0).optional()
});

// src/sources.ts
import { z as z11 } from "zod";
var instant = z11.iso.datetime({ offset: true });
var adminSource = source.extend({
  hasToken: z11.boolean(),
  tokenSetAt: instant.nullable(),
  /** The last accepted push; null until the collector has spoken. */
  lastPushAt: instant.nullable(),
  /** The collector version of the latest staged push; null until one is staged. Says when a site's collector was updated. */
  collectorVersion: z11.string().max(32).nullable(),
  /** The module that derives what it brings, set on that module's settings; null for none — then it is staged only. */
  derivedBy: derivingModule.nullable(),
  /** What names the source: a source named by anything is never deleted. */
  usage: z11.object({ desks: z11.int().min(0), stagedDays: z11.int().min(0), devices: z11.int().min(0) })
});
var adminSourceList = z11.object({ items: z11.array(adminSource) });
var sourceName = z11.string().trim().min(2).max(100);
var createSource = z11.object({ kind: sourceKind, name: sourceName, isActive: z11.boolean().default(true) });
var updateSource = z11.object({ name: sourceName.optional(), isActive: z11.boolean().optional() });
var sourceTokenIssued = z11.object({
  sourceId: z11.int(),
  /** Shown once. Write it into the collector config now. */
  token: z11.string(),
  issuedAt: instant,
  /** Whether this replaced a working token or set the first one. */
  replacedExistingToken: z11.boolean()
});
var moduleSource = source.extend({
  lastPushAt: instant.nullable(),
  days: z11.object({ staged: z11.int().min(0), derived: z11.int().min(0), failed: z11.int().min(0) })
});
var moduleSources = z11.object({
  items: z11.array(moduleSource),
  candidates: z11.array(source)
});
var cardReaderSource = source.extend({ lastPushAt: instant.nullable() });
var cardReaderSourceList = z11.object({ items: z11.array(cardReaderSource) });

// src/reference.ts
import { z as z12 } from "zod";
var referenceKind = z12.enum([
  "departments",
  "positions"
]);
var REFERENCE_KINDS = referenceKind.options;
var referenceItem = z12.object({
  id: z12.int(),
  name: z12.string(),
  /**
   * How many agreements point at this row.
   *
   * Sent so the UI can refuse a delete it knows will fail, rather than offering
   * a button whose only outcome is an error. The database has a foreign key
   * from `agreement`, so deleting a department that agreements reference raises
   * a driver error — see the 409 in `ReferenceService.remove`.
   */
  usageCount: z12.int().min(0),
  /**
   * The departments a **position** is available in. Absent for the other kinds.
   *
   * Objects rather than bare ids because the list screen renders names, and it
   * would otherwise need a second request to resolve them for a table it already
   * has one join away.
   *
   * `undefined` for a department, and never `[]` for it: for that kind the
   * field is *not applicable*, while `[]` on a position means
   * "available everywhere". One value meaning two different things across kinds
   * is how a rendering bug nobody can reproduce gets in.
   */
  departments: z12.array(referenceOption).optional()
});
var referenceList = paginated(referenceItem);
var referenceInput = z12.object({
  name: z12.string().trim().min(2).max(255),
  /**
   * Which departments a **position** is available in. Ignored for other kinds.
   *
   * **Absent and `[]` mean different things, and the difference is load-bearing.**
   * Absent leaves the restriction exactly as it was — which is what every caller
   * that predates this field sends, and what stops a rename from wiping a
   * restriction as a side effect of fixing a typo. `[]` clears it, making the
   * position available everywhere. The browser always sends the array explicitly;
   * the absent case exists for compatibility and for anyone driving the API
   * directly.
   *
   * One optional field rather than a union discriminated on the kind: `kind` is a
   * *path* parameter, so discriminating would mean copying it into the body purely
   * to switch on, and then two values could disagree about what is being created.
   * Sent for a department it is ignored rather than refused — the route stays
   * generic, and there is nothing unsafe about a field with nowhere to be stored.
   */
  departmentIds: z12.array(z12.int().positive()).max(50).optional()
});
var referenceQuery = collectionQuery;

// src/employees.ts
import { z as z13 } from "zod";
var cardDecId = z13.string().regex(/^\d{10}$/, "expected ten digits");
var rateUnit = z13.enum(["hour", "month"]);
var payBSchema = z13.enum([
  "fixed_compensation_month",
  "fixed_bonus_month",
  "fixed_compensation_hour"
]);
var agreementDepartment = z13.object({
  departmentId: z13.int().positive(),
  /** Resolved server-side. Absent on the way in. */
  departmentName: z13.string().nullable().optional(),
  weight: z13.number().positive().max(1e3)
});
var etatFraction = z13.number().gt(0).max(1).multipleOf(1e-3);
var agreement = z13.object({
  id: z13.int(),
  employeeId: z13.int(),
  companyId: z13.int(),
  positionId: z13.int(),
  /** Resolved names, so a list does not need a lookup per row. */
  companyName: z13.string().nullable(),
  positionName: z13.string().nullable(),
  /**
   * Every department this agreement splits across, with its weight. At least one
   * — an agreement attributed to nowhere would silently drop its cost out of
   * every departmental total.
   */
  departments: z13.array(agreementDepartment).min(1),
  dateFrom: isoDate,
  /**
   * Null for an open-ended agreement.
   *
   * The column is nullable and hundreds of rows use it.
   */
  dateTo: isoDate.nullable(),
  /** Omitted entirely without `canSeeCosts`. See the note above. */
  rate: z13.number().optional(),
  /** Gross per `rateUnit` for UoP and Zlecenie; the invoice value for B2B. */
  form: agreementForm,
  rateUnit,
  /**
   * Optional B schema. Both null, or both set. Omitted with `rate` when the
   * caller may not see costs — the value is pay.
   */
  payBSchema: payBSchema.nullable().optional(),
  payBValue: z13.number().positive().optional(),
  /**
   * Part of a full-time post. Always present — every agreement is full-time until
   * somebody says otherwise, so this is `1` rather than absent.
   *
   * **Not cost**, so it is not gated behind `canSeeCosts` the way `rate` is: how
   * many hours somebody is contracted for is roster information, and a scoped user
   * who can see the person's timesheet needs it to read their norm.
   */
  etatFraction: etatFraction.default(1),
  /**
   * Whether this agreement owns the employee's worked hours.
   *
   * Always present, defaulting to true, for the same reason `etatFraction` is `1`
   * rather than absent: every agreement owns its hours until a second one at a
   * second company takes the title, and a field the UI has to treat as
   * "missing means yes" is a field somebody eventually forgets to.
   *
   * Not cost, so not gated behind `canSeeCosts` — which contract the hours are
   * booked against is roster information.
   */
  isPrimary: z13.boolean().default(true)
});
var employeeListItem = z13.object({
  id: z13.int(),
  firstName: z13.string(),
  lastName: z13.string(),
  isActive: z13.boolean(),
  /**
   * Null when nobody has issued them a card. **Omitted** for a reader without
   * `hr.employees.read`, like every field of the employee record beyond their
   * days — absent, not null, so "no card" and "not yours to see" differ.
   */
  cardDecId: z13.string().nullable().optional(),
  /**
   * From the agreement in force today, or the most recent one — not from all of
   * them, so six years of history do not show as six department chips with the
   * current one indistinguishable. Omitted with `cardDecId`.
   */
  companyName: z13.string().nullable().optional(),
  /**
   * Every department that agreement splits across, heaviest share first — an
   * array because one agreement can now name several.
   *
   * Empty rather than null when the chosen agreement has no split rows or there
   * is no agreement at all: the list renders "—" for an empty array, and a
   * nullable array would give the caller two ways to say the same nothing.
   */
  departmentNames,
  positionName: z13.string().nullable(),
  /** True when the agreement the names came from covers today. */
  hasCurrentAgreement: z13.boolean(),
  /**
   * This person's identifier in the HR system (Optima akronim).
   *
   * Null until an import fills it, or somebody types it. Unique where present,
   * but not globally unique across companies — one akronim can appear in
   * multiple company payrolls.
   */
  payrollId: z13.string().max(64).nullable(),
  /**
   * The span over which this person's working day is 7 hours rather than 8.
   *
   * Null means not disabled. **Dates rather than a flag** so that setting the
   * status does not restate months already settled: a person disabled from July
   * has June computed the ordinary way, for ever. `disabledTo` is null while the
   * status is current.
   *
   * Not cost, so not gated behind `canSeeCosts` — it governs hours, which anyone
   * reading the person's timesheet needs.
   */
  disabledFrom: isoDate.nullable().optional(),
  disabledTo: isoDate.nullable().optional(),
  /**
   * Whether anything records what this person actually worked.
   *
   * False for somebody with no card. Their payroll hours are filled from the norm
   * their agreement implies rather than read as zero — see
   * `payrollEntry.hoursAutoFilled`.
   */
  countsHours: z13.boolean().optional(),
  /**
   * Whether this person is **rented** to another company, which reimburses their
   * whole cost plus {@link rentedMargin}.
   *
   * **Omitted, not false, for a reader who may not see costs.** Same rule as
   * `rate` on an agreement: a field you may not see is absent, so a client cannot
   * tell "not rented" from "not allowed to know" by shape alone. `canSeeCosts` on
   * `GET /v1/me` is what the UI branches on.
   */
  isRented: z13.boolean().optional(),
  /**
   * The **default** percent margin for this person, null when they are not
   * rented.
   *
   * A default, not a record: each payroll row copies it when created and never
   * re-reads it, so editing this governs future months only. Omitted alongside
   * `isRented` for a reader without cost visibility.
   */
  rentedMargin: rentedMargin.nullable().optional()
});
var employeeDetail = employeeListItem.extend({
  /** The reader's hex form. For support, when a card will not scan. Omitted with `cardDecId`. */
  cardId: z13.string().nullable().optional(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  /** Every agreement, newest first. Omitted for a reader without `hr.employees.read` for this person. */
  agreements: z13.array(agreement).optional(),
  /**
   * The change history, newest first.
   *
   * **Omitted, not empty, for a reader who may not see it** — a
   * department-scoped user. An empty array is the honest answer for a person
   * nobody has ever edited, so returning one here would make "you may not look"
   * indistinguishable from "nothing happened", and the UI would render the
   * reassuring version of that. Same rule as `rate`: a field you may not see is
   * absent.
   *
   * The trail names who changed what and when across the whole employee record,
   * including agreement edits. That is management information about colleagues
   * rather than the attendance data a department supervisor needs, so it stays
   * with `canEditEmployees` — `general_manager` or `Admin`.
   */
  history: z13.array(auditEntry).optional()
});
var employeeList = paginated(employeeListItem);
var employeeSort = z13.enum([
  "lastName",
  "-lastName",
  "firstName",
  "-firstName",
  "id",
  "-id"
]);
var employeeQuery = z13.object({
  page: z13.coerce.number().int().min(1).default(1),
  pageSize: z13.coerce.number().int().min(1).max(500).default(50),
  sort: employeeSort.default("lastName"),
  /** Matches the start of a first or last name. */
  q: z13.string().trim().max(255).optional(),
  /**
   * Tri-state, and it has to be: a list that can only show active people makes
   * a leaver simply vanish rather than show as inactive.
   */
  isActive: activeFilter,
  companyId: z13.coerce.number().int().positive().optional(),
  departmentId: z13.coerce.number().int().positive().optional(),
  positionId: z13.coerce.number().int().positive().optional()
});
var agreementDepartmentInput = z13.object({
  departmentId: z13.int().positive(),
  /**
   * Defaults to 1, which is what makes an even split the thing you get by not
   * thinking about it: N departments at the default weight are N equal shares.
   */
  weight: z13.number().positive().max(1e3).default(1)
});
var refinePayBSchema = (data, ctx) => {
  const hasSchema = data.payBSchema != null;
  const hasValue = data.payBValue != null;
  if (hasSchema !== hasValue) {
    ctx.addIssue({
      code: "custom",
      path: hasSchema ? ["payBValue"] : ["payBSchema"],
      message: "hr.payBIncomplete"
    });
  }
};
var refineDateRange = (data, ctx) => {
  if (data.dateFrom && data.dateTo && data.dateTo < data.dateFrom) {
    ctx.addIssue({
      code: "custom",
      path: ["dateTo"],
      message: "hr.agreementDatesReversed"
    });
  }
};
var refineAgreementWrite = (data, ctx) => {
  refineDateRange(data, ctx);
  refineRateForForm(data, ctx);
};
var refineRateForForm = (data, ctx) => {
  if (data.form === "inne") {
    if (data.rate != null) {
      ctx.addIssue({
        code: "custom",
        path: ["rate"],
        message: "hr.inneNoSalary"
      });
    }
    refinePayBSchema(data, ctx);
    return;
  }
  if (data.rate == null) {
    ctx.addIssue({
      code: "custom",
      path: ["rate"],
      message: "hr.rateRequired"
    });
  }
  refinePayBSchema(data, ctx);
};
var refineRented = (data, ctx) => {
  if (data.isRented === true && data.rentedMargin == null) {
    ctx.addIssue({
      code: "custom",
      path: ["rentedMargin"],
      message: "hr.marginRequired"
    });
  }
  if (data.isRented !== true && data.rentedMargin != null) {
    ctx.addIssue({
      code: "custom",
      path: ["rentedMargin"],
      message: "hr.marginWithoutRented"
    });
  }
};
var agreementWrite = z13.object({
  companyId: z13.int().positive(),
  positionId: z13.int().positive(),
  /**
   * At least one. An agreement with no department would drop its cost out of
   * every departmental total and out of every scoped user's view of the person —
   * which reads as the employee having vanished, not as a missing field.
   */
  departments: z13.array(agreementDepartmentInput).min(1).max(20),
  dateFrom: isoDate,
  dateTo: isoDate.nullish(),
  /**
   * Required for paid forms; omitted or null for `inne`. Applied via
   * `refineRateForForm` on every write schema that includes these fields —
   * `.superRefine` returns a ZodEffects, which has no `.extend()`, so the
   * refine cannot sit on this object itself.
   */
  rate: z13.number().min(0).max(1e6).nullish(),
  form: agreementForm,
  rateUnit,
  payBSchema: payBSchema.nullish(),
  payBValue: z13.number().positive().max(1e6).nullish(),
  /** Full-time unless stated. Absent means `1`, which is what every existing row is. */
  etatFraction: etatFraction.optional(),
  /**
   * Whether this agreement owns the employee's worked hours.
   *
   * An employee may hold two agreements at once when they are with **different
   * companies** — a Zlecenie with one firm and a Zlecenie with another. Payroll
   * shows a row per company and sums them. Hours cannot be split that way: a card
   * scan records that somebody was at work, not which contract the day belongs to.
   * So one agreement is named the owner, and that one drives the timesheet and the
   * hours-based cost estimate; the other contributes only hours-independent pay.
   *
   * Absent means **primary**, which is right for the overwhelmingly common case of
   * somebody holding exactly one agreement — and it is what every row predating
   * the flag is. The second agreement at a second company is the one somebody has
   * to think about, and it is the one they will be filling this in on.
   */
  isPrimary: z13.boolean().optional()
});
var refineDisabled = (data, ctx) => {
  if (data.disabledTo == null) {
    return;
  }
  if (data.disabledFrom == null) {
    ctx.addIssue({
      code: "custom",
      message: "hr.disabledToWithoutFrom",
      path: ["disabledFrom"]
    });
    return;
  }
  if (data.disabledTo < data.disabledFrom) {
    ctx.addIssue({
      code: "custom",
      message: "hr.disabledToBeforeFrom",
      path: ["disabledTo"]
    });
  }
};
var createEmployee = z13.object({
  firstName: z13.string().trim().min(2).max(255),
  lastName: z13.string().trim().min(2).max(255),
  isActive: z13.boolean().default(true),
  /** Optima akronim. Filled from imports when omitted. */
  payrollId: z13.string().trim().min(1).max(64).optional(),
  /** The ten-digit number printed on the card. Stored on the employee; not pushed to a reader. */
  cardDecId: cardDecId.optional(),
  agreement: agreementWrite.superRefine(refineAgreementWrite),
  /**
   * Mark this person as rented from the start. Requires `rentedMargin`.
   *
   * `.optional()` rather than `.default(false)`, unlike `isActive` above. A
   * default lands in the *output* type but not the input, so every caller that
   * builds a create body — the web form included — would be made to spell out
   * `isRented: false` for a feature it does not use. Absent already means
   * "not rented" here, and `refineRented` reads it as such.
   */
  isRented: z13.boolean().optional(),
  /** The percent margin. Required when `isRented`, refused otherwise. */
  rentedMargin: rentedMargin.optional(),
  /**
   * Already disabled when hired — the usual case, and why the form offers a
   * tick rather than making somebody find a date. It defaults to the first
   * agreement's `dateFrom`, so the status covers exactly their employment.
   */
  disabledFrom: isoDate.nullish(),
  disabledTo: isoDate.nullish(),
  /** False for somebody who will never carry a card. Absent means true. */
  countsHours: z13.boolean().optional()
}).superRefine(refineRented).superRefine(refineDisabled);
var updateEmployee = z13.object({
  firstName: z13.string().trim().min(2).max(255).optional(),
  lastName: z13.string().trim().min(2).max(255).optional(),
  isActive: z13.boolean().optional(),
  /**
   * Set or replace the HR identifier. Null clears it. Absent leaves it alone.
   */
  payrollId: z13.string().trim().min(1).max(64).nullish(),
  /**
   * A card number to issue, or null to take the card away. Absent leaves it
   * alone.
   *
   * The three-way distinction is what stops a form resubmitted with the number
   * already on file from clearing the card.
   */
  cardDecId: cardDecId.nullish(),
  /**
   * Mark the person rented, or stop renting them.
   *
   * Absent leaves both fields alone. Setting it true requires `rentedMargin` in
   * the same request; setting it false clears the margin server-side, so a
   * caller does not have to send `rentedMargin: null` as well and cannot leave a
   * margin stranded on somebody nobody rents.
   */
  isRented: z13.boolean().optional(),
  /**
   * Change the default margin. Refused unless the person is rented — either
   * already, or by `isRented: true` in the same request.
   *
   * Changing this does **not** restate months already created: each payroll row
   * holds the margin it was created with. Reopening a month and recreating its
   * rows is what picks up a new default, which is the same behaviour a rate
   * change has.
   */
  rentedMargin: rentedMargin.nullish(),
  /**
   * Start, change or clear the disabled span. Absent leaves both alone.
   *
   * Null on `disabledFrom` clears the status outright. Note that clearing it is
   * the one edit here that *does* restate history — the 7-hour rule is applied on
   * read, so removing the dates returns those months to the eight-hour
   * arithmetic. That is the intended way to undo a mistake; ending a status that
   * genuinely ran is `disabledTo`, which leaves the months it covered alone.
   */
  disabledFrom: isoDate.nullish(),
  disabledTo: isoDate.nullish(),
  /** Whether this person's worked hours are recorded at all. */
  countsHours: z13.boolean().optional()
}).superRefine(refineDisabled);
var assignCard = z13.object({
  cardDecId
});
var createAgreement = agreementWrite.extend({
  employeeId: z13.int().positive()
}).superRefine(refineAgreementWrite);
var createAgreementChange = agreementWrite.extend({
  employeeId: z13.int().positive(),
  previousAgreementId: z13.int().positive()
}).superRefine(refineAgreementWrite);
var updateAgreement = z13.object({
  companyId: z13.int().positive().optional(),
  positionId: z13.int().positive().optional(),
  departments: z13.array(agreementDepartmentInput).min(1).max(20).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.nullish(),
  rate: z13.number().min(0).max(1e6).nullish(),
  form: agreementForm.optional(),
  rateUnit: rateUnit.optional(),
  payBSchema: payBSchema.nullish(),
  payBValue: z13.number().positive().max(1e6).nullish(),
  /** Written out by hand, like every field here — see the note above. */
  etatFraction: etatFraction.optional(),
  /** Which agreement owns the hours. Written out by hand for the same reason. */
  isPrimary: z13.boolean().optional()
}).superRefine((data, ctx) => {
  refineDateRange(data, ctx);
  if (data.form === "inne" && data.rate != null) {
    ctx.addIssue({
      code: "custom",
      path: ["rate"],
      message: "hr.inneNoSalary"
    });
  }
  if (data.payBSchema !== void 0 || data.payBValue !== void 0) {
    refinePayBSchema(data, ctx);
  }
});

// src/days-off.ts
import { z as z14 } from "zod";
var dayOffType = z14.enum(["holiday", "sick", "care"]);
var DAY_OFF_TYPES = dayOffType.options;
var dayOff = z14.object({
  id: z14.int(),
  employeeId: z14.int(),
  employeeName: z14.string(),
  /**
   * The **full** work-day type, not just the three absence kinds.
   *
   * Because the data contains rows that cannot exist: two `work_day` rows are
   * `is_day_off = true` with `type = auto`, meaning "an absence the card reader
   * derived" — a hand-entered day stored as an absence because its type was
   * undefined — and the timesheet counts them as leave.
   *
   * Narrowing this to the three real kinds would make the endpoint answer 500
   * for the month containing them, which is the wrong trade: a list that refuses
   * to load is worse than a list showing an impossible value, and showing it is how anybody
   * finds out. Booking and editing stay restricted to the three (see `createDayOff`).
   */
  type: workDayType,
  startDate: isoDate,
  endDate: isoDate,
  /** Whole calendar days, inclusive of both ends. `duration / 8 h`. */
  days: z14.int().min(1),
  description: z14.string().nullable()
});
var dayOffList = paginated(dayOff);
var dayOffQuery = z14.object({
  page: z14.coerce.number().int().min(1).default(1),
  pageSize: z14.coerce.number().int().min(1).max(500).default(50),
  sort: z14.enum(["startDate", "-startDate", "employeeName", "-employeeName"]).default("-startDate"),
  /**
   * Both or neither. Absent means every absence on record, which is what the
   * screen offers as "all".
   *
   * `z.coerce.number()` before the range check, because these arrive from a query
   * string: `yearNumber` alone is `z.int()`, which rejects the string `"2026"` and
   * makes the whole list 400.
   */
  year: z14.coerce.number().pipe(yearNumber).optional(),
  month: z14.coerce.number().pipe(monthNumber).optional(),
  employeeId: z14.coerce.number().int().positive().optional(),
  type: dayOffType.optional()
});
var createDayOff = z14.object({
  employeeId: z14.int().positive(),
  type: dayOffType,
  startDate: isoDate,
  endDate: isoDate,
  description: z14.string().trim().max(50).optional()
}).refine((value) => value.endDate >= value.startDate, {
  message: "hr.dayOffEndBeforeStart",
  path: ["endDate"]
});
var updateDayOff = z14.object({
  type: dayOffType.optional(),
  startDate: isoDate.optional(),
  endDate: isoDate.optional(),
  description: z14.string().trim().max(50).nullish()
}).refine(
  (value) => value.startDate === void 0 || value.endDate === void 0 || value.endDate >= value.startDate,
  { message: "hr.dayOffEndBeforeStart", path: ["endDate"] }
);
var dayOffTypeValue = (type) => workDayType.options.indexOf(type);

// src/devices.ts
import { z as z15 } from "zod";
var ipv4 = z15.string().trim().refine(
  (value) => {
    const parts = value.split(".");
    return parts.length === 4 && parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
  },
  { message: "validation.ipv4" }
);
var ingestMode = z15.enum(["pull", "push_shadow", "push", "collector"]);
var device = z15.object({
  id: z15.int(),
  locationName: z15.string(),
  ip: z15.string(),
  port: z15.int(),
  isActive: z15.boolean(),
  /** Whether the last poll reached it. Written by the synchroniser, not by a user. */
  isConnected: z15.boolean(),
  /** When the synchroniser last talked to it, successfully or not. */
  lastSynced: isoDateTime.nullable(),
  /** The timestamp of the newest scan taken from it — how far its log is read. */
  lastLogTime: isoDateTime.nullable(),
  /**
   * HTTP Basic credentials.
   *
   * `username` is shown because it is not a secret and the operator has to know
   * which account a failing test used. The password is represented by
   * `hasPassword` and `secretUpdatedAt` and **never leaves the API** — there is
   * no shape of this contract in which a reader password reaches a browser.
   *
   * `hasPassword: false` means this device falls back to the shared
   * `READER_PASSWORD` until someone sets one.
   */
  username: z15.string().nullable(),
  hasPassword: z15.boolean(),
  secretUpdatedAt: isoDateTime.nullable(),
  ingestMode,
  /**
   * The token in the device's push URL, shown in full.
   *
   * Unlike the password, this is **not** withheld: whoever configures a reader has
   * to type this into the device and then read it back off the device's own screen
   * to check it, so a UI that hides it makes the one job it exists for impossible.
   * What protects it is that it can be regenerated per device, and that it lets
   * nobody read anything — the route it names accepts scans and answers with an
   * empty document.
   *
   * Null means this device has never been configured for push.
   */
  ingestToken: z15.string().nullable(),
  /**
   * The whole URL to type into the device's `Resource` field, ready to copy.
   *
   * Assembled by the API from `READER_PUSH_BASE_URL`, not by the browser: the
   * address a reader on an office network can reach is a fact about the deployment,
   * and the page is often opened on a laptop that is nowhere near it. Null when
   * there is no token yet, or when nobody has configured that address — and the
   * screen says which, because "no URL" for the two reasons needs two different
   * things done about it.
   */
  ingestUrl: z15.string().nullable(),
  /** When the device last sent anything, scan or heartbeat — for `collector`, when its collector last sent a reading of it. Null until then. */
  lastPushAt: isoDateTime.nullable(),
  /** The `card_reader` data source whose collector reads it; set exactly when `ingestMode` is `collector`. */
  sourceId: z15.int().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime
});
var deviceList = paginated(device);
var deviceQuery = z15.object({
  page: z15.coerce.number().int().min(1).default(1),
  pageSize: z15.coerce.number().int().min(1).max(500).default(50),
  sort: z15.enum(["locationName", "-locationName", "lastSynced", "-lastSynced"]).default("locationName"),
  q: z15.string().trim().max(255).optional(),
  isActive: activeFilter
});
var createDevice = z15.object({
  locationName: z15.string().trim().min(2).max(255),
  ip: ipv4,
  port: z15.int().min(1).max(65535),
  isActive: z15.boolean().default(true),
  /**
   * Omitted leaves the device on the shared `READER_PASSWORD`; a string sets its
   * own; `null` clears it back to the fallback.
   *
   * Three states rather than two, because "leave it alone" and "remove it" are
   * different intentions and a form that only knows about empty strings cannot
   * express both. `.trim()` is deliberately absent: a device password may end in
   * a space, and silently changing what was typed produces a credential that
   * fails with no visible reason.
   */
  username: z15.string().min(1).max(64).nullish(),
  password: z15.string().min(1).max(255).nullish(),
  /** Absent is `pull`. */
  ingestMode: ingestMode.optional(),
  /** The `card_reader` source of its collector: required with `collector`, refused with any other mode. */
  sourceId: z15.int().positive().nullish()
});
var updateDevice = z15.object({
  locationName: z15.string().trim().min(2).max(255).optional(),
  ip: ipv4.optional(),
  port: z15.int().min(1).max(65535).optional(),
  isActive: z15.boolean().optional(),
  username: z15.string().min(1).max(64).nullish(),
  password: z15.string().min(1).max(255).nullish(),
  /**
   * Moving a reader between ingest paths — the whole of the rollout, from the
   * API's side.
   *
   * `.optional()` and no default, for the reason the comment above gives: a
   * default here would put `ingestMode: 'pull'` into every unrelated PATCH and
   * would take a migrated reader back off push the next time somebody renamed it.
   * The token is not settable — it is minted when a device first moves off `pull`,
   * and regenerated through its own endpoint.
   */
  ingestMode: ingestMode.optional(),
  /**
   * The `card_reader` source of its collector, with `collector` only. Absent
   * keeps it; moving off `collector` clears it.
   */
  sourceId: z15.int().positive().nullish()
});
var synchronizeRequest = z15.object({
  from: isoDateTime.optional(),
  to: isoDateTime.optional()
});
var deviceTestFailure = z15.enum([
  /** No TCP connection: DNS, refused, timed out, or a closed port forward. */
  "unreachable",
  /** It answered 401. The credentials are wrong — the one failure with an obvious next step. */
  "unauthorized",
  /** It answered, but not like an Inveo reader. Usually a router's login page on a forwarded port. */
  "not-a-reader",
  /** It answered as a reader and then failed on something specific. */
  "error"
]);
var deviceTestResult = z15.object({
  deviceId: z15.int(),
  reachable: z15.boolean(),
  failure: deviceTestFailure.nullable(),
  /** Safe to show: it never contains a credential, only what the device said. */
  message: z15.string().nullable(),
  latencyMs: z15.int().nullable(),
  /** Which credentials were used — not what they were. */
  credentials: z15.object({
    username: z15.string(),
    source: z15.enum(["device", "environment"])
  }),
  /** Whether this API would refuse to write to the device (`READER_READ_ONLY`). */
  readOnly: z15.boolean(),
  status: z15.object({
    model: z15.string().nullable(),
    firmware: z15.string().nullable(),
    deviceTime: isoDateTime.nullable(),
    /**
     * Device clock minus server clock, in seconds. The reason this is on the
     * screen: a drifted RTC writes drifted timestamps into the log, every
     * downstream number inherits the drift, and nothing else in the system can
     * see it.
     */
    clockSkewSeconds: z15.int().nullable(),
    storedLogs: z15.int().nullable(),
    antennaEnabled: z15.boolean().nullable(),
    /**
     * The status document verbatim, capped.
     *
     * Not debugging residue: the manual documents one firmware on one model,
     * and the devices in the field are neither. ZEM is an RCP Uni on 0.71 and
     * reports no clock through the fields 0.86 documents — which is
     * unresolvable from here without seeing what it does send. Anything this
     * parser learns to read, it learns from this.
     */
    raw: z15.string()
  }).nullable(),
  log: z15.object({
    /** The CSV header line, verbatim. Its column order differs between firmwares. */
    header: z15.string(),
    rows: z15.int(),
    oldest: isoDateTime.nullable(),
    newest: isoDateTime.nullable(),
    /** Of the newest rows, how many are not already in `card_log`. */
    wouldStore: z15.int(),
    /** …and how many of those resolve to an employee by the production rule. */
    matchedEmployees: z15.int(),
    unmatchedCardIds: z15.array(z15.string()),
    /** The last few lines, unparsed, so a format surprise is visible not inferred. */
    sample: z15.array(z15.string())
  }).nullable(),
  /**
   * Why the log is missing, when it is.
   *
   * A reader that answers its status and not its log file is **not a failed
   * test** — it is reachable, the credentials work, and one section is
   * incomplete. Reporting that as "the test could not be completed" is what the
   * first run against ZEM did, and it buried the two facts that mattered
   * (reachable, authenticated) under a red box caused by a timeout of ours.
   */
  logError: z15.string().nullable(),
  /** How many cards the device holds. Read-only, and not compared to `device_card`:
   * the device's card list plays no part in attendance, so the two need not agree. */
  cardsOnDevice: z15.int().nullable()
});
var synchronizeResult = z15.object({
  deviceId: z15.int(),
  /**
   * Scans stored. Zero is a normal answer: the device answered and had nothing
   * newer, which is different from a failure and reads differently in the UI.
   */
  saved: z15.int().min(0),
  /** The device row afterwards, so the list can show the new `lastSynced`. */
  device
});

// src/access.ts
import { z as z16 } from "zod";
var accessRoleKey = z16.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
var accessScope = z16.discriminatedUnion("mode", [
  z16.object({ mode: z16.literal("all") }),
  z16.object({ mode: z16.literal("list"), ids: z16.array(z16.int().positive()).min(1).max(500) })
]);
var accessAssignment = z16.object({
  role: accessRoleKey,
  costCentres: accessScope.optional(),
  revenueCentres: accessScope.optional(),
  canteens: accessScope.optional(),
  cashDesks: accessScope.optional(),
  historyMonths: historyMonths.nullable().optional()
});
var accessRole = z16.object({
  key: accessRoleKey,
  name: z16.string(),
  /** Defined in code: cannot be edited or deleted. */
  isSystem: z16.boolean(),
  permissions: z16.array(permission),
  /** How many people hold it. A custom role is deleted only when nobody does. */
  holders: z16.int()
});
var accessRoleList = z16.object({ items: z16.array(accessRole) });
var saveAccessRole = z16.object({
  name: z16.string().trim().min(1).max(100),
  permissions: z16.array(permission).min(1).max(100)
});
var appUser = z16.object({
  id: z16.int(),
  /** Who the person is: access is given to an email. */
  email: z16.string(),
  /** Cognito's username — an opaque id. Null until the first sign-in links the row. */
  username: z16.string().nullable(),
  /** Null until the first sign-in links the row. */
  cognitoSub: z16.string().nullable(),
  /** Shown instead of the email wherever a person is named. */
  displayName: z16.string().nullable(),
  /** False: no access, from the next request. With Cognito configured, the sign-in is disabled too. */
  enabled: z16.boolean(),
  createdAt: isoDateTime,
  /** Null when the person has never signed in — an invitation not yet taken. */
  lastSeenAt: isoDateTime.nullable(),
  assignments: z16.array(accessAssignment),
  /** The applications the assignments open. Read-only: it follows from the permissions. */
  modules: z16.array(appModule),
  /** The caller. The rails refuse self-demotion and self-disable; the UI says so first. */
  isSelf: z16.boolean()
});
var appUserList = z16.object({ items: z16.array(appUser) });
var updateAppUser = z16.object({
  displayName: z16.string().trim().min(1).max(100).nullable(),
  assignments: z16.array(accessAssignment).max(50)
});
var inviteAppUser = updateAppUser.extend({
  email: z16.string().trim().toLowerCase().email().max(255)
});
var setAppUserEnabled = z16.object({ enabled: z16.boolean() });
var accessOptions = z16.object({
  /** System roles first, in `SYSTEM_ROLES` order, then the custom ones by name. */
  roles: z16.array(accessRole),
  costCentres: z16.array(z16.object({ id: z16.int(), name: z16.string() })),
  revenueCentres: z16.array(z16.object({ id: z16.int(), name: z16.string() })),
  /** Catering's sites; a site marked deleted is listed last, so a list that names it still reads. */
  canteens: z16.array(z16.object({ id: z16.int(), name: z16.string(), isDeleted: z16.boolean() })),
  cashDesks: z16.array(
    z16.object({ id: z16.int(), name: z16.string(), kind: z16.enum(["sales", "main"]), isActive: z16.boolean() })
  ),
  /**
   * False when Cognito is not configured: nobody is invited (a row is added for
   * an account that already exists), and enable/disable changes access only,
   * not the sign-in.
   */
  canInvite: z16.boolean()
});

// src/preferences.ts
import { z as z17 } from "zod";
var generalPreferences = z17.object({
  locale: z17.enum(["pl", "en"]).optional(),
  theme: z17.enum(["light", "dark", "system"]).optional(),
  /** Opened after sign-in when the person sees more than one application. */
  defaultModule: appModule.nullable().optional()
});
var screenKey = z17.string().regex(/^[a-z][a-z0-9-]{1,39}$/);
var screenPreferences = z17.record(z17.string(), z17.unknown()).refine((value) => JSON.stringify(value).length <= 4096, "A screen keeps at most 4 kB.");
var preferencesResponse = z17.object({
  general: generalPreferences,
  screens: z17.record(screenKey, screenPreferences)
});

// src/errors.ts
import { z as z18 } from "zod";

// src/errors/define.ts
var defineErrors = (texts) => texts;

// src/errors/access.ts
var accessErrors = defineErrors({
  "access.forbidden": {
    pl: "Nie masz uprawnienia: {{permission}}.",
    en: "You do not hold the permission {{permission}}."
  },
  "access.noAccess": {
    pl: "Nie masz dost\u0119pu do Overseera. Popro\u015B administratora.",
    en: "You have no access to Overseer. Ask an administrator."
  },
  "access.noToken": { pl: "Brak tokenu logowania.", en: "No token provided." },
  "access.invalidToken": { pl: "Nieprawid\u0142owy token logowania.", en: "Invalid token." },
  "access.notAccessToken": { pl: "To nie jest token dost\u0119pu.", en: "Token is not an access token." },
  "access.selfDemotion": {
    pl: "Nie mo\u017Cesz odebra\u0107 sobie roli administratora. Popro\u015B o to innego administratora.",
    en: "You cannot remove your own Admin role. Ask another administrator to do it."
  },
  "access.selfDisable": {
    pl: "Nie mo\u017Cesz zablokowa\u0107 w\u0142asnego konta.",
    en: "You cannot disable your own account."
  },
  "access.lastAdmin": {
    pl: "To ostatni aktywny administrator. Najpierw nadaj t\u0119 rol\u0119 komu\u015B innemu, inaczej nikt nie b\u0119dzie m\xF3g\u0142 zarz\u0105dza\u0107 dost\u0119pem.",
    en: "This is the last enabled administrator. Grant Admin to somebody else first, or nobody would be left who can manage access."
  },
  "access.selfRoleLosesAdmin": {
    pl: "Nie mo\u017Cesz odebra\u0107 uprawnienia do zarz\u0105dzania u\u017Cytkownikami roli, przez kt\xF3r\u0105 sam nim zarz\u0105dzasz.",
    en: "You cannot take admin.users from a role you manage access through."
  },
  "access.emailTaken": {
    pl: "{{email}} ma ju\u017C dost\u0119p. Edytuj t\u0119 osob\u0119.",
    en: "{{email}} already has access. Edit them instead."
  },
  "access.inviteUnavailable": {
    pl: "Cognito nie jest skonfigurowane na tym serwerze: nie mo\u017Cna nikogo zaprosi\u0107.",
    en: "Cognito is not configured on this server: nobody can be invited."
  },
  "access.roleHeld": {
    pl: "Rol\u0119 {{role}} ma {{holders}} os. Najpierw odbierz j\u0105 tym osobom.",
    en: "{{holders}} user(s) hold {{role}}. Take it from them first."
  },
  "access.roleNotFound": { pl: "Nie ma roli {{role}}.", en: "No role {{role}}." },
  "access.systemRole": {
    pl: "{{role}} to rola systemowa: nie mo\u017Cna jej zmieni\u0107.",
    en: "{{role}} is a system role: it cannot be changed."
  },
  "access.userNotFound": { pl: "Nie ma u\u017Cytkownika {{id}}.", en: "No user {{id}}." },
  "access.roleTwice": { pl: "Rola {{role}} jest nadana dwa razy.", en: "The role {{role}} is given twice." },
  "access.scopeNotTaken": {
    pl: "Rola {{role}} nie przyjmuje zakresu: {{scope}}.",
    en: "The role {{role}} takes no {{scope}} scope."
  },
  "access.windowNotTaken": {
    pl: "Rola {{role}} nie ma danych z dat\u0105: nie przyjmuje zakresu historii.",
    en: "The role {{role}} has no dated data: it takes no history window."
  },
  "access.beyondHistory": {
    pl: "Tw\xF3j dost\u0119p si\u0119ga danych od {{from}}.",
    en: "Your access reaches data from {{from}}."
  },
  "access.scopeMissing": {
    pl: "Rola {{role}} wymaga zakresu: {{scope}}.",
    en: "The role {{role}} needs a {{scope}} scope."
  },
  "access.scopeUnknownIds": {
    pl: "Nie ma takich pozycji ({{scope}}): {{ids}}.",
    en: "No {{scope}} with id {{ids}}."
  },
  "access.cognitoRefused": { pl: "Cognito odm\xF3wi\u0142o: {{reason}}", en: "Cognito refused this: {{reason}}" },
  "access.cognitoDown": { pl: "Cognito nie odpowiada: {{reason}}", en: "Cognito did not answer: {{reason}}" },
  "access.notAScreen": { pl: "Nieznany ekran: {{screen}}.", en: "Not a screen: {{screen}}." },
  "access.preferenceInvalid": {
    pl: "Stan ekranu musi by\u0107 obiektem JSON i mie\u0107 najwy\u017Cej 4 kB.",
    en: "A screen keeps a JSON object of at most 4 kB."
  }
});

// src/errors/cashier.ts
var cashierErrors = defineErrors({
  // Access
  "cashier.deskNotYours": {
    pl: "Nie masz dost\u0119pu do tego punktu kasowego.",
    en: "This desk is not one of yours."
  },
  "cashier.mainDeskNotYours": {
    pl: "Nie masz dost\u0119pu do tej kasy g\u0142\xF3wnej.",
    en: "This main desk is not one of yours."
  },
  "cashier.mainDesksNotVisible": {
    pl: "Wszystkie kasy g\u0142\xF3wne widzi tylko kasjer g\u0142\xF3wny i dyrekcja.",
    en: "Only the main cashier and management see every main desk."
  },
  "cashier.transferNotYours": {
    pl: "To przekazanie nie dotyczy Twoich punkt\xF3w kasowych.",
    en: "This transfer is not between your desks."
  },
  "cashier.acceptOwnReport": {
    pl: "Nie mo\u017Cesz zatwierdzi\u0107 raportu, kt\xF3ry sam z\u0142o\u017Cy\u0142e\u015B. Musi to zrobi\u0107 kto\u015B inny.",
    en: "You may not accept a report you submitted yourself; somebody else must."
  },
  "cashier.acceptOwnTransfer": {
    pl: "Nie mo\u017Cesz przyj\u0105\u0107 got\xF3wki, kt\xF3r\u0105 sam wys\u0142a\u0142e\u015B. Musi to zrobi\u0107 kto\u015B inny.",
    en: "You may not accept cash you sent yourself; somebody else must."
  },
  "cashier.closeDayNotAllowed": {
    pl: "Nie mo\u017Cesz zamyka\u0107 dni tego punktu kasowego.",
    en: "You may not close days of this desk."
  },
  "cashier.acceptReportNotAllowed": {
    pl: "Nie mo\u017Cesz zatwierdza\u0107 raport\xF3w tego punktu kasowego.",
    en: "You may not accept reports of this desk."
  },
  "cashier.returnReportNotAllowed": {
    pl: "Nie mo\u017Cesz zwraca\u0107 raport\xF3w tego punktu kasowego.",
    en: "You may not return reports of this desk."
  },
  "cashier.reopenReportNotAllowed": {
    pl: "Nie mo\u017Cesz ponownie otwiera\u0107 raport\xF3w tego punktu kasowego.",
    en: "You may not reopen reports of this desk."
  },
  "cashier.commentNotAllowed": {
    pl: "Nie mo\u017Cesz komentowa\u0107 raport\xF3w tego punktu kasowego.",
    en: "You may not comment on reports of this desk."
  },
  "cashier.sendNotAllowed": {
    pl: "Nie mo\u017Cesz wysy\u0142a\u0107 got\xF3wki z tego punktu kasowego.",
    en: "You may not send cash from this desk."
  },
  "cashier.receiveNotAllowed": {
    pl: "Nie mo\u017Cesz przyjmowa\u0107 got\xF3wki do tego punktu kasowego.",
    en: "You may not accept cash into this desk."
  },
  "cashier.bookWriteNotAllowed": {
    pl: "Nie mo\u017Cesz prowadzi\u0107 ksi\u0119gi tej kasy g\u0142\xF3wnej.",
    en: "You may not write the book of this main desk."
  },
  "cashier.closeMonthNotAllowed": {
    pl: "Nie mo\u017Cesz zamkn\u0105\u0107 miesi\u0105ca tej kasy g\u0142\xF3wnej.",
    en: "You may not close the month of this main desk."
  },
  "cashier.reopenMonthNotAllowed": {
    pl: "Nie mo\u017Cesz ponownie otworzy\u0107 zamkni\u0119tego miesi\u0105ca.",
    en: "You may not reopen a closed month."
  },
  // Not found
  "cashier.deskNotFound": { pl: "Nie ma punktu kasowego {{id}}.", en: "No cash desk {{id}}." },
  "cashier.sourceNotFound": { pl: "Nie ma \u017Ar\xF3d\u0142a danych {{id}}.", en: "No data source {{id}}." },
  "cashier.companyNotFound": { pl: "Nie ma firmy {{id}}.", en: "No company {{id}}." },
  "cashier.printerNotFound": { pl: "Nie ma drukarki {{id}}.", en: "No printer {{id}}." },
  "cashier.terminalNotFound": { pl: "Nie ma terminala {{id}}.", en: "No terminal {{id}}." },
  "cashier.reportNotFound": { pl: "Nie ma raportu {{id}}.", en: "No report {{id}}." },
  "cashier.documentNotOnDesk": {
    pl: "Ten dokument nie nale\u017Cy do tego punktu kasowego.",
    en: "That document is not one of this desk's."
  },
  "cashier.transferNotFound": { pl: "Nie ma przekazania {{id}}.", en: "No transfer {{id}}." },
  "cashier.bookEntryNotFound": { pl: "Nie ma wpisu {{id}}.", en: "No entry {{id}}." },
  // Settings
  "cashier.companyHasMainDesks": {
    pl: "Firma {{name}} ma ju\u017C kasy g\u0142\xF3wne.",
    en: "The company {{name}} has main desks."
  },
  "cashier.companyInactive": {
    pl: "Firma {{name}} jest wy\u0142\u0105czona. W\u0142\u0105cz j\u0105 w Administracja \u2192 Firmy.",
    en: "The company {{name}} is switched off. Switch it on in Admin \u2192 Companies."
  },
  "cashier.deskExists": {
    pl: "Punkt o nazwie {{name}} ju\u017C istnieje.",
    en: "A desk called {{name}} exists."
  },
  "cashier.openingDateRequired": {
    pl: "Punkt sprzeda\u017Cy musi mie\u0107 pierwszy dzie\u0144, od kt\xF3rego zamyka dni.",
    en: "A sales desk needs the first day it closes."
  },
  "cashier.targetNotMain": {
    pl: "Punkt sprzeda\u017Cy przekazuje got\xF3wk\u0119 tylko do kasy g\u0142\xF3wnej.",
    en: "A sales desk hands its cash to a main desk."
  },
  "cashier.mainDeskHasNoTarget": {
    pl: "Kasa g\u0142\xF3wna nie przekazuje got\xF3wki dalej.",
    en: "A main desk hands its cash to nobody."
  },
  "cashier.mainDeskHasNoSeries": {
    pl: "Kasa g\u0142\xF3wna nie ma serii dokument\xF3w.",
    en: "A main desk closes no series."
  },
  "cashier.mainDeskHasNoSources": {
    pl: "Kasa g\u0142\xF3wna nie pobiera danych ze \u017Ar\xF3de\u0142.",
    en: "A main desk takes data from no source."
  },
  "cashier.sourceKindNotRead": {
    pl: "Kasa nie czyta \u017Ar\xF3de\u0142 rodzaju {{kind}}.",
    en: "The cashier does not read {{kind}} sources."
  },
  "cashier.sourceNotOnDesk": {
    pl: "\u0179r\xF3d\u0142a danych {{name}} nie ma na li\u015Bcie tego punktu kasowego. Dodaj je najpierw.",
    en: "The data source {{name}} is not on this desk. Add it first."
  },
  "cashier.sourceInOpenReports": {
    pl: "Nie mo\u017Cna zdj\u0105\u0107 \u017Ar\xF3d\u0142a {{name}}: raporty z dni {{days}} nie s\u0105 zatwierdzone, a licz\u0105 jego dokumenty. Najpierw je z\u0142\xF3\u017C i zatwierd\u017A.",
    en: "The data source {{name}} cannot be taken off: the reports of {{days}} are not accepted and count its documents. Submit and accept them first."
  },
  "cashier.paymentTypeNotOfSource": {
    pl: "\u0179r\xF3d\u0142o danych {{name}} nie ma rodzaju p\u0142atno\u015Bci {{type}}.",
    en: "The data source {{name}} has no payment type {{type}}."
  },
  "cashier.mainDeskHasNoEquipment": {
    pl: "Kasa g\u0142\xF3wna nie ma drukarki ani terminala.",
    en: "A main desk has no printer or terminal."
  },
  "cashier.sourceInactive": {
    pl: "\u0179r\xF3d\u0142o danych {{name}} jest wy\u0142\u0105czone. W\u0142\u0105cz je w Administracja \u2192 \u0179r\xF3d\u0142a danych.",
    en: "The data source {{name}} is switched off. Switch it on in Admin \u2192 Data sources."
  },
  "cashier.invalidPaymentType": {
    pl: "To nie jest rodzaj p\u0142atno\u015Bci z izzy.",
    en: "type must be an izzy payment type"
  },
  "cashier.invalidId": {
    pl: "Nieprawid\u0142owy identyfikator: {{name}}.",
    en: "{{name}} must be a positive integer"
  },
  // Closing the day
  "cashier.deskInactive": { pl: "Punkt kasowy jest nieaktywny.", en: "The desk is not active." },
  "cashier.mainDeskClosesNoDays": { pl: "Kasa g\u0142\xF3wna nie zamyka dni.", en: "A main desk closes no days." },
  "cashier.beforeOpeningDate": {
    pl: "Ten punkt zamyka dni od {{date}}.",
    en: "The desk closes from {{date}}."
  },
  "cashier.dayNotStarted": { pl: "Ten dzie\u0144 jeszcze si\u0119 nie zacz\u0105\u0142.", en: "That day has not started." },
  "cashier.documentSourceNotOnDesk": {
    pl: "Dokument pochodzi ze \u017Ar\xF3d\u0142a danych, kt\xF3rego nie ma na li\u015Bcie tego punktu kasowego.",
    en: "A document comes from a data source that is not on this desk."
  },
  "cashier.printerOfOtherDesk": {
    pl: "Raport fiskalny wskazuje drukark\u0119 innego punktu.",
    en: "A fiscal report names a printer of another desk."
  },
  "cashier.seriesPrinterOfOtherDesk": {
    pl: "Seria mo\u017Ce by\u0107 przypisana tylko do drukarki tego samego punktu.",
    en: "A series may be set only to a printer of the same desk."
  },
  "cashier.terminalOfOtherDesk": {
    pl: "Raport terminala wskazuje terminal innego punktu.",
    en: "A terminal report names a terminal of another desk."
  },
  "cashier.reportLocked": {
    pl: "Raport jest z\u0142o\u017Cony. Mo\u017Cna go zmieni\u0107 dopiero po zwrocie.",
    en: "The report is submitted; it changes only after it is returned."
  },
  "cashier.reportNotSaved": {
    pl: "Zapisz raport przed z\u0142o\u017Ceniem.",
    en: "Save the report before submitting it."
  },
  "cashier.reportAlreadySubmitted": { pl: "Raport jest ju\u017C z\u0142o\u017Cony.", en: "The report is submitted already." },
  "cashier.reportAlreadyAccepted": { pl: "Raport jest ju\u017C zatwierdzony.", en: "The report is accepted already." },
  "cashier.reportBlocked": {
    pl: "Nie mo\u017Cna z\u0142o\u017Cy\u0107 raportu. Blokuj\u0105 go kontrole: {{checks}}.",
    en: "The report cannot be submitted: {{checks}}."
  },
  "cashier.reportCommentRequired": {
    pl: "Kontrola nie przesz\u0142a ({{checks}}). Wyja\u015Bnij to w komentarzu.",
    en: "A check failed ({{checks}}); say why in a comment."
  },
  "cashier.cashNotCounted": {
    pl: "Policz got\xF3wk\u0119 przed z\u0142o\u017Ceniem raportu.",
    en: "Count the cash before submitting."
  },
  "cashier.acceptNotSubmitted": {
    pl: "Zatwierdzi\u0107 mo\u017Cna tylko z\u0142o\u017Cony raport.",
    en: "The report is not submitted; only a submitted one is accepted."
  },
  "cashier.returnNotSubmitted": {
    pl: "Zwr\xF3ci\u0107 do kasjera mo\u017Cna tylko z\u0142o\u017Cony raport.",
    en: "The report is not submitted; only a submitted one is returned."
  },
  "cashier.reopenNotAccepted": {
    pl: "Ponownie otworzy\u0107 mo\u017Cna tylko zatwierdzony raport.",
    en: "The report is not accepted; only an accepted one is reopened."
  },
  // Transfers
  "cashier.sendAmountNotPositive": {
    pl: "Kwota przekazania musi by\u0107 wi\u0119ksza od zera.",
    en: "Send an amount above zero."
  },
  "cashier.noMainDeskSet": {
    pl: "Ten punkt nie ma jeszcze ustawionej kasy g\u0142\xF3wnej. Popro\u015B o jej ustawienie w Ustawieniach.",
    en: "No main desk is set for this desk yet; ask for it to be set in the settings."
  },
  "cashier.amountDiffersFromPlan": {
    pl: "Do przekazania jest {{planned}}. Napisz w notatce, dlaczego kwota jest inna.",
    en: "The planned amount is {{planned}}; say why this one differs in a note."
  },
  "cashier.handoverCountDiffers": {
    pl: "Policzono inn\u0105 kwot\u0119 ni\u017C utarg got\xF3wkowy dnia ({{amount}}). Takiego przekazania nie przyjmiesz: zwr\xF3\u0107 dzie\u0144 do poprawy.",
    en: "The count differs from the day's cash takings ({{amount}}). Such a handover is not accepted: return the day for correction."
  },
  "cashier.handoverReportOpen": {
    pl: "Ten dzie\u0144 jest w poprawie. Przyjmiesz przekazanie, gdy kasjer z\u0142o\u017Cy go ponownie.",
    en: "The day is being corrected. Accept the handover once the cashier submits it again."
  },
  "cashier.returnDayNotAllowed": {
    pl: "Dzie\u0144 do poprawy zwraca kasjer g\u0142\xF3wny kasy, do kt\xF3rej idzie przekazanie z tego dnia.",
    en: "Only the main cashier the day's handover goes to may return the day."
  },
  "cashier.paymentSplitOff": {
    pl: "Formy p\u0142atno\u015Bci dokumentu {{ref}} musz\u0105 dawa\u0107 razem {{total}}.",
    en: "The payments of {{ref}} must add up to {{total}}."
  },
  "cashier.laterDayClosed": {
    pl: "Najpierw otw\xF3rz p\xF3\u017Aniejszy zamkni\u0119ty dzie\u0144: {{date}}. Got\xF3wka dnia przechodzi na dni po nim.",
    en: "First open the later closed day, {{date}}: the cash of a day goes into the days after it."
  },
  "cashier.transferTargetRequired": {
    pl: "Wybierz punkt, do kt\xF3rego idzie got\xF3wka.",
    en: "Say which desk the cash goes to."
  },
  "cashier.transferToSameDesk": {
    pl: "Przekazanie musi i\u015B\u0107 do innego punktu.",
    en: "A transfer goes to another desk."
  },
  "cashier.sendFromStashBOnly": {
    pl: "Got\xF3wk\u0119 przekazuje tylko kasa B \u2014 do innej kasy B albo do punktu kasowego jako pogotowie kasowe.",
    en: "Only a stash B desk sends cash \u2014 to another stash B desk, or to a sales desk as float."
  },
  "cashier.receivingDeskInactive": {
    pl: "Punkt odbieraj\u0105cy jest nieaktywny.",
    en: "The receiving desk is not active."
  },
  "cashier.transferAlreadyAccepted": {
    pl: "To przekazanie jest ju\u017C przyj\u0119te.",
    en: "The transfer is accepted already."
  },
  "cashier.countedDiffers": {
    pl: "Policzono inn\u0105 kwot\u0119 ni\u017C wys\u0142ana ({{amount}}). Takiego przekazania nie przyjmiesz: odrzu\u0107 je, a nadawca wy\u015Ble je ponownie.",
    en: "The count differs from the amount sent ({{amount}}). Such a transfer is not accepted: refuse it, and its sender sends it again."
  },
  "cashier.refuseNotAllowed": {
    pl: "Przekazanie odrzuca tylko punkt, do kt\xF3rego idzie.",
    en: "Only the desk a transfer goes to may refuse it."
  },
  "cashier.refuseHandover": {
    pl: "Przekazania z raportu dnia nie odrzucasz: zwr\xF3\u0107 dzie\u0144 do poprawy.",
    en: "A day's handover is not refused: return the day for correction."
  },
  "cashier.transferNotAccepted": {
    pl: "To przekazanie nie jest jeszcze przyj\u0119te.",
    en: "The transfer is not accepted yet."
  },
  "cashier.mainDeskMonthClosed": {
    pl: "Miesi\u0105c {{month}}/{{year}} w kasie g\u0142\xF3wnej jest zamkni\u0119ty.",
    en: "The main desk's {{month}}/{{year}} is closed."
  },
  // Main desk book
  "cashier.notMainDesk": { pl: "Ksi\u0119g\u0119 prowadzi tylko kasa g\u0142\xF3wna.", en: "Only a main desk keeps a book." },
  "cashier.bookAmountNotPositive": { pl: "Wpisz kwot\u0119 wi\u0119ksz\u0105 od zera.", en: "Enter an amount above zero." },
  "cashier.documentRequired": {
    pl: "Wpisz numer dowodu: faktury, paragonu lub innego dokumentu.",
    en: "Enter the document number: the invoice, receipt or other document."
  },
  "cashier.lenderRequired": {
    pl: "Wpisz, kto po\u017Cycza kasie pieni\u0105dze lub komu kasa je oddaje.",
    en: "Enter who lends the desk the cash, or who the desk pays it back to."
  },
  "cashier.bookLineLinked": {
    pl: "Ten wpis pochodzi z przekazania lub wyp\u0142aty. Zmienisz tu tylko jego dat\u0119.",
    en: "This line comes from a transfer or a payout; only its date changes here."
  },
  "cashier.bookDateNotHere": {
    pl: "To przekazanie nie stoi w ksi\u0119dze tej kasy g\u0142\xF3wnej.",
    en: "This transfer is not in this main desk's book."
  },
  "cashier.monthNotOver": {
    pl: "Miesi\u0105c jeszcze trwa. Zamkniesz go od {{date}}.",
    en: "The month is not over yet; it closes from {{date}}."
  },
  "cashier.closeBelowZero": {
    pl: "Miesi\u0105ca nie zamkniesz: stan kasy A spada w nim poni\u017Cej zera, brakuje {{amount}}. Dodaj wcze\u015Bniejszy wpis, np. po\u017Cyczk\u0119.",
    en: "The month does not close: stash A falls below zero in it, {{amount}} short. Add an earlier line, such as a loan."
  },
  "cashier.entryKindStashAOnly": {
    pl: "Po\u017Cyczka, zwrot po\u017Cyczki, wp\u0142ata do banku i wyp\u0142ata z banku s\u0105 tylko w kasie A.",
    en: "A loan, its repayment, a bank deposit and a bank withdrawal are for stash A only."
  },
  "cashier.entryKindStashBOnly": {
    pl: "Zasilenie i zwrot zasilenia s\u0105 tylko w kasie B.",
    en: "A funding and its return are for stash B only."
  },
  "cashier.moveTargetRequired": {
    pl: "Wybierz kas\u0119 B, do kt\xF3rej idzie got\xF3wka.",
    en: "Pick the stash B desk the cash goes to."
  },
  "cashier.moveTargetNotStashB": {
    pl: "Przekazanie mi\u0119dzy kasami g\u0142\xF3wnymi idzie tylko z kasy B do innej kasy B.",
    en: "A move between main desks goes only from a stash B desk to another stash B desk."
  },
  "cashier.moveIsBookEntry": {
    pl: "Przekazanie do innej kasy B to wpis w ksi\u0119dze, nie przekazanie got\xF3wki.",
    en: "A move to another stash B desk is a book line, not a cash transfer."
  },
  "cashier.transferDateAhead": {
    pl: "Przekazanie nie mo\u017Ce sta\u0107 pod dniem, kt\xF3ry jeszcze nie nadszed\u0142.",
    en: "A transfer cannot stand under a day still to come."
  },
  "cashier.loanMonthClosedAtB": {
    pl: "Miesi\u0105c {{month}}/{{year}} w kasie B jest zamkni\u0119ty, a po\u017Cyczka stoi te\u017C w jej ksi\u0119dze. Najpierw otw\xF3rz go tam.",
    en: "Stash B's {{month}}/{{year}} is closed, and the loan stands in its book too. Reopen it there first."
  },
  "cashier.openingBelowZero": {
    pl: "Kasa A nie mo\u017Ce zaczyna\u0107 od stanu ujemnego.",
    en: "Stash A cannot open below zero."
  },
  "cashier.closeBalanceChanged": {
    pl: "Stan kasy zmieni\u0142 si\u0119 od otwarcia ekranu: teraz {{balance}}. Sprawd\u017A ksi\u0119g\u0119 i zamknij ponownie.",
    en: "The balance changed since the screen opened: it is {{balance}} now. Check the book and close again."
  },
  // Payouts
  "cashier.payoutNotAllowed": {
    pl: "Nie mo\u017Cesz wydawa\u0107 wyp\u0142at z tej kasy.",
    en: "You may not pay out from this desk."
  },
  "cashier.payoutWrongDesk": {
    pl: "Wyp\u0142aty got\xF3wkowe wydaje kasa A lub B firmy pracownika.",
    en: "Cash pay is paid from stash A or B of the employee's company."
  },
  "cashier.payoutWrongPart": {
    pl: "Ta kasa nie wydaje tej kwoty: kasa A wydaje wynagrodzenie A, kasa B wynagrodzenie B, premi\u0119 B i service charge.",
    en: "This desk does not pay that amount: stash A pays pay A; stash B pays pay B, bonus B and service charge."
  },
  "cashier.payoutRowNotFound": {
    pl: "Na li\u015Bcie p\u0142ac {{month}}/{{year}} nie ma tego wiersza.",
    en: "The payroll of {{month}}/{{year}} has no such row."
  },
  "cashier.payoutNotReady": {
    pl: "Ten wiersz listy p\u0142ac nie jest oznaczony jako Gotowe.",
    en: "This payroll row is not marked ready."
  },
  "cashier.payoutNothingLeft": {
    pl: "Z kwoty {{part}} nie zosta\u0142o nic do rozliczenia.",
    en: "Nothing is left to settle of {{part}}."
  },
  "cashier.payoutChanged": {
    pl: "Kwota zmieni\u0142a si\u0119 od otwarcia ekranu: teraz {{total}}. Sprawd\u017A i rozlicz ponownie.",
    en: "The amount changed since the screen opened: it is {{total}} now. Check and settle again."
  },
  "cashier.payoutZero": {
    pl: "Wybrane kwoty daj\u0105 razem zero. Wybierz inne.",
    en: "The amounts chosen add up to zero; choose others."
  },
  "cashier.payoutNotFound": { pl: "Nie ma wyp\u0142aty {{id}}.", en: "No payout {{id}}." },
  "cashier.monthClosedAlready": { pl: "Miesi\u0105c jest ju\u017C zamkni\u0119ty.", en: "The month is closed already." },
  "cashier.monthNotClosed": { pl: "Ten miesi\u0105c nie jest zamkni\u0119ty.", en: "The month is not closed." },
  "cashier.monthClosed": {
    pl: "Miesi\u0105c {{month}}/{{year}} jest zamkni\u0119ty.",
    en: "{{month}}/{{year}} is closed."
  }
});

// src/errors/catering.ts
var cateringErrors = defineErrors({
  "catering.badDate": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 dat\u0105 RRRR-MM-DD.",
    en: "{{name}} must be a date as YYYY-MM-DD"
  },
  "catering.badId": { pl: "Parametr \u201E{{name}}\u201D musi by\u0107 liczb\u0105 dodatni\u0105.", en: "{{name}} must be a positive integer" },
  "catering.badRange": {
    pl: "Zakres dat musi mie\u0107 od 0 do {{max}} dni, a \u201Eod\u201D nie mo\u017Ce by\u0107 po \u201Edo\u201D.",
    en: "The date range must span 0 to {{max}} days, from not after to"
  },
  "catering.badSiteCode": { pl: "Nieprawid\u0142owy kod sto\u0142\xF3wki.", en: "Not a canteen site code" },
  "catering.siteRequired": { pl: "Wybierz sto\u0142\xF3wk\u0119.", en: "A canteen site is required" },
  "catering.badLinesKind": { pl: "Rodzaj musi by\u0107 \u201Ebill\u201D albo \u201Edocument\u201D.", en: "kind must be bill or document" },
  "catering.badLinesId": { pl: "Nieprawid\u0142owy identyfikator rachunku lub dokumentu.", en: "Not a bill or document id" },
  "catering.badMonth": { pl: "Miesi\u0105c musi mie\u0107 posta\u0107 RRRR-MM.", en: "The month must be YYYY-MM" },
  "catering.siteNotFound": { pl: "Nie ma takiej sto\u0142\xF3wki.", en: "No such canteen site" },
  "catering.companyTwice": {
    pl: "Ka\u017Cda firma mo\u017Ce by\u0107 na li\u015Bcie tylko raz.",
    en: "Each company may be on the list once"
  },
  "catering.siteCodeTaken": {
    pl: "Sto\u0142\xF3wka o kodzie \u201E{{code}}\u201D ju\u017C istnieje.",
    en: 'a canteen site with code "{{code}}" exists'
  },
  "catering.siteIdNotFound": { pl: "Nie ma sto\u0142\xF3wki {{id}}.", en: "canteen site {{id}} not found" },
  "catering.siteDeleted": { pl: "Sto\u0142\xF3wka {{id}} jest usuni\u0119ta.", en: "canteen site {{id}} is deleted" },
  "catering.siteInUse": {
    pl: "Dla sto\u0142\xF3wki {{id}} licz\u0105 si\u0119 jeszcze punkty sprzeda\u017Cy lub serie ({{count}}). Najpierw przenie\u015B je do innej sto\u0142\xF3wki.",
    en: "{{count}} points of sale and series still count for canteen site {{id}}"
  },
  "catering.pointOfSaleNotFound": {
    pl: "Nie ma punktu sprzeda\u017Cy {{id}}.",
    en: "point of sale {{id}} not found"
  },
  "catering.seriesNotFound": { pl: "Nie ma serii {{code}}.", en: "series {{code}} not found" },
  "catering.badSeriesCode": { pl: "Nieprawid\u0142owy kod serii.", en: "code must be a series code" },
  "catering.categoryNotFound": { pl: "Nie ma kategorii {{id}}.", en: "category {{id}} not found" },
  "catering.categoryNameTaken": {
    pl: "Ta sto\u0142\xF3wka ma ju\u017C kategori\u0119 \u201E{{name}}\u201D.",
    en: 'the canteen site already has a category "{{name}}"'
  },
  "catering.categoryNotSites": {
    pl: "Kategoria {{categoryId}} nie nale\u017Cy do sto\u0142\xF3wki {{outletId}}.",
    en: "category {{categoryId}} is not one of canteen site {{outletId}}'s"
  },
  "catering.outletNotYours": {
    pl: "Nie masz dost\u0119pu do tej sto\u0142\xF3wki.",
    en: "You do not have access to this canteen site"
  }
});

// src/errors/core.ts
var coreErrors = defineErrors({
  "core.queryDate": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 dat\u0105 w formacie RRRR-MM-DD, otrzymano \u201E{{value}}\u201D.",
    en: "'{{name}}' must be a date as YYYY-MM-DD, got '{{value}}'"
  },
  "core.queryPositiveInteger": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 dodatni\u0105 liczb\u0105 ca\u0142kowit\u0105, otrzymano \u201E{{value}}\u201D.",
    en: "'{{name}}' must be a positive integer, got '{{value}}'"
  },
  "core.queryIntegerRange": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 liczb\u0105 ca\u0142kowit\u0105 od 1 do {{max}}, otrzymano \u201E{{value}}\u201D.",
    en: "'{{name}}' must be an integer between 1 and {{max}}, got '{{value}}'"
  },
  "core.companyNotFound": { pl: "Nie ma firmy {{id}}.", en: "No company {{id}}." },
  "core.companyExists": {
    pl: "Firma o nazwie \u201E{{name}}\u201D ju\u017C istnieje.",
    en: 'A company called "{{name}}" exists.'
  },
  "core.companyInUse": {
    pl: "Firma jest u\u017Cywana: umowy {{agreements}}, wpisy listy p\u0142ac {{payrollEntries}}, punkty kasowe {{desks}}. Zamiast usuwa\u0107, wy\u0142\u0105cz j\u0105.",
    en: "The company is in use: {{agreements}} agreement(s), {{payrollEntries}} payroll entr(ies), {{desks}} cash desk(s). Switch it off instead."
  },
  "core.optimaPayerNameTaken": {
    pl: "Nazwa p\u0142atnika w Optimie \u201E{{payer}}\u201D jest ju\u017C przypisana do firmy \u201E{{company}}\u201D.",
    en: 'Optima payer name "{{payer}}" is already held by company "{{company}}".'
  },
  "core.responseContract": {
    pl: "Serwer zwr\xF3ci\u0142 odpowied\u017A niezgodn\u0105 z kontraktem. To b\u0142\u0105d aplikacji: zg\u0142o\u015B go.",
    en: "Response did not match its contract"
  },
  "core.bodyTooLarge": {
    pl: "Dane s\u0105 za du\u017Ce: serwer przyjmuje najwy\u017Cej {{megabytes}} MB.",
    en: "The body is too large: the server takes at most {{megabytes}} MB."
  }
});

// src/errors/gastronomy.ts
var gastronomyErrors = defineErrors({
  "gastronomy.outletCodeTaken": {
    pl: "Punkt o kodzie \u201E{{code}}\u201D ju\u017C istnieje.",
    en: 'an outlet with code "{{code}}" exists'
  },
  "gastronomy.outletNotFound": { pl: "Nie ma punktu {{id}}.", en: "outlet {{id}} not found" },
  "gastronomy.outletMissing": { pl: "Nie ma takiego punktu.", en: "outlet not found" },
  "gastronomy.outletDeleted": { pl: "Punkt {{id}} jest usuni\u0119ty.", en: "outlet {{id}} is deleted" },
  "gastronomy.outletInUse": {
    pl: "Dla punktu {{id}} licz\u0105 si\u0119 jeszcze punkty sprzeda\u017Cy, serie lub regu\u0142y ({{count}}). Najpierw przenie\u015B je do innego punktu.",
    en: "{{count}} points of sale, series and rules still count for outlet {{id}}"
  },
  "gastronomy.outletNotYours": {
    pl: "Nie masz dost\u0119pu do tego punktu.",
    en: "This outlet is not one of yours."
  },
  "gastronomy.pointOfSaleNotFound": {
    pl: "Nie ma punktu sprzeda\u017Cy {{id}}.",
    en: "point of sale {{id}} not found"
  },
  "gastronomy.seriesNotFound": { pl: "Nie ma serii {{code}}.", en: "series {{code}} not found" },
  "gastronomy.categoryNotFound": { pl: "Nie ma kategorii {{id}}.", en: "category {{id}} not found" },
  "gastronomy.categoryNameTaken": {
    pl: "Ten punkt ma ju\u017C kategori\u0119 \u201E{{name}}\u201D.",
    en: 'the outlet already has a category "{{name}}"'
  },
  "gastronomy.categoryNotOutlets": {
    pl: "Kategoria {{categoryId}} nie nale\u017Cy do punktu {{outletId}}.",
    en: "category {{categoryId}} is not one of outlet {{outletId}}'s"
  },
  "gastronomy.remarkBlank": { pl: "Uwaga jest pusta.", en: "the remark is blank" },
  "gastronomy.variantNotFound": {
    pl: "Produkt {{code}} nie ma wariantu {{variantId}}.",
    en: "product {{code}} has no variant {{variantId}}"
  },
  "gastronomy.badSeriesCode": { pl: "Nieprawid\u0142owy kod serii.", en: "code must be a series code" },
  "gastronomy.badProductCode": { pl: "Nieprawid\u0142owy kod produktu.", en: "code must be a product code" },
  "gastronomy.badOutletCode": { pl: "Nieprawid\u0142owy kod punktu.", en: "outlet must be an outlet code" },
  "gastronomy.badId": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 dodatni\u0105 liczb\u0105 ca\u0142kowit\u0105.",
    en: "{{name}} must be a positive integer"
  },
  "gastronomy.outletRequired": { pl: "Wybierz punkt.", en: "outlet is required" },
  "gastronomy.badLinesKind": {
    pl: "Parametr \u201Ekind\u201D musi mie\u0107 warto\u015B\u0107 bill albo document.",
    en: "kind must be bill or document"
  },
  "gastronomy.badLinesId": {
    pl: "Nieprawid\u0142owy identyfikator rachunku lub dokumentu.",
    en: "id must be a bill or document id"
  },
  "gastronomy.badDate": {
    pl: "Parametr \u201E{{name}}\u201D musi by\u0107 dat\u0105 w formacie RRRR-MM-DD.",
    en: "{{name}} must be YYYY-MM-DD"
  },
  "gastronomy.badRange": { pl: "Okres musi mie\u0107 od 0 do {{max}} dni.", en: "from..to must be 0\u2013{{max}} days" }
});

// src/errors/hotel.ts
var hotelErrors = defineErrors({
  "hotel.roomGroupNameTaken": {
    pl: "Grupa pokoi \u201E{{name}}\u201D ju\u017C istnieje.",
    en: 'Room group "{{name}}" already exists'
  },
  "hotel.roomGroupNotFound": { pl: "Nie ma grupy pokoi {{id}}.", en: "Room group {{id}} not found" },
  "hotel.roomGroupHasRooms": {
    pl: "Grupa pokoi {{id}} ma jeszcze pokoje ({{count}}). Najpierw przenie\u015B je do innej grupy.",
    en: "Room group {{id}} still has {{count}} room(s) \u2014 reassign them first"
  },
  "hotel.roomCodeTaken": {
    pl: "Pok\xF3j o identyfikatorze Izzy \u201E{{code}}\u201D ju\u017C istnieje.",
    en: 'Room code "{{code}}" already exists'
  },
  "hotel.roomNotFound": { pl: "Nie ma pokoju {{id}}.", en: "Room {{id}} not found" },
  "hotel.roomInUse": {
    pl: "Pok\xF3j {{code}} ma jeszcze doby, najmy lub blokady.",
    en: "Room {{code}} is still referenced by nights, rents or blocks"
  },
  "hotel.roomNotConfigured": {
    pl: "Pokoju \u201E{{code}}\u201D nie ma w ustawieniach. Najpierw dodaj go w sekcji Pokoje.",
    en: 'Room "{{code}}" is not in settings \u2014 add it under Rooms first'
  },
  "hotel.validRangeBackwards": {
    pl: "Data \u201EWa\u017Cne do\u201D ({{to}}) jest wcze\u015Bniejsza ni\u017C \u201EWa\u017Cne od\u201D ({{from}}).",
    en: "validTo ({{to}}) is before validFrom ({{from}})"
  },
  "hotel.rentNotFound": { pl: "Nie ma najmu {{id}}.", en: "Rent {{id}} not found" },
  "hotel.rentOverlaps": {
    pl: "Okres najmu nachodzi na najem {{id}} ({{from}} \u2013 {{to}}).",
    en: "Rent period overlaps with existing rent {{id}} ({{from}} \u2013 {{to}})"
  },
  "hotel.rentOverlapsOpen": {
    pl: "Okres najmu nachodzi na najem {{id}} (od {{from}}, bez daty ko\u0144ca).",
    en: "Rent period overlaps with existing rent {{id}} ({{from}} \u2013 open)"
  },
  "hotel.blockNotFound": { pl: "Nie ma blokady {{id}}.", en: "Block {{id}} not found" },
  "hotel.blockOverlaps": {
    pl: "Okres blokady nachodzi na blokad\u0119 {{id}} ({{from}} \u2013 {{to}}).",
    en: "Block period overlaps with existing block {{id}} ({{from}} \u2013 {{to}})"
  },
  "hotel.blockOverlapsOpen": {
    pl: "Okres blokady nachodzi na blokad\u0119 {{id}} (od {{from}}, bez daty ko\u0144ca).",
    en: "Block period overlaps with existing block {{id}} ({{from}} \u2013 open)"
  },
  "hotel.mealRateExists": {
    pl: "Stawka wy\u017Cywienia od {{date}} ju\u017C istnieje (id {{id}}).",
    en: "Meal rate for {{date}} already exists (id {{id}})"
  },
  "hotel.mealRateNotFound": { pl: "Nie ma stawki wy\u017Cywienia {{id}}.", en: "Meal rate {{id}} not found" },
  "hotel.periodBackwards": {
    pl: "Data ko\u0144cowa ({{to}}) nie mo\u017Ce by\u0107 wcze\u015Bniejsza ni\u017C pocz\u0105tkowa ({{from}}).",
    en: "'to' ({{to}}) must not be before 'from' ({{from}})"
  },
  "hotel.periodTooLong": {
    pl: "Okres mo\u017Ce mie\u0107 najwy\u017Cej {{max}} dni, wybrano {{days}}.",
    en: "'from'\u2026'to' must span at most {{max}} days, got {{days}}"
  }
});

// src/errors/hr.ts
var hrErrors = defineErrors({
  // Who may see or act on an employee. A 404 reads the same whether the record
  // is missing or out of the caller's scope.
  "hr.noSuchRecord": { pl: "Nie ma takiego rekordu.", en: "No such record" },
  "hr.noSuchEmployee": { pl: "Nie ma takiego pracownika.", en: "No such employee" },
  "hr.noSuchAgreement": { pl: "Nie ma takiej umowy.", en: "No such agreement" },
  "hr.noSuchWorkDay": { pl: "Nie ma takiego dnia pracy.", en: "No such work day" },
  "hr.noSuchRead": { pl: "Nie ma takiego odczytu karty.", en: "No such read" },
  "hr.mayNotCreateEmployees": {
    pl: "Nie mo\u017Cesz dodawa\u0107 pracownik\xF3w.",
    en: "You may not create employees."
  },
  "hr.mayNotChangeEmployee": {
    pl: "Nie mo\u017Cesz zmienia\u0107 danych tego pracownika.",
    en: "You may not change this employee."
  },
  "hr.mayNotChangeDepartmentAgreements": {
    pl: "Nie mo\u017Cesz zmienia\u0107 um\xF3w w tym dziale.",
    en: "You may not change agreements in this department."
  },
  "hr.mayNotCorrectAttendance": {
    pl: "Nie mo\u017Cesz poprawia\u0107 ewidencji tego pracownika.",
    en: "You may not correct this employee's attendance."
  },
  "hr.monthEnded": {
    pl: "Miesi\u0105c {{month}} ju\u017C si\u0119 sko\u0144czy\u0142. Ewidencj\u0119 poprawia teraz tylko administrator HR.",
    en: "The month {{month}} has ended. Only an HR administrator may correct its attendance now."
  },
  "hr.mayNotChangeRented": {
    pl: "Nie mo\u017Cesz zmienia\u0107 wynaj\u0119cia pracownika ani mar\u017Cy.",
    en: "You may not change rented status or margin."
  },
  // Records looked up by id.
  "hr.employeeNotFound": { pl: "Nie znaleziono pracownika {{id}}.", en: "No employee {{id}}" },
  "hr.agreementNotFound": { pl: "Nie znaleziono umowy {{id}}.", en: "No agreement {{id}}" },
  "hr.workDayNotFound": { pl: "Nie znaleziono dnia pracy {{id}}.", en: "No work day {{id}}" },
  "hr.workLogNotFound": { pl: "Nie znaleziono odczytu karty {{id}}.", en: "No work log {{id}}" },
  "hr.absenceNotFound": { pl: "Nie znaleziono nieobecno\u015Bci {{id}}.", en: "No absence {{id}}" },
  "hr.cardReaderNotFound": { pl: "Nie znaleziono czytnika {{id}}.", en: "No card reader {{id}}" },
  "hr.departmentNotFound": { pl: "Nie znaleziono dzia\u0142u {{id}}.", en: "No department {{id}}" },
  "hr.positionNotFound": { pl: "Nie znaleziono stanowiska {{id}}.", en: "No position {{id}}" },
  // Employees.
  "hr.marginWithoutRented": {
    pl: "Mar\u017C\u0119 mo\u017Cna ustawi\u0107 tylko pracownikowi wynaj\u0119temu.",
    en: "A margin cannot be set on an employee who is not rented."
  },
  "hr.marginRequired": {
    pl: "Podaj mar\u017C\u0119 pracownika wynaj\u0119tego.",
    en: "A margin is required for a rented employee."
  },
  "hr.agreementEndDateRequired": {
    pl: "Pracownik {{id}} ma obowi\u0105zuj\u0105c\u0105 umow\u0119. Podaj dat\u0119 zako\u0144czenia umowy.",
    en: "Employee {{id}} has an agreement in force; agreementEndDate is required"
  },
  "hr.agreementDatesReversed": {
    pl: "Data zako\u0144czenia umowy nie mo\u017Ce by\u0107 wcze\u015Bniejsza ni\u017C data jej rozpocz\u0119cia.",
    en: "An agreement cannot end before it starts."
  },
  "hr.agreementEndBeforeStart": {
    pl: "Data zako\u0144czenia umowy nie mo\u017Ce by\u0107 wcze\u015Bniejsza ni\u017C jej pocz\u0105tek ({{dateFrom}}).",
    en: "agreementEndDate cannot precede the agreement's start date {{dateFrom}}"
  },
  "hr.cardTaken": {
    pl: "Karta \u201E{{cardId}}\u201D jest ju\u017C przypisana do pracownika {{employeeId}} ({{name}}).",
    en: 'Card "{{cardId}}" is already held by employee {{employeeId}} ({{name}}).'
  },
  "hr.cardNumberInvalid": {
    pl: "Nie mo\u017Cna odczyta\u0107 numeru karty {{cardNumber}}. Wpisz dziesi\u0119ciocyfrowy numer z karty.",
    en: "Cannot convert card number {{cardNumber}}"
  },
  // Agreements.
  "hr.agreementOfOtherEmployee": {
    pl: "Umowa {{agreementId}} nie nale\u017Cy do pracownika {{employeeId}}.",
    en: "Agreement {{agreementId}} does not belong to employee {{employeeId}}."
  },
  "hr.agreementChangeBeforeStart": {
    pl: "Obecna umowa zaczyna si\u0119 {{dateFrom}}, czyli p\xF3\u017Aniej ni\u017C {{date}}. Nowa umowa nie mo\u017Ce zacz\u0105\u0107 si\u0119 wcze\u015Bniej ni\u017C obecna.",
    en: "The current agreement starts on {{dateFrom}}, which is after {{date}}."
  },
  "hr.agreementOverlaps": {
    pl: "Nie mo\u017Cna zapisa\u0107 umowy: jej okres nak\u0142ada si\u0119 na inn\u0105 umow\u0119 tej samej formy w tej samej firmie.",
    en: "Agreement can't be created, working time overlaps with another agreement of the same form at the same company."
  },
  "hr.agreementPrimaryTaken": {
    pl: "Godziny mog\u0105 by\u0107 liczone tylko do jednej umowy naraz. Najpierw ustaw w drugiej umowie \u201ELiczone do innej umowy\u201D.",
    en: "Only one agreement at a time can be the primary one \u2014 the hours are recorded against it. Make the other agreement secondary first."
  },
  "hr.agreementWouldLeaveGap": {
    pl: "Pracownik {{employeeId}} nie mia\u0142by umowy obowi\u0105zuj\u0105cej w dniu {{date}}.",
    en: "Employee {{employeeId}} would have no agreement covering {{date}}"
  },
  "hr.inneNoSalary": {
    pl: "Umowa Inne nie mo\u017Ce mie\u0107 stawki.",
    en: "An Inne agreement cannot hold a salary."
  },
  "hr.rateRequired": { pl: "Podaj stawk\u0119.", en: "A rate is required." },
  "hr.agreementNeedsDepartment": {
    pl: "Umowa musi mie\u0107 co najmniej jeden dzia\u0142.",
    en: "An agreement needs at least one department."
  },
  "hr.departmentListedTwice": {
    pl: "Dzia\u0142 {{departmentId}} wyst\u0119puje dwa razy. Podaj go raz, z \u0142\u0105czn\u0105 wag\u0105.",
    en: "Department {{departmentId}} is listed twice. Give it one combined weight instead."
  },
  "hr.departmentWeightNotPositive": {
    pl: "Waga dzia\u0142u {{departmentId}} musi by\u0107 wi\u0119ksza od zera. Zamiast wagi zero usu\u0144 dzia\u0142.",
    en: "Weight for department {{departmentId}} must be greater than zero. Remove the department rather than weighting it at nothing."
  },
  // Attendance.
  "hr.absenceCannotBecomeWorkDay": {
    pl: "Nieobecno\u015Bci nie mo\u017Cna zmieni\u0107 w dzie\u0144 pracy. Usu\u0144 j\u0105 i dodaj dzie\u0144 pracy.",
    en: "An absence cannot become a worked day. Delete it and record the work day."
  },
  "hr.workDayCannotBecomeAbsence": {
    pl: "Dnia pracy nie mo\u017Cna zmieni\u0107 w nieobecno\u015B\u0107. Usu\u0144 go i dodaj nieobecno\u015B\u0107.",
    en: "A worked day cannot become an absence. Delete it and book the absence."
  },
  "hr.workDayEndBeforeStart": {
    pl: "Wyj\u015Bcie jest wcze\u015Bniejsze ni\u017C wej\u015Bcie.",
    en: "endTime is before startTime"
  },
  "hr.absenceEndBeforeStart": {
    pl: "Koniec nieobecno\u015Bci ({{endDate}}) jest wcze\u015Bniejszy ni\u017C jej pocz\u0105tek ({{startDate}}).",
    en: "endDate {{endDate}} is before startDate {{startDate}}"
  },
  "hr.rangeReversed": {
    pl: "Data pocz\u0105tkowa nie mo\u017Ce by\u0107 p\xF3\u017Aniejsza ni\u017C ko\u0144cowa.",
    en: "from must not be after to"
  },
  "hr.recalculateChanged": {
    pl: "Miesi\u0105c zmieni\u0142 si\u0119 od podgl\u0105du: dni z czytnika jest teraz {{now}}, przy potwierdzeniu by\u0142o {{confirmed}}. Sprawd\u017A go ponownie przed przeliczeniem.",
    en: "The month changed since it was previewed: {{now}} derived days now, {{confirmed}} when confirmed. Review it again before rebuilding."
  },
  "hr.workLogsRangeChanged": {
    pl: "Zakres zmieni\u0142 si\u0119 od podgl\u0105du: odczyt\xF3w karty jest teraz {{now}}, przy potwierdzeniu by\u0142o {{confirmed}}. Sprawd\u017A go ponownie przed przeliczeniem.",
    en: "The range changed since it was previewed: {{now}} reads now, {{confirmed}} when confirmed. Review it again before recalculating."
  },
  // Reference data.
  "hr.departmentHasAccessHolders": {
    pl: "U\u017Cytkownicy z dost\u0119pem ograniczonym do tego dzia\u0142u: {{count}}. Najpierw zmie\u0144 ich dost\u0119p.",
    en: "{{count}} user(s) have access scoped to this department. Change their access first."
  },
  "hr.departmentInUse": {
    pl: "Dzia\u0142 jest u\u017Cywany w umowach: {{count}}. Najpierw przenie\u015B je do innego dzia\u0142u.",
    en: "Still referenced by {{count}} agreement(s). Move them to another department first."
  },
  "hr.positionInUse": {
    pl: "Stanowisko jest u\u017Cywane w umowach: {{count}}. Najpierw przenie\u015B je na inne stanowisko.",
    en: "Still referenced by {{count}} agreement(s). Move them to another position first."
  },
  // Card readers.
  "hr.readerUnreachable": {
    pl: "Czytnik {{name}} ({{address}}) nie odpowiada: {{reason}}",
    en: "Reader {{name}} at {{address}} did not answer: {{reason}}"
  },
  "hr.readerAddressTaken": {
    pl: "Adres {{address}} jest ju\u017C zapisany jako czytnik \u201E{{name}}\u201D.",
    en: '{{address}} is already registered as "{{name}}".'
  },
  "hr.readerReportedNoCard": {
    pl: "Czytnik nie odczyta\u0142 \u017Cadnej karty.",
    en: "Reader did not report a card."
  },
  "hr.noActiveReader": {
    pl: "Brak aktywnego czytnika, na kt\xF3rym mo\u017Cna zapisa\u0107 kart\u0119.",
    en: "Cannot find an active reader to register the card on."
  },
  "hr.readerRejectedCard": {
    pl: "Czytnik nie przyj\u0105\u0142 karty {{cardId}}.",
    en: "Reader did not accept card {{cardId}}"
  },
  "hr.cardRegistrationReadOnly": {
    pl: "Zapisywanie kart w czytnikach jest wy\u0142\u0105czone: API dzia\u0142a wobec czytnik\xF3w tylko do odczytu (READER_READ_ONLY). Ewidencja dzia\u0142a normalnie \u2014 lista kart czytnika nie s\u0142u\u017Cy do dopasowania odczytu do pracownika.",
    en: "Card registration is disabled: the API is in read-only mode for reader devices (READER_READ_ONLY). Attendance is unaffected \u2014 the device card list is not used to match a reading to an employee."
  },
  "hr.cardReaderNotOfSource": {
    pl: "Czytnik {{id}} nie nale\u017Cy do tego \u017Ar\xF3d\u0142a danych.",
    en: "Card reader {{id}} does not belong to this data source."
  },
  "hr.readerOnCollector": {
    pl: "Czytnik {{name}} czyta kolektor w obiekcie. Serwer si\u0119 z nim nie \u0142\u0105czy.",
    en: "Reader {{name}} is read by its site collector. The API does not connect to it."
  },
  "hr.readerCollectorNeedsSource": {
    pl: "Wybierz \u017Ar\xF3d\u0142o danych kolektora, kt\xF3ry czyta ten czytnik.",
    en: "Choose the data source of the collector that reads this reader."
  },
  "hr.readerSourceOnlyForCollector": {
    pl: "\u0179r\xF3d\u0142o danych ma tylko czytnik czytany przez kolektor.",
    en: "Only a reader read by a collector has a data source."
  },
  "hr.readerSourceNotCardReader": {
    pl: "\u0179r\xF3d\u0142o danych {{id}} nie jest \u017Ar\xF3d\u0142em rodzaju czytniki kart.",
    en: "Data source {{id}} is not a card-reader source."
  },
  "hr.readerSecretUnreadable": {
    pl: "Zapisane has\u0142o czytnika ma nieznany format.",
    en: "Stored secret is not in a recognised format."
  },
  "hr.dayOffEndBeforeStart": {
    pl: "Data ko\u0144ca jest przed dat\u0105 pocz\u0105tku.",
    en: "endDate is before startDate"
  },
  "hr.payBIncomplete": {
    pl: "Wybierz typ wynagrodzenia B i podaj jego warto\u015B\u0107 albo zostaw oba puste.",
    en: "Pay B schema and value must both be set, or both omitted."
  },
  "hr.disabledToWithoutFrom": {
    pl: "Podaj dat\u0119 \u201EOd\u201D: orzeczenie nie mo\u017Ce si\u0119 sko\u0144czy\u0107, zanim si\u0119 zacznie.",
    en: "disabledTo needs disabledFrom \u2014 a status cannot end without starting."
  },
  "hr.disabledToBeforeFrom": {
    pl: "Data \u201EDo\u201D orzeczenia jest przed dat\u0105 \u201EOd\u201D.",
    en: "disabledTo is before disabledFrom."
  },
  "hr.readerSecretKeyMissing": {
    pl: "Brak READER_SECRET_KEY, wi\u0119c nie mo\u017Cna u\u017Cy\u0107 zapisanych hase\u0142 czytnik\xF3w.",
    en: "READER_SECRET_KEY is not configured, so stored credentials cannot be used."
  }
});

// src/errors/payroll.ts
var payrollErrors = defineErrors({
  "payroll.monthClosed": {
    pl: "Lista p\u0142ac za {{month}} jest zamkni\u0119ta. Otw\xF3rz miesi\u0105c ponownie, zanim cokolwiek w nim zmienisz.",
    en: "Payroll for {{month}} is closed. Reopen the month before changing anything in it."
  },
  "payroll.entryLocked": {
    pl: "Pozycja listy p\u0142ac {{entryId}} jest zatwierdzona, wi\u0119c jej kwoty s\u0105 tylko do odczytu. Cofnij zatwierdzenie, zanim cokolwiek w niej zmienisz.",
    en: "Payroll entry {{entryId}} is approved, so its figures are read-only. Withdraw the approval before changing anything on it."
  },
  // The month and its rows.
  "payroll.monthOpenFailed": {
    pl: "Nie uda\u0142o si\u0119 otworzy\u0107 listy p\u0142ac za {{month}}.",
    en: "Could not open the payroll month {{month}}."
  },
  "payroll.notClosed": {
    pl: "Lista p\u0142ac za {{month}} nie jest zamkni\u0119ta, wi\u0119c nie ma czego otwiera\u0107 ponownie.",
    en: "Payroll for {{month}} is not closed, so there is nothing to reopen."
  },
  "payroll.entryNotFound": {
    pl: "Nie znaleziono wiersza listy p\u0142ac {{id}}.",
    en: "No payroll entry {{id}}."
  },
  "payroll.entryHasNoMonth": {
    pl: "Wiersz listy p\u0142ac {{id}} nie ma miesi\u0105ca.",
    en: "Payroll entry {{id}} has no month."
  },
  "payroll.fieldNotEditable": {
    pl: "Pola \u201E{{field}}\u201D nie mo\u017Cna edytowa\u0107 w wierszu listy p\u0142ac dla formy umowy {{form}}.",
    en: 'Field "{{field}}" is not editable on a {{form}} payroll entry.'
  },
  "payroll.closeChanged": {
    pl: "Miesi\u0105c zmieni\u0142 si\u0119 od podgl\u0105du: sprawdzonych wierszy jest teraz {{ready}} z {{active}}, przy potwierdzeniu by\u0142o {{confirmed}}. Przejrzyj miesi\u0105c przed zamkni\u0119ciem.",
    en: "The month changed since it was previewed: {{ready}} of {{active}} rows are ready now, {{confirmed}} when confirmed. Review the month before closing it."
  },
  "payroll.notAllReady": {
    pl: "Pracownicy bez sprawdzonego wiersza: {{count}} z {{active}}. Przed zamkni\u0119ciem miesi\u0105ca trzeba sprawdzi\u0107 ka\u017Cdego.",
    en: "{{count}} of {{active}} employees are not marked ready. Every one of them must be checked before the month can be closed."
  },
  "payroll.restoreChanged": {
    pl: "Miesi\u0105c zmieni\u0142 si\u0119 od podgl\u0105du: wierszy jest teraz {{now}}, przy potwierdzeniu by\u0142o {{confirmed}}. Przejrzyj miesi\u0105c przed przywr\xF3ceniem.",
    en: "The month changed since it was previewed: {{now}} rows exist now, {{confirmed}} when confirmed. Review the month before restoring it."
  },
  // Import from the payroll office.
  "payroll.importMonthClosed": {
    pl: "Lista p\u0142ac za {{month}} jest zamkni\u0119ta. Otw\xF3rz miesi\u0105c ponownie, aby do niego importowa\u0107.",
    en: "Payroll for {{month}} is closed. Reopen the month to import into it."
  },
  "payroll.importNoFile": {
    pl: "Nie przes\u0142ano pliku. Wy\u015Blij go w polu `file`.",
    en: "No file was uploaded. Send it as the `file` field."
  },
  "payroll.importUnreadable": {
    pl: "Nie mo\u017Cna odczyta\u0107 pliku {{file}}: {{reason}}",
    en: "Could not read {{file}}: {{reason}}"
  },
  "payroll.importNoRows": {
    pl: "Plik {{file}} ma nag\u0142\xF3wek, ale nie ma wierszy z danymi.",
    en: "{{file}} has a header but no data rows."
  },
  "payroll.importNoCompany": {
    pl: "Import nie ma firmy. Powi\u0105\u017C p\u0142atnika z Optimy z firm\u0105 i potwierd\u017A ponownie.",
    en: "The import has no company. Link the Optima payer to a company, then confirm again."
  },
  "payroll.importChanged": {
    pl: "Miesi\u0105c zmieni\u0142 si\u0119 od podgl\u0105du: dopasowanych wierszy jest teraz {{now}}, przy potwierdzeniu by\u0142o {{confirmed}}. Przejrzyj import ponownie, zanim go zapiszesz.",
    en: "The month changed since it was previewed: {{now}} rows match now, {{confirmed}} when confirmed. Review the import again before applying it."
  },
  "payroll.companyNotFound": {
    pl: "Firma {{id}} nie istnieje.",
    en: "Company {{id}} does not exist."
  },
  "payroll.companyLinkedToOtherPayer": {
    pl: "Firma {{id}} (\u201E{{name}}\u201D) jest ju\u017C powi\u0105zana z p\u0142atnikiem w Optimie \u201E{{payer}}\u201D. Usu\u0144 to powi\u0105zanie w Administracja \u2192 Firmy, zanim dodasz inne.",
    en: 'Company {{id}} ("{{name}}") is already linked to Optima payer "{{payer}}". Clear it in Admin \u2192 Companies before linking another.'
  },
  "payroll.payerLinkedToOtherCompany": {
    pl: "P\u0142atnik w Optimie \u201E{{payer}}\u201D jest ju\u017C powi\u0105zany z firm\u0105 {{id}} (\u201E{{name}}\u201D).",
    en: 'Optima payer "{{payer}}" is already linked to company {{id}} ("{{name}}").'
  },
  "payroll.assignmentNoAgreement": {
    pl: "Wiersz {{row}}: pracownik {{employeeId}} nie ma w tym miesi\u0105cu umowy w tej firmie.",
    en: "Assignment {{row}} names employee {{employeeId}}, who has no agreement at this company this month."
  },
  "payroll.assignmentWrongForm": {
    pl: "Wiersz {{row}} ({{name}}): ta osoba nie ma umowy, kt\xF3rej dotyczy ten wiersz.",
    en: "Assignment {{row}} ({{name}}) does not hold the contract this row is for."
  },
  "payroll.assignmentDuplicate": {
    pl: "Dwa wiersze przypisano do: {{name}}, na t\u0119 sam\u0105 umow\u0119 w tej firmie.",
    en: "Two rows were assigned to {{name}} under the same contract at this company."
  },
  "payroll.assignmentsPerRow": {
    pl: "Ka\u017Cdy wiersz musi mie\u0107 jedno przypisanie.",
    en: "assignments must have one entry per row"
  }
});

// src/errors/source.ts
var sourceErrors = defineErrors({
  "source.notFound": { pl: "Nie ma \u017Ar\xF3d\u0142a danych {{id}}.", en: "No data source {{id}}." },
  "source.nameExists": {
    pl: "\u0179r\xF3d\u0142o danych o nazwie \u201E{{name}}\u201D ju\u017C istnieje.",
    en: 'A data source called "{{name}}" exists.'
  },
  "source.inUse": {
    pl: "\u0179r\xF3d\u0142o danych jest u\u017Cywane: punkty kasowe {{desks}}, dni przys\u0142ane przez kolektor {{stagedDays}}, czytniki kart {{devices}}. Zamiast usuwa\u0107, wy\u0142\u0105cz je.",
    en: "The data source is in use: {{desks}} cash desks, {{stagedDays}} days its collector sent, {{devices}} card readers. Switch it off instead."
  },
  "source.derived": {
    pl: "\u0179r\xF3d\u0142o danych czyta modu\u0142 {{module}}. Najpierw usu\u0144 je z ustawie\u0144 modu\u0142u.",
    en: "The {{module}} module reads this data source. Remove it from the module\u2019s settings first."
  },
  "source.wrongKind": {
    pl: "Ten modu\u0142 nie czyta \u017Ar\xF3de\u0142 rodzaju {{kind}}.",
    en: "This module does not read {{kind}} sources."
  },
  "source.derivedElsewhere": {
    pl: "\u0179r\xF3d\u0142o danych czyta ju\u017C modu\u0142 {{module}}. Jedno \u017Ar\xF3d\u0142o czyta jeden modu\u0142.",
    en: "The {{module}} module already reads this data source. One module reads a source."
  },
  "source.ingestRouteNotForKind": {
    pl: "\u0179r\xF3d\u0142o danych rodzaju {{kind}} nie wysy\u0142a na /ingest/{{route}}.",
    en: "A {{kind}} data source does not push to /ingest/{{route}}."
  },
  "source.notDerived": {
    pl: "Modu\u0142 nie czyta \u017Ar\xF3d\u0142a danych {{id}}.",
    en: "The module does not read data source {{id}}."
  }
});

// src/errors/validation.ts
var validationErrors = defineErrors({
  "validation.invalid_type": { pl: "Nieprawid\u0142owa warto\u015B\u0107.", en: "Invalid value." },
  "validation.invalid_type.required": { pl: "To pole jest wymagane.", en: "This field is required." },
  "validation.too_small": { pl: "Za ma\u0142o: co najmniej {{minimum}}.", en: "Too small: at least {{minimum}}." },
  "validation.too_small.string": {
    pl: "Za kr\xF3tkie: najmniejsza d\u0142ugo\u015B\u0107 to {{minimum}}.",
    en: "Too short: at least {{minimum}} characters."
  },
  "validation.too_small.array": {
    pl: "Wybierz co najmniej {{minimum}}.",
    en: "Choose at least {{minimum}}."
  },
  "validation.too_big": { pl: "Za du\u017Co: najwy\u017Cej {{maximum}}.", en: "Too big: at most {{maximum}}." },
  "validation.too_big.string": {
    pl: "Za d\u0142ugie: najwi\u0119ksza d\u0142ugo\u015B\u0107 to {{maximum}}.",
    en: "Too long: at most {{maximum}} characters."
  },
  "validation.too_big.array": { pl: "Za du\u017Co pozycji: najwy\u017Cej {{maximum}}.", en: "At most {{maximum}} items." },
  "validation.invalid_format": { pl: "Nieprawid\u0142owy format.", en: "Invalid format." },
  "validation.invalid_format.email": { pl: "Nieprawid\u0142owy adres e-mail.", en: "Invalid email address." },
  "validation.invalid_format.date": { pl: "Nieprawid\u0142owa data.", en: "Invalid date." },
  "validation.invalid_format.datetime": { pl: "Nieprawid\u0142owa data i godzina.", en: "Invalid date and time." },
  "validation.invalid_value": { pl: "Niedozwolona warto\u015B\u0107.", en: "Value not allowed." },
  "validation.not_multiple_of": {
    pl: "Warto\u015B\u0107 musi by\u0107 wielokrotno\u015Bci\u0105 {{divisor}}.",
    en: "The value must be a multiple of {{divisor}}."
  },
  "validation.unrecognized_keys": { pl: "Nieznane pole.", en: "Unknown field." },
  "validation.invalid_union": { pl: "Nieprawid\u0142owa warto\u015B\u0107.", en: "Invalid value." },
  "validation.custom": { pl: "Nieprawid\u0142owa warto\u015B\u0107.", en: "Invalid value." },
  "validation.noFields": {
    pl: "Nic nie zmieniono.",
    en: "Nothing to update \u2014 send at least one field."
  },
  "validation.ipv4": { pl: "Podaj adres IPv4, np. 192.168.1.10.", en: "expected an IPv4 address" },
  "validation.failed": { pl: "Popraw zaznaczone pola.", en: "Correct the marked fields." },
  "validation.readingNotOnBusinessDate": {
    pl: "Odczyt nie jest z dnia businessDate (czas Europe/Warsaw).",
    en: "The reading is not on businessDate (Europe/Warsaw)."
  }
});

// src/errors.ts
var ERRORS = {
  ...accessErrors,
  ...cashierErrors,
  ...cateringErrors,
  ...coreErrors,
  ...gastronomyErrors,
  ...hotelErrors,
  ...hrErrors,
  ...payrollErrors,
  ...sourceErrors,
  ...validationErrors
};
var errorParams = z18.record(z18.string(), z18.union([z18.string(), z18.number()]));
var isErrorCode = (code2) => code2 in ERRORS;
var formatError = (code2, params = {}, locale = "en") => ERRORS[code2][locale].replace(
  /\{\{(\w+)\}\}/g,
  (whole, name2) => name2 in params ? String(params[name2]) : whole
);

// src/ingest.ts
import { z as z19 } from "zod";
var ingestCardLog = z19.object({
  deviceId: z19.coerce.number().int(),
  /**
   * The raw card id as the reader reports it.
   *
   * Not normalised here, and deliberately: `EmployeeService.findByCardId`
   * matches on `substring(card_id from 5)`, so the first four characters are
   * the reader's own prefix and mean nothing. A reading whose card matches
   * nobody is stored and attributed to nobody — production holds 5 848 of them
   * — so there is nothing to validate against.
   */
  cardId: z19.string().min(1).max(255),
  /** An instant. The device sends local time with an offset. */
  time: z19.iso.datetime({ offset: true })
});
var ingestCardLogs = z19.object({
  logs: z19.array(ingestCardLog).min(1).max(5e3)
});
var ingestResult = z19.object({
  data: z19.object({
    type: z19.literal("card-log-ingest"),
    attributes: z19.object({
      received: z19.int().min(0),
      recorded: z19.int().min(0)
    })
  })
});
var instant2 = z19.iso.datetime({ offset: true });
var warsawDateFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Warsaw",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
var warsawDateOf = (at) => warsawDateFormat.format(new Date(at));
var cardReaderReading = z19.object({
  /** `card_reader.device.id`; the device must be of the token's source. */
  deviceId: z19.int().positive(),
  cardId: z19.string().min(1).max(255),
  time: instant2
});
var ingestCardReaderDay = z19.object({
  collectorVersion: z19.string().min(1).max(32),
  /** The Warsaw date of every reading below. */
  businessDate: isoDate,
  /** Accepted and of no meaning: a day is never closed, a later reading is stored. */
  isFinal: z19.boolean().default(false),
  extractedAt: instant2,
  payload: z19.object({
    readings: z19.array(cardReaderReading).max(5e3)
  })
}).superRefine((push, ctx) => {
  push.payload.readings.forEach((reading, i) => {
    if (warsawDateOf(reading.time) !== push.businessDate) {
      ctx.addIssue({
        code: "custom",
        path: ["payload", "readings", i, "time"],
        message: "validation.readingNotOnBusinessDate"
      });
    }
  });
});
var ingestCardReaderDayResult = z19.object({
  businessDate: isoDate,
  received: z19.int().min(0),
  recorded: z19.int().min(0)
});
var cardReaderState = z19.object({
  deviceId: z19.int().positive(),
  /** The collector's last read of it succeeded. */
  reachable: z19.boolean(),
  /** Its last whole log-file pull that succeeded. */
  pulledAt: instant2.optional()
});
var ingestCardReaderHeartbeat = ingestHeartbeat.extend({
  readers: z19.array(cardReaderState).max(100).optional()
});

// src/hotel.ts
import { z as z20 } from "zod";
var nightStatus = z20.enum(["sold", "free", "blocked", "absent"]);
var nightQualification = z20.enum(["noc", "impreza", "gratis"]);
var rentUnit = z20.enum(["month", "night"]);
var blockKind = z20.enum(["long", "soft"]);
var hotelMoneyMode = z20.enum(["nett", "gross"]);
var hotelAmount = z20.object({
  nett: decimalString.nullable(),
  gross: decimalString.nullable()
});
var hotelAmountInput = z20.union([hotelAmount, decimalString]);
var hotelMetrics = z20.object({
  /** Occupancy rate — sold nights / sellable nights. */
  occupancy: decimalString.nullable(),
  /** Average Daily Rate — room revenue / nights at a hotel price (rateN). */
  adr: hotelAmount,
  /** Revenue Per Available Room — room revenue / sellable nights. */
  revpar: hotelAmount,
  /** Total Revenue Per Occupied Room — total revenue / nights at a hotel price. */
  trevpor: hotelAmount,
  /** Total Revenue Per Available Room — total revenue / sellable nights. */
  trevpar: hotelAmount,
  /** Average Length of Stay. */
  alos: decimalString.nullable()
});
var roomGridCell = z20.object({
  roomCode: z20.string(),
  /**
   * The room's display name from settings.
   *
   * Null when the room has no settings row at all — an ingest auto-create the
   * grid still has to draw. The UI falls back to `roomCode` there, which is
   * also what it showed everywhere before this field existed.
   *
   * Separate from `roomCode` on purpose: the code is Izzy's identifier and has
   * to match byte for byte (`_BialaSala1` and `_BiałaSala3` are both real), so
   * it can never be renamed. `name` is the half a person may edit.
   */
  roomName: z20.string().nullable(),
  date: isoDate,
  status: nightStatus,
  /** Price for the night (nett + gross). Null when free/blocked/absent/unpriced. */
  price: hotelAmount.nullable(),
  /** Number of persons occupying. */
  persons: z20.number().int(),
  /**
   * Whether this night is sellable inventory.
   * Long blocks leave inventory; long-term rent stays in (priced from contract).
   */
  sellable: z20.boolean(),
  /**
   * Settings marker for what covers this night. Null when nothing does.
   *
   * `rent` — still sellable, priced from the contract.
   * `long_block` — out of sellable inventory.
   * `soft_block` — a soft block covers it and it did **not** sell, so it is out
   *   of inventory. A soft-blocked night that *did* sell reports null here: it
   *   is ordinary sellable inventory and the grid must not mark it as out.
   */
  outReason: z20.enum(["rent", "long_block", "soft_block"]).nullable(),
  /** How this night is qualified, if at all. */
  qualification: nightQualification.nullable(),
  /** The reservation this night belongs to, if sold. */
  reservationId: z20.string().nullable(),
  /** Settings room group, or null when the room has none. */
  groupId: z20.number().int().nullable(),
  groupName: z20.string().nullable(),
  /** `room_group.sort_order`; ungrouped cells sort last. */
  groupSortOrder: z20.number().int()
});
var otbPoint = z20.object({
  /** The stay date this measures. */
  stayDate: isoDate,
  /** When this snapshot was taken. */
  asOf: isoDate,
  soldNights: z20.number().int(),
  sellableNights: z20.number().int()
});
var pickup = z20.object({
  stayDate: isoDate,
  /** Days back: 1, 3, or 7. */
  k: z20.union([z20.literal(1), z20.literal(3), z20.literal(7)]),
  /** Change in sold nights. */
  delta: z20.number().int(),
  /** Current OTB. */
  otbNow: z20.number().int(),
  /** OTB k days ago. */
  otbThen: z20.number().int()
});
var leadTimeBucket = z20.object({
  /** Days before arrival. */
  days: z20.number().int().min(0),
  /** Count of reservations in this bucket. */
  count: z20.number().int()
});
var priceGap = z20.object({
  roomCode: z20.string(),
  date: isoDate,
  /** Reservation key, if this is a sold night. */
  reservationKey: z20.string().nullable(),
  persons: z20.number().int(),
  /** The amount in question (gross; nett when the source had one). */
  amount: hotelAmount.nullable(),
  /** How it has been qualified, if at all. */
  qualification: nightQualification.nullable()
});
var priceGapNightRef = z20.object({
  roomCode: z20.string(),
  date: isoDate
});
var priceGapPanelRow = z20.object({
  roomCode: z20.string(),
  date: isoDate,
  persons: z20.number().int(),
  stayFrom: isoDate,
  stayTo: isoDate,
  stayNights: z20.number().int(),
  reservationId: z20.string().nullable(),
  /** Sold nights on this reservation (any price). */
  reservationNights: z20.number().int(),
  /** Distinct rooms on this reservation. */
  reservationRooms: z20.number().int(),
  /** Unpriced nights still open on this reservation. */
  reservationUnpriced: z20.number().int(),
  /** Median priced night for this room in the month — hint only (gross). */
  suggestedRate: decimalString.nullable(),
  /**
   * Default pick: `impreza` when the reservation covers 5+ unpriced rooms,
   * otherwise `noc`.
   */
  defaultQualification: nightQualification,
  stayGaps: z20.array(priceGapNightRef),
  reservationGaps: z20.array(priceGapNightRef)
});
var otbLag = z20.object({
  stayDate: isoDate,
  soldNights: z20.number().int(),
  sellableNights: z20.number().int(),
  priorYearPace: z20.number().int().nullable(),
  priorYearFinal: z20.number().int().nullable(),
  /** Rooms short of prior-year final at this lead. */
  gapRooms: z20.number().int()
});
var hotelRoomGroup = z20.object({
  id: z20.number().int(),
  name: z20.string(),
  sortOrder: z20.number().int()
});
var hotelRoomGroups = z20.array(hotelRoomGroup);
var createHotelRoomGroup = z20.object({
  name: z20.string().min(1).max(128),
  sortOrder: z20.number().int().default(0)
});
var updateHotelRoomGroup = z20.object({
  name: z20.string().min(1).max(128).optional(),
  sortOrder: z20.number().int().optional()
});
var hotelRoom = z20.object({
  id: z20.number().int(),
  sourceId: z20.int(),
  name: z20.string(),
  groupId: z20.number().int().nullable(),
  izzyHotelIdentifier: z20.string(),
  validFrom: isoDate.nullable(),
  validTo: isoDate.nullable()
});
var createHotelRoom = z20.object({
  /** The data source the room is of: a room code is unique within one source only. */
  sourceId: z20.int().positive(),
  name: z20.string().min(1).max(128),
  groupId: z20.number().int().positive(),
  izzyHotelIdentifier: z20.string().min(1).max(32),
  validFrom: isoDate.nullable().optional(),
  validTo: isoDate.nullable().optional()
});
var updateHotelRoom = z20.object({
  name: z20.string().min(1).max(128).optional(),
  groupId: z20.number().int().positive().nullable().optional(),
  izzyHotelIdentifier: z20.string().min(1).max(32).optional(),
  validFrom: isoDate.nullable().optional(),
  validTo: isoDate.nullable().optional()
});
var hotelRent = z20.object({
  id: z20.number().int(),
  roomCode: z20.string(),
  dateFrom: isoDate,
  /** Null means still in force. */
  dateTo: isoDate.nullable(),
  /** Gross rate (what guests / contracts quote). */
  rate: decimalString,
  /** Nett rate; null when never entered. */
  rateNett: decimalString.nullable(),
  unit: rentUnit,
  description: z20.string(),
  createdBy: z20.string().nullable(),
  createdAt: z20.iso.datetime({ offset: true })
});
var createHotelRent = z20.object({
  /** The data source its room is of: a room code is unique within one source only. */
  sourceId: z20.int().positive(),
  roomCode: z20.string().min(1).max(32),
  dateFrom: isoDate,
  dateTo: isoDate.nullable().optional(),
  rate: decimalString,
  rateNett: decimalString.nullable().optional(),
  unit: rentUnit,
  description: z20.string().max(500).default("")
});
var updateHotelRent = z20.object({
  roomCode: z20.string().min(1).max(32).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.nullable().optional(),
  rate: decimalString.optional(),
  rateNett: decimalString.nullable().optional(),
  unit: rentUnit.optional(),
  description: z20.string().max(500).optional()
});
var hotelBlock = z20.object({
  id: z20.number().int(),
  roomCode: z20.string(),
  dateFrom: isoDate,
  dateTo: isoDate.nullable(),
  kind: blockKind,
  reason: z20.string(),
  createdBy: z20.string().nullable(),
  createdAt: z20.iso.datetime({ offset: true })
});
var createHotelBlock = z20.object({
  /** The data source its room is of: a room code is unique within one source only. */
  sourceId: z20.int().positive(),
  roomCode: z20.string().min(1).max(32),
  dateFrom: isoDate,
  dateTo: isoDate.nullable().optional(),
  /** Omitted means `long` — the only kind that existed before soft blocks. */
  kind: blockKind.default("long"),
  reason: z20.string().min(1).max(500)
});
var updateHotelBlock = z20.object({
  roomCode: z20.string().min(1).max(32).optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.nullable().optional(),
  kind: blockKind.optional(),
  reason: z20.string().min(1).max(500).optional()
});
var hotelMealRate = z20.object({
  id: z20.number().int(),
  effectiveFrom: isoDate,
  breakfast: decimalString,
  breakfastNett: decimalString.nullable(),
  dinner: decimalString,
  dinnerNett: decimalString.nullable(),
  /** Factor applied to child meals — typically 0.5. */
  childFactor: decimalString,
  description: z20.string(),
  createdBy: z20.string().nullable(),
  createdAt: z20.iso.datetime({ offset: true })
});
var createHotelMealRate = z20.object({
  effectiveFrom: isoDate,
  breakfast: decimalString,
  breakfastNett: decimalString.nullable().optional(),
  dinner: decimalString,
  dinnerNett: decimalString.nullable().optional(),
  childFactor: decimalString,
  description: z20.string().max(500).default("")
});
var updateHotelMealRate = z20.object({
  effectiveFrom: isoDate.optional(),
  breakfast: decimalString.optional(),
  breakfastNett: decimalString.nullable().optional(),
  dinner: decimalString.optional(),
  dinnerNett: decimalString.nullable().optional(),
  childFactor: decimalString.optional(),
  description: z20.string().max(500).optional()
});
var hotelNightQualification = z20.object({
  roomCode: z20.string(),
  date: isoDate,
  qualification: nightQualification,
  /** Override amount — gross (and nett when provided). */
  amount: hotelAmount.nullable(),
  createdBy: z20.string().nullable(),
  createdAt: z20.iso.datetime({ offset: true })
});
var qualifyHotelNights = z20.object({
  /** The data source the nights' rooms are of. */
  sourceId: z20.int().positive(),
  nights: z20.array(
    z20.object({
      roomCode: z20.string().min(1).max(32),
      date: isoDate,
      qualification: nightQualification,
      amount: decimalString.nullable().optional(),
      amountNett: decimalString.nullable().optional()
    })
  )
});
var ingestNightRow = z20.object({
  // Bounded to the column widths (`night.room_code` varchar(32),
  // `night.reservation_id` varchar(64)). Unbounded, an oversized value passed
  // validation and threw from the INSERT instead — after `upsertFromDay` had
  // already deleted the day it was replacing. Reject at the boundary.
  roomCode: z20.string().max(32),
  /** 0 = free, 1 = sold, 9 = blocked, or the enum value. */
  status: z20.union([z20.literal(0), z20.literal(1), z20.literal(9), nightStatus]),
  price: decimalString.nullable().optional(),
  priceNett: decimalString.nullable().optional(),
  persons: z20.number().int().default(0),
  reservationId: z20.string().max(64).nullable().optional()
});
var mealCountsInput = z20.object({
  breakfastAdult: z20.number().int().optional(),
  breakfastChild: z20.number().int().optional(),
  dinnerAdult: z20.number().int().optional(),
  dinnerChild: z20.number().int().optional()
});
var hotelDocumentIssues = (payload) => {
  const issues = [];
  const documents = new Set((payload.documents ?? []).map((d) => d.id));
  const seen = /* @__PURE__ */ new Set();
  (payload.documentLines ?? []).forEach((line, i) => {
    const key = `${line.documentId}#${line.position}`;
    if (seen.has(key)) issues.push({ path: ["documentLines", i, "position"], message: "duplicate line on its document" });
    seen.add(key);
    if (!documents.has(line.documentId)) {
      issues.push({ path: ["documentLines", i, "documentId"], message: "document not in this push" });
    }
  });
  return issues;
};
var ingestHotelDay = z20.object({
  collectorVersion: z20.string().min(1).max(32),
  businessDate: isoDate,
  /** True when this is the closed, final extract. */
  isFinal: z20.boolean().default(false),
  extractedAt: z20.iso.datetime({ offset: true }),
  payload: z20.object({
    /** Room inventory — optional, collector may omit. */
    rooms: z20.array(z20.string()).optional(),
    /** Nights — the core data. */
    nights: z20.array(ingestNightRow),
    /** Meal counts dated on this business day — optional. */
    meals: mealCountsInput.optional(),
    /**
     * Meal counts dated the **next** morning, read ahead by the collector.
     *
     * A night is billed for the breakfasts eaten the morning after, so the
     * carve reads day `j+1`. That day has not been pushed yet while `j` is
     * today, which left today's F&B at zero until the next push — TRevPOR came
     * out exactly equal to ADR, and the split on screen showed no breakfast
     * against a hotel full of guests who had already booked one.
     *
     * `VWYZYW` is populated in advance (checked on the live PMS: 71 portions
     * for tomorrow, and rows weeks out), so the collector can read `j+1` in the
     * same pass. Optional, and only ever a *fallback*: once the next day is
     * pushed in its own right, that row is the better source and wins. A
     * collector that predates this field simply omits it and behaves as before.
     */
    mealsNextMorning: mealCountsInput.optional(),
    /**
     * Folio categories as nett+gross pairs, or a bare gross string from a
     * collector that predates the nett side.
     * Keys: gastro, bar, other, open (and the alias keys older collectors send).
     */
    folio: z20.record(z20.string(), hotelAmountInput).optional(),
    /**
     * Cash document totals as nett+gross pairs, or a bare gross string.
     * Keys: nocleg, sniadanie, obiadokolacja, gastronomia, zaliczka, inne.
     */
    cash: z20.record(z20.string(), hotelAmountInput).optional(),
    /**
     * Reservations whose stay starts on this business date (`VHMINDATE`),
     * excluding cancelled (`VHSTATUS = 5`). Null `source` = blank VHPOCHODZENIE.
     */
    reservationSources: z20.array(
      z20.object({
        source: z20.string().nullable(),
        count: z20.number().int().nonnegative(),
        profitroom: z20.number().int().nonnegative()
      })
    ).optional(),
    /** Arrival/departure counts — optional. */
    arrivals: z20.number().int().optional(),
    departures: z20.number().int().optional(),
    /**
     * Reception's fiscal documents issued on this date (`FHEAD`) and their
     * payments (`FHSP`), for the cash desks' close. Optional: a collector that
     * predates them omits both, and the stored documents stay as they were.
     */
    documents: z20.array(ingestFiscalDocument).max(2e4).optional(),
    documentPayments: z20.array(ingestFiscalDocumentPayment).max(4e4).optional(),
    /**
     * The documents' lines (`FELEM`), for the per-rate figures of the cash
     * desks' fiscal reports. Optional: a collector that predates them omits
     * them, and the stored lines stay as they were.
     */
    documentLines: z20.array(ingestFiscalDocumentLine).max(1e5).optional()
  }).superRefine((payload, ctx) => {
    for (const issue of hotelDocumentIssues(payload)) ctx.addIssue({ code: "custom", ...issue });
  })
});
var ingestHotelReservation = z20.object({
  id: z20.string().min(1).max(55),
  year: z20.number().int(),
  month: z20.number().int(),
  number: z20.number().int(),
  /** `VHSTATUS`; 5 is cancelled. */
  status: z20.number().int(),
  bookedOn: isoDate.nullable(),
  arrival: isoDate,
  departure: isoDate,
  guest: z20.string().max(200).nullable(),
  persons: z20.number().int(),
  rooms: z20.number().int(),
  total: decimalString
});
var ingestHotelForward = z20.object({
  collectorVersion: z20.string().min(1).max(32),
  /** When this snapshot was taken. */
  takenOn: isoDate,
  extractedAt: z20.iso.datetime({ offset: true }),
  grid: z20.array(
    z20.object({
      roomCode: z20.string(),
      date: isoDate,
      status: z20.union([z20.literal(0), z20.literal(1), z20.literal(9), nightStatus]),
      price: decimalString.nullable().optional(),
      priceNett: decimalString.nullable().optional(),
      persons: z20.number().int().default(0),
      reservationId: z20.string().nullable().optional(),
      /** When this reservation was made. */
      bookedOn: isoDate.nullable().optional()
    })
  ),
  /**
   * The `VH` header of every stay in the house or due within the horizon,
   * cancelled ones (`status` 5) included. Kept by id, never pruned.
   * Optional: a collector that predates it omits it.
   */
  reservations: z20.array(ingestHotelReservation).max(2e4).optional()
});
var ingestHotelResult = z20.object({
  businessDate: isoDate.optional(),
  takenOn: isoDate.optional(),
  /** True when this replaced an existing extract. */
  replaced: z20.boolean(),
  /** How many times this day has been pushed. */
  pushCount: z20.number().int().optional(),
  /** Whether metrics were derived. */
  derived: z20.boolean(),
  /** Row counts as stored. */
  counts: z20.record(z20.string(), z20.number().int())
});
var hotelCounts = z20.object({
  sold: z20.number().int(),
  /** Sellable inventory (denominator for occupancy / RevPAR). */
  sellable: z20.number().int(),
  free: z20.number().int(),
  /** Short operational blocks — still in the sellable denominator. */
  blockedShort: z20.number().int(),
  /** Long blocks from settings — out of sellable. */
  locked: z20.number().int(),
  /** Long-term rent nights — out of sellable. */
  rent: z20.number().int(),
  /** Sold nights with no price and no qualification. */
  unpriced: z20.number().int(),
  /** Nights in the ADR / TRevPOR denominator (rateN). */
  rateN: z20.number().int(),
  arrivals: z20.number().int(),
  departures: z20.number().int(),
  /** Guests on arrival nights (same nights as `arrivals`). */
  arrivalPersons: z20.number().int().optional(),
  /** Guests on departure nights (same nights as `departures`). */
  departurePersons: z20.number().int().optional(),
  /** Guests on sold sellable nights (person-nights) — meal coverage denominator. */
  persons: z20.number().int().optional(),
  /**
   * Breakfast portions dated the morning after (VWYZYW j+1). Kept for
   * period totals; the day tile uses {@link hotelCounts.meals} instead.
   */
  breakfastTomorrow: z20.number().int().optional(),
  /**
   * Meal portions on the period's calendar days (consumption date).
   * On a single day this is that day's breakfast + dinner split.
   */
  meals: z20.object({
    breakfastAdult: z20.number().int(),
    breakfastChild: z20.number().int(),
    dinnerAdult: z20.number().int(),
    dinnerChild: z20.number().int()
  }).optional(),
  stays: z20.number().int().optional()
});
var hotelRevenueDetail = z20.object({
  /** Rooms + F&B + rent attributed to stay nights. */
  total: hotelAmount,
  rooms: hotelAmount,
  /** Meal carve + folio F&B (not cash documents). */
  rest: hotelAmount,
  rent: hotelAmount,
  /** Document-dated cash (excludes advances). */
  cash: hotelAmount,
  carve: hotelAmount,
  breakfast: hotelAmount,
  dinner: hotelAmount,
  gastro: hotelAmount,
  bar: hotelAmount,
  other: hotelAmount,
  openFolio: hotelAmount
});
var hotelYoyMetrics = z20.object({
  priorYearDate: isoDate,
  sold: z20.number().int().optional(),
  sellable: z20.number().int().optional(),
  occupancy: decimalString.nullable(),
  adr: hotelAmount,
  revpar: hotelAmount,
  trevpor: hotelAmount,
  trevpar: hotelAmount,
  alos: decimalString.nullable(),
  roomRevenue: hotelAmount,
  totalRevenue: hotelAmount
});
var hotelDay = z20.object({
  /** The data source — the property — the day is of; null when the hotel derives none. */
  sourceId: z20.int().nullable(),
  date: isoDate,
  /** True when this is the latest staged day in the database. */
  isLatest: z20.boolean(),
  metrics: hotelMetrics,
  counts: hotelCounts,
  revenue: hotelRevenueDetail,
  grid: z20.array(roomGridCell),
  /**
   * Unpriced sold nights on this day, with stay/reservation context for the
   * mockup editor. Empty when every sold night has a price or a qualification.
   */
  priceGaps: z20.array(priceGapPanelRow).optional(),
  /** Weekday-aligned prior year (−364 days). */
  yoy: hotelYoyMetrics.optional(),
  /** Two years back, weekday-aligned (−728 = 2×52 weeks). */
  yoy2: hotelYoyMetrics.optional(),
  /** Same calendar date one year ago — not weekday-aligned; Feb 29 → Feb 28. */
  calendarYoy: hotelYoyMetrics.optional(),
  /**
   * ~28 days of context ending at `date` — occupancy / RevPAR for the strip
   * (previous four weeks, including the selected day).
   */
  context: z20.array(
    z20.object({
      date: isoDate,
      occupancy: decimalString.nullable(),
      revpar: hotelAmount
    })
  ).optional(),
  /**
   * The previous eight same-weekdays (for the “is this a normal Saturday?” band).
   */
  sameWeekdayBand: z20.array(
    z20.object({
      date: isoDate,
      revpar: hotelAmount
    })
  ).optional()
});
var hotelPeriod = z20.object({
  from: isoDate,
  to: isoDate,
  metrics: hotelMetrics,
  counts: hotelCounts,
  revenue: hotelRevenueDetail,
  /** Priced nights / rateN — when below 1, ADR/RevPAR are understated. */
  priceCoverage: decimalString.nullable(),
  yoy: hotelYoyMetrics.optional(),
  /** Days in the period with unpriced sold nights, busiest first. */
  unpricedDays: z20.array(
    z20.object({
      date: isoDate,
      unpriced: z20.number().int()
    })
  ).optional(),
  notes: z20.array(z20.string()).optional(),
  daily: z20.array(
    z20.object({
      date: isoDate,
      attributed: hotelAmount,
      cash: hotelAmount,
      occupancy: decimalString.nullable(),
      adr: hotelAmount,
      /** Room revenue after meal carve — for RevPAR on the period strip. */
      roomRevenue: hotelAmount,
      sold: z20.number().int(),
      sellable: z20.number().int()
    })
  ).optional(),
  /**
   * Reservations whose stay starts in the period, by VHPOCHODZENIE.
   * `source` null = blank field (“nie zapisano”). `profitroom` is the subset
   * with a VPROFIT id — the darker bar segment in the mockup.
   */
  reservationSources: z20.array(
    z20.object({
      source: z20.string().nullable(),
      count: z20.number().int(),
      profitroom: z20.number().int()
    })
  ).optional()
});
var otbComparison = z20.object({
  stayDate: isoDate,
  asOf: isoDate,
  soldNights: z20.number().int(),
  sellableNights: z20.number().int(),
  /** sold / sellable on this stay date (OTB). */
  occupancy: decimalString.nullable(),
  /**
   * Provisional RevPAR from booked night rates on the forward grid
   * (room revenue / sellable). Null when no priced books or no inventory.
   */
  revpar: hotelAmount,
  /** OTB at the same lead time one year earlier (−364 on asOf). */
  priorYearPace: z20.number().int().nullable(),
  /** Final sold nights on that stay date last year (from night facts). */
  priorYearFinal: z20.number().int().nullable()
});
var hotelForward = z20.object({
  horizon: z20.number().int(),
  asOf: isoDate,
  /** Aggregates over the horizon (mockup KPI strip). */
  summary: z20.object({
    soldNights: z20.number().int(),
    sellableNights: z20.number().int(),
    occupancy: decimalString.nullable(),
    priorYearPaceOccupancy: decimalString.nullable(),
    priorYearFinalOccupancy: decimalString.nullable(),
    gapToPriorFinal: z20.number().int(),
    pickup1: z20.number().int(),
    pickup3: z20.number().int(),
    pickup7: z20.number().int()
  }),
  otb: z20.array(otbComparison),
  /** Kept for detailed tables; summary pickups are horizon totals. */
  pickup: z20.array(pickup),
  leadTime: z20.array(leadTimeBucket).optional(),
  cancellations: z20.object({
    total: z20.number().int(),
    nightsLost: z20.number().int(),
    rate: decimalString.nullable()
  }).optional(),
  attention: z20.array(otbLag).optional()
});
var hotelSettings = z20.object({
  sourceId: z20.int().nullable(),
  roomGroups: z20.array(hotelRoomGroup),
  rooms: z20.array(hotelRoom),
  rents: z20.array(hotelRent),
  blocks: z20.array(hotelBlock),
  mealRates: z20.array(hotelMealRate)
});
var hotelSettingsPreviewRequest = z20.object({
  from: isoDate,
  to: isoDate,
  rents: z20.array(createHotelRent).default([]),
  blocks: z20.array(createHotelBlock).default([]),
  mealRates: z20.array(createHotelMealRate).default([])
});
var hotelSettingsPreview = z20.object({
  from: isoDate,
  to: isoDate,
  metrics: hotelMetrics,
  counts: hotelCounts.pick({ sold: true, sellable: true, rent: true, locked: true, free: true, blockedShort: true }),
  revenue: hotelRevenueDetail.pick({ total: true, rooms: true, rest: true, rent: true })
});
var hotelSettingsCandidates = z20.object({
  highOccupancy: z20.array(z20.object({
    roomCode: z20.string(),
    occupancy: decimalString,
    sold: z20.number().int(),
    nights: z20.number().int()
  })),
  longBlocks: z20.array(z20.object({
    roomCode: z20.string(),
    dateFrom: isoDate,
    dateTo: isoDate,
    nights: z20.number().int()
  }))
});

// src/gastronomy.ts
import { z as z21 } from "zod";
var ROW_CAP = 5e4;
var instant3 = z21.iso.datetime({ offset: true });
var code = z21.string().min(1).max(20);
var billId = z21.string().min(1).max(13);
var documentId = z21.string().min(1).max(55);
var userName = z21.string().max(40);
var ingestBill = z21.object({
  id: billId,
  /** `OHPSID`. */
  pointOfSale: z21.number().int(),
  /** `OHSTOLIK`, a `PSTABLES.PTID`. Null when the bill has no table. */
  tableId: z21.number().int().nullable(),
  /** `OHCRDATE`. */
  openedAt: instant3,
  /** `OHCLOSETIME`. The bill belongs to this instant's Warsaw date. */
  closedAt: instant3,
  /** `OHUSER` — who opened it. */
  user: userName,
  /** `OHOPIS`. Free text: a table name, a group, a guest. */
  description: z21.string().max(250).nullable(),
  /**
   * `OHOSOB` as recorded. Recorded only at the restaurant, and it has typos
   * (a bill of 333 920 guests exists), so the API caps it at the hall's seats.
   */
  covers: z21.number().int().nullable(),
  /** `OHTOTAL`. */
  gross: decimalString,
  /** `OHTOTALN`. Null on a few bills. */
  nett: decimalString.nullable(),
  /** `OHRABAT` — a percentage. */
  discountPercent: decimalString.nullable(),
  /** `OHFHIID` — the fiscal document this bill was closed on. */
  documentId: documentId.nullable(),
  /** `OHKRW` — the internal-settlement reason, a `DEF` type 45 code. */
  internalReason: z21.string().max(20).nullable(),
  /** `OHKRWDOD` — the internal-settlement note; at a canteen, the employee number of the card. */
  internalNote: z21.string().max(100).nullable(),
  /**
   * `OHKARTAKLUB` — the card the bill was settled on: at a canteen, the
   * employee card. Absent from a collector that does not send it yet.
   */
  cardNumber: z21.string().max(40).nullable().optional()
});
var ingestBillLine = z21.object({
  billId,
  /** `OECOUNTER` — the line's number on its bill. */
  position: z21.number().int(),
  /** `OEKOD`. Empty on a cancelled line of nothing in some databases: kept, so the line stays in the void history. */
  productCode: z21.string().max(20),
  /**
   * `PROD.PAGRUPA` when the line was read. Sent with the line because a
   * product can move between groups; the menu mix of a past day uses the
   * group it had then.
   */
  productGroup: code.nullable(),
  /**
   * `PROD.PAVATID` when the line was read, in percent — the till keeps no rate
   * on the line. Exempt (`ZW`) and outside VAT (`NP`) are 0. Null for a
   * product no longer in `PROD`.
   */
  vatRate: z21.number().int().min(0).max(100).nullable(),
  /** `OEILOSC`. */
  quantity: decimalString,
  /** `OECENA` — unit price charged, gross. */
  price: decimalString,
  /** `OEBCENA` — unit list price, gross. Above `price` when discounted. */
  listPrice: decimalString.nullable(),
  /** `OECANCELED = 'T'`. */
  cancelled: z21.boolean(),
  /** `OEPRINTED = 'T'`. */
  printed: z21.boolean(),
  /** `OEDATETIME`. */
  addedAt: instant3,
  /** `OECANCELTIME`. */
  cancelledAt: instant3.nullable(),
  /** `OEUSER.OUUSER` — who added the line. */
  addedBy: userName.nullable(),
  /**
   * `OEUWAGI` — what the crew typed on the line, trimmed; null when blank.
   * For a split product it names the dish. Absent from collectors before it.
   */
  remark: z21.string().max(1200).nullable().optional()
});
var ingestBillPayment = z21.object({
  billId,
  /** `OPPLATNOSC` — a `DEF` type 1 code, plus `IZZYHOTEL` for a room charge. */
  type: z21.string().min(1).max(20),
  /** `OPKWOTA`. */
  amount: decimalString,
  /** `OPKRW` — the reason when `type` is `WEWN`. */
  internalReason: z21.string().max(20).nullable()
});
var ingestBillDiscount = z21.object({
  billId,
  /** `ORRABAT` — a percentage. */
  percent: decimalString,
  /** `ORPOWOD`. */
  reason: z21.string().max(100).nullable()
});
var ingestBillOperation = z21.object({
  billId,
  /** `OIDATE`. */
  at: instant3,
  /** `OIUSER`. */
  user: userName,
  /** `OIOPER`, as written. */
  text: z21.string().min(1).max(250)
});
var ingestDocument = ingestFiscalDocument;
var ingestDocumentLine = ingestFiscalDocumentLine;
var ingestDocumentPayment = ingestFiscalDocumentPayment;
var duplicates = (ids) => {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  ids.forEach((id, i) => {
    if (seen.has(id)) out.push(i);
    seen.add(id);
  });
  return out;
};
var checkDay = (payload) => {
  const issues = [];
  for (const i of duplicates(payload.bills.map((b) => b.id))) {
    issues.push({ path: ["bills", i, "id"], message: "duplicate bill id" });
  }
  for (const i of duplicates(payload.documents.map((d) => d.id))) {
    issues.push({ path: ["documents", i, "id"], message: "duplicate document id" });
  }
  for (const i of duplicates(payload.lines.map((l) => `${l.billId}#${l.position}`))) {
    issues.push({ path: ["lines", i, "position"], message: "duplicate line on its bill" });
  }
  for (const i of duplicates(payload.documentLines.map((l) => `${l.documentId}#${l.position}`))) {
    issues.push({ path: ["documentLines", i, "position"], message: "duplicate line on its document" });
  }
  const bills = new Set(payload.bills.map((b) => b.id));
  const byBill = ["lines", "payments", "discounts", "operations"];
  for (const key of byBill) {
    payload[key].forEach((row, i) => {
      if (!bills.has(row.billId)) {
        issues.push({ path: [key, i, "billId"], message: "bill not in this push" });
      }
    });
  }
  const documents = new Set(payload.documents.map((d) => d.id));
  const byDocument = ["documentLines", "documentPayments"];
  for (const key of byDocument) {
    payload[key].forEach((row, i) => {
      if (!documents.has(row.documentId)) {
        issues.push({ path: [key, i, "documentId"], message: "document not in this push" });
      }
    });
  }
  return issues;
};
var dayPayload = z21.object({
  /** Bills whose `closedAt` falls on `businessDate`. */
  bills: z21.array(ingestBill).max(ROW_CAP),
  lines: z21.array(ingestBillLine).max(ROW_CAP),
  payments: z21.array(ingestBillPayment).max(ROW_CAP),
  discounts: z21.array(ingestBillDiscount).max(ROW_CAP),
  operations: z21.array(ingestBillOperation).max(ROW_CAP),
  /** Documents whose `issuedOn` is `businessDate`, every series included. */
  documents: z21.array(ingestDocument).max(ROW_CAP),
  documentLines: z21.array(ingestDocumentLine).max(ROW_CAP),
  documentPayments: z21.array(ingestDocumentPayment).max(ROW_CAP)
});
var ingestGastronomyDay = z21.object({
  collectorVersion: z21.string().min(1).max(32),
  businessDate: isoDate,
  /** True when the day is closed and this is its complete extract. */
  isFinal: z21.boolean().default(false),
  /** When the database was read, not when this arrived. */
  extractedAt: instant3,
  payload: dayPayload.superRefine((payload, ctx) => {
    for (const issue of checkDay(payload)) ctx.addIssue({ code: "custom", ...issue });
  })
});
var ingestReservation = z21.object({
  /** `RZPSID`. */
  pointOfSale: z21.number().int(),
  /** `RZID`, unique within its point of sale. */
  id: z21.number().int(),
  /** `RZDATA`. */
  date: isoDate,
  /** `RZCZAS`, `HH:MM`. */
  time: z21.string().regex(/^\d{2}:\d{2}$/, "expected HH:MM").nullable(),
  /** `RZOSOB`. */
  covers: z21.number().int().nonnegative(),
  /** `RZSALA`. */
  hall: z21.string().max(50).nullable(),
  /** `RZSTOLIK`. */
  table: z21.string().max(10).nullable(),
  /** `RZANULACJA = 'T'`. */
  cancelled: z21.boolean(),
  /** `RZZALICZKA`. */
  deposit: decimalString.nullable(),
  /** `RZDATA_UTWORZENIA`. Null on a few old rows. */
  createdAt: instant3.nullable()
});
var ingestEventBooking = z21.object({
  /** `KHID`. */
  id: z21.number().int(),
  /** `KHPSID`. */
  pointOfSale: z21.number().int(),
  /** `KHDATA`. */
  eventDate: isoDate,
  /** `KHDATAUTW`. */
  createdAt: instant3,
  /** `KHTOTAL`. Since 2025 this holds the deposit, not the event's value. */
  amount: decimalString,
  /** `KHOSOB`; the collector sends 0 — not filled in, which is almost always — as null. */
  guests: z21.number().int().positive().nullable()
});
var ingestGastronomyForward = z21.object({
  collectorVersion: z21.string().min(1).max(32),
  takenOn: isoDate,
  extractedAt: instant3,
  reservations: z21.array(ingestReservation).max(ROW_CAP),
  events: z21.array(ingestEventBooking).max(ROW_CAP)
}).superRefine((forward, ctx) => {
  const keys = forward.reservations.map((r) => `${r.pointOfSale}#${r.id}`);
  for (const i of duplicates(keys)) {
    ctx.addIssue({ code: "custom", path: ["reservations", i, "id"], message: "duplicate reservation" });
  }
  for (const i of duplicates(forward.events.map((e) => String(e.id)))) {
    ctx.addIssue({ code: "custom", path: ["events", i, "id"], message: "duplicate event booking" });
  }
});
var codeName = z21.object({ code, name: z21.string().max(100) });
var ingestGastronomyReference = z21.object({
  collectorVersion: z21.string().min(1).max(32),
  extractedAt: instant3,
  /** `PS`. */
  pointsOfSale: z21.array(z21.object({ id: z21.number().int(), name: z21.string().max(100) })).max(1e3),
  /** `PSTABLES` — the tables on each point of sale's floor plan. */
  tables: z21.array(
    z21.object({
      pointOfSale: z21.number().int(),
      /** `PTID` — what a bill's `tableId` points at. */
      id: z21.number().int(),
      /** `PTROOM` — the hall. */
      hall: z21.string().max(100),
      /** `PTCAPTION`. */
      caption: z21.string().max(100),
      /** `PTPLACES`. */
      seats: z21.number().int().nonnegative()
    })
  ).max(ROW_CAP),
  /** `GRPR` — a tree through `parent`. */
  productGroups: z21.array(z21.object({ code, parent: code.nullable(), name: z21.string().max(100) })).max(ROW_CAP),
  /** `PROD`. */
  products: z21.array(z21.object({ code, name: z21.string().max(100), group: code.nullable() })).max(ROW_CAP),
  /** `DEF` type 1. */
  paymentTypes: z21.array(codeName).max(1e3),
  /** `DEF` type 45 — the internal-settlement reasons. */
  internalReasons: z21.array(codeName).max(1e3),
  /** `DEF` type 21 — the document series. */
  series: z21.array(codeName).max(1e3)
});
var gastronomyOutlet = z21.object({
  code: z21.string(),
  name: z21.string(),
  /** False for horse riding. */
  isFood: z21.boolean()
});
var gastronomyRevenue = z21.object({
  from: isoDate,
  to: isoDate,
  rows: z21.array(
    z21.object({
      date: isoDate,
      /** Null when a point of sale or series has no outlet yet. */
      outlet: gastronomyOutlet.nullable(),
      gross: decimalString,
      nett: decimalString,
      /**
       * Bills that carried revenue, and documents no bill points to that are a
       * sale — not a correction or a replacement.
       */
      bills: z21.number().int(),
      /** Documents counted on their own — no bill behind them. */
      documents: z21.number().int()
    })
  )
});
var gastronomyAmount = z21.object({
  gross: decimalString,
  nett: decimalString
});
var gastronomyShare = z21.object({
  gross: z21.number(),
  nett: z21.number()
});
var gastronomyLastYear = gastronomyAmount.extend({
  bills: z21.number().int()
});
var gastronomyEventLineKind = z21.enum([
  "gastronomy8",
  "gastronomy23",
  "services23",
  "hotel",
  "advanceTaken",
  "advanceSettled"
]);
var gastronomyDocumentNumber = z21.object({
  id: z21.string(),
  series: z21.string(),
  type: z21.string(),
  number: z21.number().int(),
  issuedOn: isoDate
});
var gastronomyEventDocument = gastronomyAmount.extend({
  kind: z21.enum(["bill", "document"]),
  /** The data source it is of: Izzy's ids are unique within one database only. */
  sourceId: z21.int(),
  id: z21.string(),
  /** A document's number; null for a bill. */
  document: gastronomyDocumentNumber.nullable(),
  /** The day it counts on. */
  countsOn: isoDate,
  at: instant3.nullable(),
  /** From its text, or the text of an advance it takes off; null when none says. */
  eventDate: isoDate.nullable(),
  payments: z21.array(z21.object({ type: z21.string(), name: z21.string().nullable(), amount: decimalString })),
  lines: z21.array(
    gastronomyAmount.extend({
      code: z21.string(),
      name: z21.string().nullable(),
      quantity: decimalString,
      kind: gastronomyEventLineKind
    })
  ),
  /** The advances a final invoice takes off. */
  settles: z21.array(gastronomyDocumentNumber.extend({ found: z21.boolean() })),
  /** The final invoices that took an advance off. */
  settledBy: z21.array(gastronomyDocumentNumber),
  /** The outlet whose document it is, when a rule moved its lines here. */
  movedFrom: z21.string().nullable()
});
var gastronomyEvents = z21.object({
  streams: z21.array(gastronomyAmount.extend({ kind: gastronomyEventLineKind.exclude(["advanceTaken", "advanceSettled"]) })),
  sales: gastronomyAmount,
  advances: z21.object({
    taken: gastronomyAmount,
    /** Taken off this outlet's own final invoices and bills: part of its revenue. */
    settled: gastronomyAmount
  }),
  documents: z21.array(gastronomyEventDocument)
});
var gastronomyOutletFigures = gastronomyAmount.extend({
  /** Null when a point of sale or series has no outlet yet. */
  outlet: gastronomyOutlet.nullable(),
  /**
   * Bills that carried revenue, and documents no bill points to that are a
   * sale — not a correction or a replacement.
   */
  bills: z21.number().int(),
  /** Documents counted on their own — events, deposits, invoice-only sales. */
  documents: z21.number().int(),
  /** Revenue ÷ bills, documents counted as bills; null with no bills. */
  averageBill: gastronomyAmount.nullable(),
  /**
   * Guests on the bills, each bill capped at its hall's seats. Null for an
   * outlet that does not record covers — only the restaurant does, from
   * 2023-07.
   */
  covers: z21.number().int().nullable(),
  /** Revenue of bills at the till ÷ covers — a document records no guests; null when `covers` is null or 0. */
  spendPerCover: gastronomyAmount.nullable(),
  lastYear: gastronomyLastYear,
  /**
   * For the events outlet only: its sales — the final invoices' whole value,
   * before the advances they take off — and the advances taken, with both a
   * year back (364 days). Null elsewhere.
   */
  events: z21.object({
    sales: gastronomyAmount,
    advances: gastronomyAmount,
    lastYear: z21.object({ sales: gastronomyAmount, advances: gastronomyAmount })
  }).nullable()
});
var gastronomyLineSales = gastronomyAmount.extend({
  quantity: decimalString,
  lines: z21.number().int()
});
var gastronomyProductVariant = z21.object({
  id: z21.number().int().nullable(),
  name: z21.string().nullable()
});
var gastronomyTopProduct = gastronomyLineSales.extend({
  /** The data source it is of: Izzy's codes are unique within one database only. */
  sourceId: z21.int(),
  code: z21.string(),
  name: z21.string().nullable(),
  variant: gastronomyProductVariant.nullable(),
  groupName: z21.string().nullable()
});
var gastronomyMenuProduct = gastronomyLineSales.extend({
  /** The data source it is of: Izzy's codes are unique within one database only. */
  sourceId: z21.int(),
  code: z21.string(),
  name: z21.string().nullable(),
  variant: gastronomyProductVariant.nullable(),
  share: gastronomyShare,
  typicalShare: gastronomyShare
});
var gastronomyMenuShare = gastronomyLineSales.extend({
  /** The data source of the group; null with the group. */
  sourceId: z21.int().nullable(),
  groupCode: z21.string().nullable(),
  groupName: z21.string().nullable(),
  parentName: z21.string().nullable(),
  share: gastronomyShare,
  typicalShare: gastronomyShare,
  /** The products sold in it, as its own rows: sold in the span or with a typical share above zero. */
  products: z21.array(gastronomyMenuProduct)
});
var gastronomyPaymentGroup = z21.enum([
  "employee-card",
  "card",
  "cash",
  "transfer",
  "online",
  "prepaid",
  "room",
  "other",
  "none"
]);
var gastronomyPaymentType = gastronomyAmount.extend({
  code: z21.string(),
  name: z21.string().nullable(),
  lines: z21.number().int(),
  share: gastronomyShare,
  typicalShare: gastronomyShare
});
var gastronomyPaymentShare = gastronomyAmount.extend({
  group: gastronomyPaymentGroup,
  lines: z21.number().int(),
  share: gastronomyShare,
  typicalShare: gastronomyShare,
  types: z21.array(gastronomyPaymentType)
});
var gastronomyPayments = z21.object({
  groups: z21.array(gastronomyPaymentShare),
  /** Paid by internal settlement (`WEWN`) — consumption, gifts: not revenue. */
  internal: gastronomyAmount
});
var gastronomyMenuCategory = gastronomyLineSales.extend({
  categoryId: z21.number().int().nullable(),
  name: z21.string().nullable(),
  /**
   * Its place among the outlet's categories in their set order, from 0; null
   * for no category. A screen colours a category by it, so the colour stays
   * with the category from day to day.
   */
  order: z21.number().int().nullable(),
  share: gastronomyShare,
  typicalShare: gastronomyShare,
  groups: z21.array(gastronomyMenuShare)
});
var gastronomyOtherRemarks = z21.object({
  /** Every split product, with its variants in their order, for the pick. */
  products: z21.array(
    z21.object({
      sourceId: z21.int(),
      code: z21.string(),
      name: z21.string().nullable(),
      variants: z21.array(z21.object({ id: z21.number().int(), name: z21.string() }))
    })
  ),
  rows: z21.array(
    gastronomyLineSales.extend({
      sourceId: z21.int(),
      code: z21.string(),
      text: z21.string()
    })
  )
});
var gastronomyBestseller = gastronomyTopProduct.extend({
  typicalQuantity: decimalString
});
var gastronomyMissingProduct = z21.object({
  /** The data source it is of: Izzy's codes are unique within one database only. */
  sourceId: z21.int(),
  code: z21.string(),
  name: z21.string().nullable(),
  variant: gastronomyProductVariant.nullable(),
  groupName: z21.string().nullable(),
  typicalQuantity: decimalString,
  typical: gastronomyAmount
});
var gastronomyCorrection = gastronomyAmount.extend({
  /** The data source it is of: Izzy's ids are unique within one database only. */
  sourceId: z21.int(),
  id: z21.string(),
  series: z21.string(),
  type: z21.string(),
  number: z21.number().int(),
  /** The outlet it counts for — the sale's. */
  outletCode: z21.string().nullable(),
  /** The outlet of its own series, when that is another: it was entered there. */
  seriesOutletCode: z21.string().nullable(),
  issuedOn: isoDate,
  /** The day it counts on. */
  saleDay: isoDate,
  /** The document it corrects, or the invoice a replacement stands in for. */
  correctsId: z21.string(),
  /**
   * `replacement`: an invoice issued again in place of one a correction set to
   * zero — matched by exact values, same series and day — and counted with
   * the sale it replaces.
   */
  kind: z21.enum(["correction", "replacement"])
});
var gastronomyFreshness = z21.object({
  lastPushAt: z21.iso.datetime({ offset: true }).nullable(),
  /** Whether the day, or the period's last day, has been pushed as final. */
  isFinal: z21.boolean()
});
var gastronomySameWeekday = gastronomyAmount.extend({
  date: isoDate,
  bills: z21.number().int(),
  documents: z21.number().int(),
  /** Null when no weather is known for it: the provider may be down, or no location is set. */
  weather: weatherDay.nullable()
});
var gastronomyDayDetail = z21.object({
  outlet: z21.string(),
  /** The 8 same weekdays before the day, oldest first, closed ones included. */
  sameWeekdays: z21.array(gastronomySameWeekday).max(8),
  /** Median revenue and bills over the weeks with revenue; null when there were none. */
  median: gastronomyLastYear.nullable(),
  /** The weeks with bills — what the medians below are taken over. */
  billWeeks: z21.number().int().min(0).max(8),
  /**
   * Bill revenue per Warsaw closing hour, and the hour's median. An hour
   * appears when it had revenue on the day or in any compared week.
   */
  hours: z21.array(
    gastronomyAmount.extend({
      hour: z21.number().int().min(0).max(23),
      bills: z21.number().int(),
      /** Null when no week had bills. */
      median: gastronomyAmount.nullable()
    })
  ),
  /** The menu's structure by category against its median over `billWeeks`. */
  menu: z21.array(gastronomyMenuCategory),
  /** The day's split-product lines whose remark is in no variant. */
  otherRemarks: gastronomyOtherRemarks,
  payments: gastronomyPayments,
  /** The events outlet's view; null for any other outlet. */
  events: gastronomyEvents.nullable(),
  /** The day's best sellers, each with its median quantity. */
  products: z21.array(gastronomyBestseller),
  /**
   * Products sold in more than half of `billWeeks` and not on the day: a dish
   * off, a product out of stock.
   */
  missing: z21.array(gastronomyMissingProduct)
});
var gastronomyDay = z21.object({
  date: isoDate,
  /**
   * The day's weather at the company's weather location. Null when none is
   * known — no location set, the provider down — which is not an error: weather
   * explains a figure, it never stops one.
   */
  weather: weatherDay.nullable(),
  /** The weather on the day the r/r figures compare with, 364 days back. */
  lastYearWeather: weatherDay.nullable(),
  outlets: z21.array(gastronomyOutletFigures),
  /** Null when no outlet with a code had anything on the day. */
  detail: gastronomyDayDetail.nullable(),
  /**
   * Corrections issued on the day that count on another, and corrections
   * issued on another day that count on this one.
   */
  corrections: z21.array(gastronomyCorrection),
  freshness: gastronomyFreshness
});
var gastronomyPeriodDetail = z21.object({
  outlet: z21.string(),
  /** Revenue per day, every date with revenue this year or last. */
  daily: z21.array(
    gastronomyAmount.extend({
      date: isoDate,
      lastYear: gastronomyAmount
    })
  ),
  /** Bill revenue by ISO weekday (1 = Monday) and Warsaw closing hour. */
  heatmap: z21.array(
    gastronomyAmount.extend({
      weekday: z21.number().int().min(1).max(7),
      hour: z21.number().int().min(0).max(23),
      bills: z21.number().int()
    })
  ),
  /** The menu's structure by category against last year's. */
  menu: z21.array(gastronomyMenuCategory),
  /** The period's split-product lines whose remark is in no variant. */
  otherRemarks: gastronomyOtherRemarks,
  payments: gastronomyPayments,
  /** The events outlet's view; null for any other outlet. */
  events: gastronomyEvents.nullable(),
  /** The period's best sellers, each with last year's quantity. */
  products: z21.array(gastronomyBestseller),
  /** Products sold last year and not in the period: a dish off the menu. */
  missing: z21.array(gastronomyMissingProduct)
});
var gastronomyPeriod = z21.object({
  from: isoDate,
  to: isoDate,
  outlets: z21.array(gastronomyOutletFigures),
  /** Null when no outlet with a code had anything in the period. */
  detail: gastronomyPeriodDetail.nullable(),
  freshness: gastronomyFreshness
});
var gastronomyDayItem = gastronomyAmount.extend({
  kind: z21.enum(["bill", "document"]),
  /** The data source it is of: Izzy's ids are unique within one database only. */
  sourceId: z21.int(),
  id: z21.string(),
  /** When the bill closed or the document was made. */
  at: instant3.nullable(),
  /** Its own gross, as the till issued it. */
  sourceGross: decimalString,
  /** A bill's internal settlement (`WEWN`), which is not revenue; null for none. */
  internal: decimalString.nullable(),
  /** A bill's receipt or invoice; for a document, itself. */
  document: z21.object({ id: z21.string(), series: z21.string(), type: z21.string(), number: z21.number().int(), issuedOn: isoDate }).nullable(),
  /** The document a correction corrects. */
  correctsId: z21.string().nullable(),
  /** The invoice a replacement was issued again in place of. */
  replacesId: z21.string().nullable(),
  /** A correction's issue date, when it counts on another day. */
  issuedOn: isoDate.nullable(),
  /** The outlet whose till sold it, when this is the part of a bill a settings rule moved here. */
  movedFrom: z21.string().nullable(),
  tableId: z21.number().int().nullable(),
  covers: z21.number().int().nullable(),
  user: z21.string().nullable(),
  payments: z21.array(z21.object({ type: z21.string(), name: z21.string().nullable(), amount: decimalString }))
});
var gastronomyDayItems = z21.object({
  date: isoDate,
  outlet: z21.string(),
  items: z21.array(gastronomyDayItem),
  total: gastronomyAmount
});
var gastronomyItemLines = z21.object({ lines: z21.array(salesLine) });
var gastronomyEventKind = z21.enum([
  "wedding",
  "communion",
  "birthday",
  "reception",
  "meeting",
  "catering",
  "stay",
  "other"
]);
var gastronomyForward = z21.object({
  /** The latest snapshot read; null before the first forward push. Each source's own latest is read. */
  takenOn: isoDate.nullable(),
  /** Table reservations per day and point of sale of a source, from `takenOn` on. */
  reservations: z21.array(
    z21.object({
      date: isoDate,
      sourceId: z21.int(),
      pointOfSale: z21.number().int(),
      pointOfSaleName: z21.string().nullable(),
      reservations: z21.number().int(),
      covers: z21.number().int(),
      cancelled: z21.number().int(),
      cancelledCovers: z21.number().int()
    })
  ),
  /** Reservations made in the 1, 3 and 7 days up to `takenOn`, not cancelled. */
  pickup: z21.array(
    z21.object({
      days: z21.number().int(),
      reservations: z21.number().int(),
      covers: z21.number().int()
    })
  ),
  /**
   * Events ahead, from the deposits: a deposit line reads "Zadatek za wesele w
   * dniu 11.09.2027", so its text gives the kind and the event date. Per event
   * month and kind; the amount is the deposits paid.
   */
  events: z21.array(
    gastronomyAmount.extend({
      month: z21.string().regex(/^\d{4}-\d{2}$/),
      kind: gastronomyEventKind,
      /** Deposit documents for events in that month. */
      count: z21.number().int()
    })
  ),
  /** Event bookings (`KALH`) per event month, from the same snapshot. */
  bookings: z21.array(
    z21.object({
      month: z21.string().regex(/^\d{4}-\d{2}$/),
      count: z21.number().int()
    })
  )
});
var gastronomyControls = z21.object({
  from: isoDate,
  to: isoDate,
  /**
   * Deletes somebody authorised, per authoriser and outlet. Deletes of a line
   * not yet sent are counted apart in `newLineDeletes`: normal work.
   */
  deletes: z21.array(
    z21.object({
      authorisedBy: z21.string().nullable(),
      outletCode: z21.string().nullable(),
      count: z21.number().int()
    })
  ),
  newLineDeletes: z21.number().int(),
  priceChanges: z21.array(
    z21.object({ authorisedBy: z21.string().nullable(), count: z21.number().int() })
  ),
  /**
   * Cancelled lines that had been printed, per outlet, at their price. A line
   * above 20 in quantity or 1 000 PLN a unit is left out as a typo — a
   * quantity typed into the price field and taken off at once — and a line at
   * or below zero as a deduction.
   */
  voids: z21.array(
    gastronomyAmount.extend({ outletCode: z21.string().nullable(), lines: z21.number().int() })
  ),
  /** Bill discounts per reason, with the value off the list price. */
  discounts: z21.array(
    gastronomyAmount.extend({ reason: z21.string().nullable(), bills: z21.number().int() })
  ),
  /** Bills cancelled, per the reason the till recorded. */
  cancelledBills: z21.array(z21.object({ reason: z21.string().nullable(), count: z21.number().int() })),
  /**
   * Corrections that count on another day than they were issued, issued in
   * the range or counting on a day in it — what a month here differs from the
   * books by.
   */
  corrections: z21.array(gastronomyCorrection),
  /**
   * Internal settlement (`WEWN`) that is not revenue, per reason — shown for
   * audit. A reason that is revenue is not here: at a canteen, the employee
   * card.
   */
  internal: z21.array(
    gastronomyAmount.extend({
      reason: z21.string().nullable(),
      name: z21.string().nullable(),
      bills: z21.number().int()
    })
  ),
  /**
   * Bills closed with no payment — a typo or a void — where the module leaves
   * them out of revenue (catering). Absent where such a bill counts at its
   * gross (gastronomy).
   */
  unpaidBills: z21.object({
    /** Per outlet of the till: how many, and their gross as the till has it. */
    outlets: z21.array(gastronomyAmount.extend({ outletCode: z21.string().nullable(), count: z21.number().int() })),
    /** The largest, so each can be opened (`items/lines?kind=bill`); at most `UNPAID_BILLS_LISTED`. */
    largest: z21.array(
      gastronomyAmount.extend({
        sourceId: z21.int(),
        id: z21.string(),
        date: isoDate,
        outletCode: z21.string().nullable()
      })
    )
  }).optional(),
  /**
   * The names of the outlets the controls name, for a module the web has no
   * catalogue of outlets for (catering's sites). Absent for gastronomy.
   */
  outletNames: z21.array(z21.object({ code: z21.string(), name: z21.string() })).optional()
});
var UNPAID_BILLS_LISTED = 20;
var categoryName = z21.string().trim().min(1).max(64);
var gastronomySettingsOutlet = z21.object({
  id: z21.number().int(),
  code: z21.string(),
  name: z21.string(),
  isFood: z21.boolean(),
  recordsCovers: z21.boolean(),
  position: z21.number().int(),
  /** Set when marked deleted; the outlet is then off the screens and the pickers. */
  deletedAt: z21.string().nullable()
});
var gastronomySettings = z21.object({
  sourceId: z21.int().nullable(),
  outlets: z21.array(gastronomySettingsOutlet),
  /** Null `outletId`: revenue under no outlet. */
  pointsOfSale: z21.array(z21.object({ id: z21.number().int(), name: z21.string(), outletId: z21.number().int().nullable() })),
  /** Null `outletId`: not gastronomy revenue unless placed — the canteen's `VAT` stays so. */
  series: z21.array(z21.object({ code: z21.string(), name: z21.string(), outletId: z21.number().int().nullable() })),
  categories: z21.array(
    z21.object({ id: z21.number().int(), outletId: z21.number().int(), name: z21.string(), position: z21.number().int() })
  ),
  /** Izzy's product group tree. */
  groups: z21.array(z21.object({ code: z21.string(), name: z21.string(), parent: z21.string().nullable() })),
  groupCategories: z21.array(
    z21.object({ outletId: z21.number().int(), productGroup: z21.string(), categoryId: z21.number().int() })
  ),
  productCategories: z21.array(
    z21.object({
      outletId: z21.number().int(),
      productCode: z21.string(),
      productName: z21.string().nullable(),
      productGroup: z21.string().nullable(),
      categoryId: z21.number().int(),
      /** Its bill-line sales of the last 365 days that count for the outlet, part of its group's in `groupSales`. */
      gross: decimalString
    })
  ),
  /**
   * Izzy groups sold at `outletId` that count for `toOutletId`; their subgroups
   * follow unless they have a rule of their own. A rule to the outlet itself
   * keeps a subgroup home.
   */
  groupMoves: z21.array(
    z21.object({ outletId: z21.number().int(), productGroup: z21.string(), toOutletId: z21.number().int() })
  ),
  /** Single products sold at `outletId` that count for `toOutletId`, whatever their group's rule. */
  productMoves: z21.array(
    z21.object({
      outletId: z21.number().int(),
      productCode: z21.string(),
      productName: z21.string().nullable(),
      productGroup: z21.string().nullable(),
      toOutletId: z21.number().int(),
      /** Its bill-line sales of the last 365 days at the outlet's tills. */
      gross: decimalString
    })
  ),
  /**
   * Bill-line sales of the last 365 days per outlet whose till sold them
   * (`outletId`), outlet they count for (`countsFor`) and Izzy group — which
   * groups an outlet actually sells, and how much rides on each placement.
   */
  groupSales: z21.array(
    z21.object({
      outletId: z21.number().int(),
      countsFor: z21.number().int(),
      productGroup: z21.string().nullable(),
      gross: decimalString,
      products: z21.number().int()
    })
  ),
  /** Revenue of the last 365 days under no outlet: a point of sale or series not placed. */
  unassignedGross: decimalString
});
var gastronomySettingsProducts = z21.object({
  products: z21.array(
    z21.object({
      code: z21.string(),
      name: z21.string().nullable(),
      productGroup: z21.string().nullable(),
      groupName: z21.string().nullable(),
      /** Bill-line gross of the last 365 days sold at the outlet's tills or counting for it. */
      gross: decimalString,
      /** Placed on its own; null when it follows its group. */
      categoryId: z21.number().int().nullable(),
      /** Its category, where it counts: its own placement, else its group's. Null for none. */
      countsIn: z21.number().int().nullable(),
      /** The outlet its sales at this outlet's tills count for; null when it is not sold here, only moved in. */
      countsFor: z21.number().int().nullable(),
      /** Its own rule at this outlet; null when it follows its group's. */
      moveTo: z21.number().int().nullable(),
      /** Some of it is sold at another outlet's tills and moved here. */
      movedIn: z21.boolean()
    })
  )
});
var updateGastronomyOutlet = z21.object({
  name: z21.string().trim().min(1).max(64).optional(),
  isFood: z21.boolean().optional(),
  recordsCovers: z21.boolean().optional(),
  position: z21.number().int().min(0).max(1e3).optional()
}).strict();
var createGastronomyOutlet = z21.object({
  code: z21.string().trim().regex(/^[a-z0-9-]{1,32}$/),
  name: z21.string().trim().min(1).max(64),
  isFood: z21.boolean().default(true),
  recordsCovers: z21.boolean().default(false)
}).strict();
var assignGastronomyOutlet = z21.object({ sourceId: z21.int().positive(), outletId: z21.number().int().positive().nullable() }).strict();
var createGastronomyCategory = z21.object({
  outletId: z21.number().int().positive(),
  name: categoryName,
  position: z21.number().int().min(0).max(1e3).optional()
}).strict();
var updateGastronomyCategory = z21.object({ name: categoryName.optional(), position: z21.number().int().min(0).max(1e3).optional() }).strict();
var gastronomyCategory = z21.object({
  id: z21.number().int(),
  outletId: z21.number().int(),
  name: z21.string(),
  position: z21.number().int()
});
var placeGastronomyGroup = z21.object({
  /** The data source the group or product is of. */
  sourceId: z21.int().positive(),
  outletId: z21.number().int().positive(),
  productGroup: z21.string().min(1).max(20),
  categoryId: z21.number().int().positive().nullable()
}).strict();
var placeGastronomyProduct = z21.object({
  /** The data source the group or product is of. */
  sourceId: z21.int().positive(),
  outletId: z21.number().int().positive(),
  productCode: z21.string().min(1).max(20),
  categoryId: z21.number().int().positive().nullable()
}).strict();
var moveGastronomyGroup = z21.object({
  /** The data source the group or product is of. */
  sourceId: z21.int().positive(),
  outletId: z21.number().int().positive(),
  productGroup: z21.string().min(1).max(20),
  toOutletId: z21.number().int().positive().nullable()
}).strict();
var moveGastronomyProduct = z21.object({
  /** The data source the group or product is of. */
  sourceId: z21.int().positive(),
  outletId: z21.number().int().positive(),
  productCode: z21.string().min(1).max(20),
  toOutletId: z21.number().int().positive().nullable()
}).strict();
var gastronomySplitProducts = z21.object({
  /** The data source the products are of; null when gastronomy derives none. */
  sourceId: z21.int().nullable(),
  days: z21.number().int(),
  products: z21.array(
    z21.object({
      code: z21.string(),
      name: z21.string().nullable(),
      groupName: z21.string().nullable(),
      variants: z21.array(z21.object({ id: z21.number().int(), name: z21.string(), words: z21.array(z21.string()) })),
      remarks: z21.array(
        z21.object({
          text: z21.string(),
          lines: z21.number().int(),
          gross: decimalString,
          variantId: z21.number().int().nullable()
        })
      ),
      lines: z21.number().int(),
      unmatched: z21.number().int()
    })
  )
});
var setGastronomySplitProduct = z21.object({
  /** The data source the product is of. */
  sourceId: z21.int().positive(),
  variants: z21.array(
    z21.object({
      id: z21.number().int().positive().optional(),
      name: z21.string().trim().min(1).max(64),
      words: z21.array(z21.string().trim().min(1).max(64)).min(1).max(500)
    }).strict()
  ).max(100).refine(
    (variants) => new Set(variants.map((variant) => variant.name.toLowerCase())).size === variants.length,
    "variant names must differ"
  )
}).strict();
var assignGastronomyRemark = z21.object({ sourceId: z21.int().positive(), text: z21.string().trim().min(1).max(1200) }).strict();
var ingestGastronomyResult = z21.object({
  businessDate: isoDate.optional(),
  takenOn: isoDate.optional(),
  /** True when this replaced an earlier push of the same day or snapshot. */
  replaced: z21.boolean(),
  /** How many times this day has been pushed. */
  pushCount: z21.number().int().optional(),
  /** Whether the day was derived. A failed derive keeps the staged push. */
  derived: z21.boolean(),
  /** Row counts as stored. */
  counts: z21.record(z21.string(), z21.number().int())
});

// src/cashier.ts
import { z as z22 } from "zod";
var isoDate2 = z22.iso.date();
var instant4 = z22.iso.datetime({ offset: true });
var money2 = z22.string().regex(/^-?\d{1,12}(\.\d{1,2})?$/, 'expected an amount with at most two decimals, e.g. "12.30"');
var positiveMoney = money2.refine((value) => !value.startsWith("-"), "must not be negative");
var name = z22.string().trim().min(1).max(100);
var note = z22.string().trim().max(2e3);
var cashierSalesModule = z22.enum(["gastronomy", "hotel", "catering"]);
var cashDeskKind = z22.enum(["sales", "main"]);
var cashierPaymentKind = z22.enum(["cash", "card", "transfer", "none"]);
var dayStart = z22.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");
var cashStash = z22.enum(["A", "B"]);
var cashierPrinter = z22.object({
  id: z22.int(),
  deskId: z22.int(),
  name: z22.string(),
  model: z22.string().nullable(),
  /** The fiscal device's `numer unikatowy`. */
  uniqueNumber: z22.string().nullable(),
  isActive: z22.boolean()
});
var cashierTerminal = z22.object({
  id: z22.int(),
  deskId: z22.int(),
  name: z22.string(),
  /** Fiserv's terminal id. */
  tid: z22.string().nullable(),
  isActive: z22.boolean()
});
var cashierDeskSeries = z22.object({ sourceId: z22.int(), series: z22.string(), printerId: z22.int().nullable().default(null) });
var cashDesk = z22.object({
  id: z22.int(),
  kind: cashDeskKind,
  name: z22.string(),
  /** A main desk's company and stash; null on a sales desk. */
  companyId: z22.int().nullable(),
  stash: cashStash.nullable(),
  /** The main desk a sales desk hands its cash to; null until set, and on a main desk. */
  targetDeskId: z22.int().nullable(),
  /**
   * Where the desk's day starts. A document belongs to its issue date's day;
   * one created before this hour on its issue date belongs to the day before.
   */
  dayStart,
  /** The desk's cash before its first report. */
  openingCash: decimalString,
  /** The first day the desk closes. Null for a main desk. */
  openingDate: isoDate2.nullable(),
  /**
   * What a sales desk keeps in its drawer after the handover: 0 where every
   * złoty goes, a fixed float where change must stay. Null when not set, and
   * on a main desk.
   */
  floatTarget: decimalString.nullable(),
  /** A stash A desk's shareholder: whom its loans come from. Null when not set, and on any other desk. */
  shareholder: z22.string().nullable(),
  isActive: z22.boolean(),
  position: z22.int(),
  /**
   * The data sources a sales desk takes data from, oldest first. Its series
   * and its payment mapping are per source; a main desk has none.
   */
  sourceIds: z22.array(z22.int()),
  series: z22.array(cashierDeskSeries),
  printers: z22.array(cashierPrinter),
  terminals: z22.array(cashierTerminal)
});
var cashierSeriesOption = z22.object({
  sourceId: z22.int(),
  series: z22.string(),
  /** The series' own name in izzy. */
  name: z22.string().nullable(),
  /** The points of sale whose bills close on this series, so it reads as points of sale. */
  pointsOfSale: z22.array(z22.string()),
  /** The desk it is bound to, or null. */
  deskId: z22.int().nullable()
});
var cashierPaymentType = z22.object({
  deskId: z22.int(),
  sourceId: z22.int(),
  type: z22.string(),
  /** Izzy's label for it, when the dictionary has one. */
  name: z22.string().nullable(),
  /** Null until set: an unmapped type counts as none. */
  kind: cashierPaymentKind.nullable()
});
var cashierThresholds = z22.object({
  /** A fiscal report's VAT and gross may be off by this much per rate. */
  fiscalTolerance: positiveMoney,
  /** Declared and system figures may differ by this much. */
  systemTolerance: positiveMoney,
  /** Counted cash this far from expected is a warning… */
  cashWarning: positiveMoney,
  /** …and this far an error. */
  cashError: positiveMoney
});
var cashierSettings = z22.object({
  /** Every data source, from Administracja → Źródła danych; a desk binds to series of an active one. */
  sources: z22.array(source),
  /** Every company, from Administracja → Firmy; main desks are added for an active one that has none. */
  companies: z22.array(company),
  desks: z22.array(cashDesk),
  series: z22.array(cashierSeriesOption),
  /** Each sales desk's mapping, per source on the desk: every code the source's feed lists, and any mapped since gone. */
  paymentTypes: z22.array(cashierPaymentType),
  thresholds: cashierThresholds
});
var createCashDesk = z22.object({
  name,
  targetDeskId: z22.int().positive().nullable().default(null),
  dayStart: dayStart.default("04:00"),
  openingCash: positiveMoney.default("0"),
  openingDate: isoDate2.nullable().default(null),
  floatTarget: positiveMoney.nullable().default(null)
});
var updateCashDesk = z22.object({
  name: name.optional(),
  dayStart: dayStart.optional(),
  openingCash: positiveMoney.optional(),
  openingDate: isoDate2.nullable().optional(),
  /** A sales desk only: what stays in the drawer after the handover. */
  floatTarget: positiveMoney.nullable().optional(),
  /** A stash A desk only: whom its loans come from. Empty clears it. */
  shareholder: z22.string().trim().max(200).transform((value) => value === "" ? null : value).nullable().optional(),
  isActive: z22.boolean().optional(),
  position: z22.int().min(0).optional(),
  /** A sales desk only: the main desk it hands its cash to. */
  targetDeskId: z22.int().positive().nullable().optional()
});
var addMainDesks = z22.object({ companyId: z22.int().positive() });
var setCashDeskSeries = z22.object({ series: z22.array(cashierDeskSeries).max(50) });
var savePrinter = z22.object({
  name,
  model: z22.string().trim().max(100).nullable().default(null),
  uniqueNumber: z22.string().trim().max(40).nullable().default(null),
  isActive: z22.boolean().default(true)
});
var saveTerminal = z22.object({
  name,
  tid: z22.string().trim().max(40).nullable().default(null),
  isActive: z22.boolean().default(true)
});
var setCashierPaymentType = z22.object({ kind: cashierPaymentKind });
var cashReportState = z22.enum(["draft", "submitted", "returned", "accepted"]);
var cashCheckCode = z22.enum([
  "FISCAL_NOT_VALID",
  "TERMINAL_NOT_VALID",
  "SYSTEM_CASH_NOT_VALID",
  "SYSTEM_CARD_NOT_VALID",
  "SYSTEM_TOTAL_NOT_VALID",
  "SMALL_CASH_DIFF",
  "LARGE_CASH_DIFF",
  "PREVIOUS_REPORT_NOT_APPROVED",
  "NO_PREVIOUS_REPORT",
  /** A day with sales on the desk's series and no report lies before this one. */
  "MISSING_REPORT",
  /** A fiscal report printed on another day than the report's. */
  "FISCAL_DATE_MISMATCH",
  /** The system figures are older than the last fiscal report. */
  "SYSTEM_DATA_STALE",
  /**
   * A document whose money the day counts is counted by another report too:
   * pulled in there as well, or settled in its own desk's day already.
   * `detail.documents` is a JSON list of `{ ref, desk, date }`.
   */
  "DOCUMENT_COUNTED_TWICE",
  /** An active printer has no fiscal report; `detail.devices` names them. Without it nothing is compared. */
  "FISCAL_MISSING",
  /** An active terminal has no batch close; `detail.devices` names them. Without it nothing is compared. */
  "TERMINAL_MISSING"
]);
var CASH_SLIP_CHECKS = ["FISCAL_MISSING", "TERMINAL_MISSING"];
var CASH_CHECKS_TO_EXPLAIN = [
  "FISCAL_NOT_VALID",
  "TERMINAL_NOT_VALID",
  "SYSTEM_CASH_NOT_VALID",
  "SYSTEM_CARD_NOT_VALID",
  "SYSTEM_TOTAL_NOT_VALID",
  "SMALL_CASH_DIFF",
  "LARGE_CASH_DIFF",
  "FISCAL_DATE_MISMATCH"
];
var needsExplanation = (checks) => checks.some((check) => CASH_CHECKS_TO_EXPLAIN.includes(check.code));
var cashCheckLevel = z22.enum(["warning", "error", "blocking"]);
var cashCheck = z22.object({
  code: cashCheckCode,
  level: cashCheckLevel,
  /** The figures the check compared, for the screen to say by how much. */
  detail: z22.record(z22.string(), z22.string()).nullable()
});
var FISCAL_RATES = { a: "23", b: "8", c: "5", d: "0" };
var rateFigures = z22.object({ a: money2, b: money2, c: money2, d: money2 });
var cashFiscalReport = z22.object({
  printerId: z22.int().positive(),
  printedAt: instant4,
  /** The slip's total gross. */
  totalGross: money2,
  nett: rateFigures,
  vat: rateFigures
});
var cashTerminalReport = z22.object({
  terminalId: z22.int().positive(),
  printedAt: instant4,
  total: money2
});
var documentRef = z22.object({ sourceId: z22.int().positive(), documentId: z22.string().min(1).max(55) });
var cashPaymentMethod = z22.enum(["cash", "card", "transfer"]);
var cashReportDraft = z22.object({
  fiscalReports: z22.array(cashFiscalReport).max(20).default([]),
  terminalReports: z22.array(cashTerminalReport).max(30).default([]),
  /** Null until counted. */
  countedCash: positiveMoney.nullable().default(null),
  /** Documents of the window the cashier takes out of the day. */
  dropped: z22.array(documentRef.extend({ reason: note.min(1) })).max(500).default([]),
  /** Documents from outside the window the cashier puts in. */
  pulledIn: z22.array(documentRef.extend({ reason: note.min(1) })).max(500).default([]),
  /** Invoices and fiscal errors the cashier holds on paper. */
  checked: z22.array(documentRef).max(2e3).default([]),
  /**
   * Documents the cashier says were paid otherwise than izzy recorded, with
   * what went to each method — one method or a split — for this close only:
   * izzy keeps its record. The three add up to the document's total (for a
   * correction, the difference it makes). One dropped from the day, or one
   * another desk settled, is left as it is.
   */
  paymentChanges: z22.array(documentRef.extend({ cash: money2, card: money2, transfer: money2, reason: note.min(1) })).max(500).default([])
});
var cashDocument = z22.object({
  /** The module of its source's kind: it names the payment types. */
  module: cashierSalesModule,
  sourceId: z22.int(),
  /** The source's name, to tell apart two sources' documents of one number. */
  sourceName: z22.string(),
  documentId: z22.string(),
  series: z22.string(),
  /** `Paragon NR 12/09/K/2026` as the old reports showed it. */
  ref: z22.string(),
  type: z22.string(),
  createdAt: instant4,
  /** For a correction, the difference it makes. */
  gross: decimalString,
  cash: decimalString,
  card: decimalString,
  transfer: decimalString,
  isFiscal: z22.boolean(),
  isCorrection: z22.boolean(),
  isInvoice: z22.boolean(),
  isDropped: z22.boolean(),
  isPulledIn: z22.boolean(),
  isChecked: z22.boolean(),
  /** Of a series the desk settles; one of another desk's was printed there, so it counts as non-fiscal. */
  isDeskSeries: z22.boolean(),
  /** The desk that pulled it in and took its money: printed here, it brings this drawer nothing. */
  settledBy: z22.string().nullable(),
  /** Why it was dropped from the day or pulled into it. */
  reason: z22.string().nullable(),
  /** The cashier's change of its payments (`paymentChanges`); `cash`/`card`/`transfer` above are after it. */
  paymentChange: z22.object({ cash: decimalString, card: decimalString, transfer: decimalString, reason: z22.string() }).nullable(),
  /** What izzy recorded, while a `paymentChange` stands; null without one. */
  izzy: z22.object({ cash: decimalString, card: decimalString, transfer: decimalString }).nullable()
});
var CASH_DOCUMENT_SEARCH_LIMIT = 50;
var cashFoundDocument = cashDocument.extend({
  /** The desk that settles its series, when another one does. */
  seriesDeskName: z22.string().nullable()
});
var cashDocumentLines = z22.object({ lines: z22.array(salesLine) });
var cashPaymentSplit = z22.object({
  cash: decimalString,
  card: decimalString,
  transfer: decimalString,
  total: decimalString
});
var cashBalance = z22.object({
  opening: decimalString,
  /** The day's cash, fiscal and non-fiscal: what the printer says less card and transfer, plus non-fiscal cash. */
  takings: decimalString,
  /** Transfers out of the desk sent in the day. */
  sent: decimalString,
  /** Transfers into the desk accepted in the day. */
  received: decimalString,
  expected: decimalString,
  counted: decimalString.nullable(),
  /** Counted − expected. */
  difference: decimalString.nullable()
});
var cashDayFigures = z22.object({
  /** All documents of the window and those pulled in, dropped ones flagged. */
  system: cashPaymentSplit,
  nonFiscal: cashPaymentSplit,
  /** Fiscal total from the reports, card from the terminals, transfer from izzy. */
  declared: cashPaymentSplit,
  balance: cashBalance,
  checks: z22.array(cashCheck),
  /** When the newest izzy data for the desk's series arrived. Null when none did. */
  systemAsOf: instant4.nullable()
});
var decimalRates = z22.object({ a: decimalString, b: decimalString, c: decimalString, d: decimalString });
var cashFiscalSuggestion = z22.object({
  nett: decimalRates,
  vat: decimalRates,
  /** The gross of every counted line, an unmapped rate's too. */
  totalGross: decimalString,
  /** Counted documents with no lines stored: their gross is missing from the rates. */
  documentsWithoutLines: z22.int().nonnegative(),
  /** Lines at a rate no printer letter stands for, per rate in percent. */
  unmapped: z22.array(z22.object({ rate: z22.int(), gross: decimalString }))
});
var cashComment = z22.object({
  id: z22.int(),
  kind: z22.enum(["note", "return", "reopen"]),
  text: z22.string(),
  createdAt: instant4,
  createdByName: z22.string()
});
var cashRevisionSummary = z22.object({
  id: z22.int(),
  number: z22.int(),
  submittedAt: instant4,
  submittedByName: z22.string()
});
var cashHandoverCorrection = z22.object({
  amount: decimalString,
  acceptedAt: instant4,
  acceptedByName: z22.string()
});
var cashReport = z22.object({
  id: z22.int().nullable(),
  deskId: z22.int(),
  businessDate: isoDate2,
  state: cashReportState.nullable(),
  /** The hours the desk day spans, for the screen; documents are chosen by issue date. */
  window: z22.object({ from: instant4, to: instant4 }),
  /** Live while the report is a draft; the current revision's once submitted. */
  draft: cashReportDraft,
  figures: cashDayFigures,
  documents: z22.array(cashDocument),
  /** The per-rate figures to copy into a fiscal report; null once the report is frozen. */
  fiscalSuggestion: cashFiscalSuggestion.nullable(),
  /**
   * Per active printer, what its daily report should say: from the documents of
   * the series set to it, or of every series when the desk has one active
   * printer. Empty once the report is frozen.
   */
  printerSuggestions: z22.array(z22.object({ printerId: z22.int(), suggestion: cashFiscalSuggestion })),
  /**
   * With more than one active printer: the series with fiscal documents in the
   * day that no printer is set for. Their documents are in no printer's suggestion.
   */
  unassignedSeries: z22.array(z22.object({ sourceId: z22.int(), series: z22.string() })),
  /** The transfer this report's submit made: the day's whole cash takings handed to the main desk. */
  handover: z22.object({
    transferId: z22.int(),
    amount: decimalString,
    bagNumber: z22.string().nullable(),
    state: z22.enum(["sent", "accepted"]),
    acceptedAt: instant4.nullable(),
    acceptedByName: z22.string().nullable(),
    /** What the main cashier had accepted before a correction changed the amount; null otherwise. */
    correctedFrom: cashHandoverCorrection.nullable()
  }).nullable(),
  /**
   * The desk's printers and terminals a report may name: the active ones, and
   * any inactive one the draft names already, so a saved slip keeps its device.
   */
  printers: z22.array(z22.object({ id: z22.int(), name: z22.string(), isActive: z22.boolean() })),
  terminals: z22.array(z22.object({ id: z22.int(), name: z22.string(), isActive: z22.boolean() })),
  /** The tolerance the fiscal check uses, for the screen's hints. */
  fiscalTolerance: decimalString,
  comments: z22.array(cashComment),
  revisions: z22.array(cashRevisionSummary),
  /** Which revision `figures` and `documents` come from; null for a live draft. */
  revisionNumber: z22.int().nullable(),
  /** The comment given with the current revision's submit: the cashier's explanation. */
  submitComment: z22.string().nullable(),
  /** The revision before the current one, so a reviewer sees what changed. Null for a draft or revision 1. */
  previousRevision: z22.object({ number: z22.int(), draft: cashReportDraft }).nullable(),
  submittedAt: instant4.nullable(),
  submittedByName: z22.string().nullable(),
  acceptedAt: instant4.nullable(),
  acceptedByName: z22.string().nullable(),
  /** The day before this one the desk last reported, if any. */
  previousDate: isoDate2.nullable(),
  /**
   * Why a day still open is not the user's to edit: before the desk's first day,
   * not begun yet, or the user does not close days at the desk. Null when they
   * may, and once the day is submitted or accepted (its state says so).
   */
  readOnlyReason: z22.enum(["beforeOpening", "future", "notReporter"]).nullable(),
  /** The desk's first day, named when `readOnlyReason` is `beforeOpening`. */
  openingDate: isoDate2.nullable(),
  /** What the signed-in user may do with this report now. */
  can: z22.object({
    edit: z22.boolean(),
    submit: z22.boolean(),
    accept: z22.boolean(),
    return: z22.boolean(),
    reopen: z22.boolean(),
    /** The user also accepts at this desk: their submit accepts the report at once. */
    acceptOnSubmit: z22.boolean(),
    /** The desk has a main desk set: the submit hands the day's cash to it. */
    handOver: z22.boolean()
  }),
  /**
   * The desk's latest closed day after this one, when there is one: this day
   * is returned or reopened only once that one is — the cash of a day goes
   * into the days after it. Null when this is the last closed day.
   */
  laterClosedDay: isoDate2.nullable()
});
var cashReportQuery = z22.object({ desk: z22.coerce.number().int().positive(), date: isoDate2 });
var saveCashReportDraft = cashReportDraft;
var submitCashReport = z22.object({
  /** Required when a check the cashier can explain failed (`needsExplanation`). */
  comment: note.nullable().default(null),
  /**
   * The bag the day's cash goes to the main desk in. The amount is never the
   * cashier's: the submit hands over the day's whole cash takings (nothing
   * when they are not above 0), and it counts on the next desk day — the cash
   * leaves after the count.
   */
  bagNumber: z22.string().trim().max(40).nullable().default(null)
});
var cashReportReason = z22.object({ reason: note.min(1) });
var addCashComment = z22.object({ text: note.min(1) });
var cashFloatLevel = z22.enum(["ok", "warning", "error"]);
var cashMonthHandover = z22.object({
  amount: decimalString,
  state: z22.enum(["sent", "accepted"]),
  sentAt: instant4,
  acceptedAt: instant4.nullable(),
  acceptedByName: z22.string().nullable()
});
var cashMonthRow = z22.object({
  businessDate: isoDate2,
  reportId: z22.int().nullable(),
  state: cashReportState.nullable(),
  createdByName: z22.string().nullable(),
  submittedAt: instant4.nullable(),
  /** Who submitted the current revision. */
  submittedByName: z22.string().nullable(),
  /**
   * The day should be submitted and is not: on or after the desk's first day,
   * before the current desk day, with no report or only a draft.
   */
  overdue: z22.boolean(),
  declared: cashPaymentSplit.nullable(),
  balance: cashBalance.nullable(),
  checks: z22.array(cashCheck),
  acceptedByName: z22.string().nullable(),
  acceptedAt: instant4.nullable(),
  revisionCount: z22.int(),
  handover: cashMonthHandover.nullable(),
  /** Counted cash less the handover: what stays in the drawer. Null until the day is submitted. */
  afterHandover: decimalString.nullable(),
  /** `afterHandover` against the desk's `floatTarget`; null without either. */
  afterHandoverLevel: cashFloatLevel.nullable()
});
var cashMonth = z22.object({
  deskId: z22.int(),
  year: yearNumber,
  month: monthNumber,
  /** What the desk keeps after the handover; null when not set. */
  floatTarget: decimalString.nullable(),
  rows: z22.array(cashMonthRow),
  totals: z22.object({
    /** The month's cash takings. */
    takings: decimalString,
    /** What the month's submits handed to the main desk. */
    handedOver: decimalString,
    /** The last submitted day's cash after its handover: what the month leaves in the drawer. */
    afterHandover: decimalString.nullable(),
    afterHandoverLevel: cashFloatLevel.nullable()
  })
});
var cashMonthQuery = z22.object({
  desk: z22.coerce.number().int().positive(),
  year: z22.coerce.number().pipe(yearNumber),
  month: z22.coerce.number().pipe(monthNumber)
});
var cashPendingReport = z22.object({
  reportId: z22.int(),
  deskId: z22.int(),
  deskName: z22.string(),
  businessDate: isoDate2,
  submittedAt: instant4.nullable(),
  submittedByName: z22.string().nullable(),
  revisionNumber: z22.int(),
  declared: cashPaymentSplit.nullable(),
  balance: cashBalance.nullable(),
  checks: z22.array(cashCheck),
  /** The comment given with the submit. */
  explanation: z22.string().nullable(),
  /** False when four eyes refuse it: the user submitted it. */
  canAccept: z22.boolean()
});
var cashPendingReports = z22.object({ reports: z22.array(cashPendingReport) });
var cashTransferState = z22.enum(["sent", "accepted"]);
var cashTransfer = z22.object({
  id: z22.int(),
  fromDeskId: z22.int(),
  toDeskId: z22.int(),
  /**
   * The desks' names. A main desk is named by its company and stash only to
   * those who keep main desks or read everything; to a cashier it is "the main
   * desk", and its id says nothing more.
   */
  fromName: z22.string(),
  toName: z22.string(),
  amount: decimalString,
  bagNumber: z22.string().nullable(),
  note: z22.string().nullable(),
  state: cashTransferState,
  sentAt: instant4,
  sentByName: z22.string(),
  acceptedAt: instant4.nullable(),
  acceptedByName: z22.string().nullable(),
  countedAmount: decimalString.nullable(),
  /** Counted − sent; null until accepted. Always 0 now: a transfer is accepted only at its amount. */
  difference: decimalString.nullable(),
  /** The day whose report's submit made it: its whole cash takings. Null for cash sent by hand. */
  reportDate: isoDate2.nullable(),
  /** That report as it stands, for whoever counts the bag: its state, the cash counted − expected, the checks it failed. */
  report: z22.object({ state: cashReportState, difference: decimalString.nullable(), checks: z22.array(cashCheck) }).nullable(),
  correctedFrom: cashHandoverCorrection.nullable(),
  can: z22.object({
    /** Only at its amount: a different count returns the day (`returnDay`) or refuses the bag (`refuse`). */
    accept: z22.boolean(),
    /** The receiver sends the day back to the cashier: a day's handover still to accept, its report submitted or accepted. */
    returnDay: z22.boolean(),
    /** The receiver refuses cash sent by hand that does not count up; the sender sends it again. */
    refuse: z22.boolean()
  })
});
var cashTransfersQuery = z22.object({
  desk: z22.coerce.number().int().positive().optional(),
  /** With `desk`: only the transfers between it and this desk, either way. */
  with: z22.coerce.number().int().positive().optional(),
  state: cashTransferState.optional(),
  from: isoDate2.optional(),
  to: isoDate2.optional()
});
var cashOutstanding = z22.object({
  deskId: z22.int(),
  takings: decimalString,
  sent: decimalString,
  outstanding: decimalString
});
var sendCashTransfer = z22.object({
  fromDeskId: z22.int().positive(),
  toDeskId: z22.int().positive().nullable().default(null),
  amount: positiveMoney,
  bagNumber: z22.string().trim().max(40).nullable().default(null),
  /** Required when the amount is not what is outstanding. */
  note: note.nullable().default(null),
  /**
   * A main desk sending: the day it stands under in the books — both, between
   * two main desks. Null: today. A sales desk's handover is always today's.
   */
  businessDate: isoDate2.nullable().default(null)
});
var acceptCashTransfer = z22.object({
  countedAmount: positiveMoney,
  note: note.nullable().default(null)
});
var refuseCashTransfer = z22.object({ note: note.min(1) });
var cashBookEntryKind = z22.enum(["deposit", "withdrawal", "income", "expense", "loan", "repayment", "funding", "defunding", "move"]);
var cashBookLineKind = z22.enum([
  "deposit",
  "withdrawal",
  "income",
  "expense",
  "loan",
  "repayment",
  "funding",
  "defunding",
  "move",
  "move-in",
  "loan-given",
  "loan-returned",
  "transfer-in",
  "transfer-out",
  "payout",
  "payout-return"
]);
var cashPayoutPart = z22.enum(["a", "b", "bonus", "sc"]);
var cashPayoutParts = z22.object({
  a: decimalString.nullable(),
  b: decimalString.nullable(),
  bonus: decimalString.nullable(),
  sc: decimalString.nullable()
});
var cashBookLine = z22.object({
  /** `entry:12`, `transfer:40`, `payout:7`, `loan:12` or `move:12` — another desk's entry 12 seen from this one. */
  key: z22.string(),
  at: instant4,
  businessDate: isoDate2,
  kind: cashBookLineKind,
  /** The KP/KW number the system gave, `KP/09/001`; null on a typed document and on lines older than the numbers. */
  number: z22.string().nullable(),
  /** The document a person typed: an invoice, a receipt. */
  documentRef: z22.string().nullable(),
  description: z22.string().nullable(),
  /** Typed on an entry; the other desk on a transfer, the employee on a payout, the bank on a deposit. */
  counterparty: z22.string().nullable(),
  /** Signed: what it does to the desk. */
  amount: decimalString,
  createdByName: z22.string(),
  entryId: z22.int().nullable(),
  transferId: z22.int().nullable(),
  payoutId: z22.int().nullable(),
  /** The other main desk of a move or of stash A's loan: where a move goes, or where it came from. */
  otherDeskId: z22.int().nullable(),
  /** A transfer's other desk, its bag, and the day whose takings it is when a report's submit made it. */
  transfer: z22.object({ deskId: z22.int(), reportDate: isoDate2.nullable(), bagNumber: z22.string().nullable() }).nullable(),
  /** A payout's payroll month and what it paid of each part. */
  payout: z22.object({ year: yearNumber, month: monthNumber, parts: cashPayoutParts }).nullable(),
  /**
   * Wstępny: on a stash A desk, a line that takes cash out and leaves the desk
   * below zero. Stash A is counted cash and cannot hold less than nothing, so
   * the line waits for an earlier one — a loan, income — that covers it; then
   * it is a line like any other.
   */
  provisional: z22.boolean(),
  can: z22.object({
    /** A hand-entered line: change every field, or delete it. */
    edit: z22.boolean(),
    delete: z22.boolean(),
    /** A linked line: change only the day it stands under in this book. */
    editDate: z22.boolean()
  })
});
var cashBookPayouts = z22.object({
  year: yearNumber,
  month: monthNumber,
  /** Σ of what is left to pay, and the people it is owed to. */
  toPay: decimalString,
  toPayCount: z22.int(),
  /** Σ of what people hand back after payroll lowered a paid amount. */
  back: decimalString,
  backCount: z22.int()
});
var cashBook = z22.object({
  deskId: z22.int(),
  year: yearNumber,
  month: monthNumber,
  /** The month before's closing balance; the desk's opening cash (`openingCash`) before its first line. */
  openingBalance: decimalString,
  lines: z22.array(cashBookLine),
  closingBalance: decimalString,
  /**
   * On a stash A desk whose cash falls below zero by the month's end — in this
   * month or one before it: how much an earlier line must bring in, at least,
   * for no line to stay provisional. Null when nothing is short. The month
   * does not close while it is short.
   */
  shortfall: decimalString.nullable(),
  /**
   * On a stash A desk: how far its stash B stands below zero at the same
   * moment as `closingBalance` — cash B borrowed out of A's safe, which A's
   * book does not show. A holds that much less in hand. Null when B is not
   * below zero.
   */
  borrowedByB: decimalString.nullable(),
  /** Transfers sent to this desk and not accepted yet. */
  inTransit: decimalString,
  inTransitCount: z22.int(),
  /** The payroll month before the book's — paid out in this one. Only on a desk with a stash, for a user with `cashier.payouts`. */
  payouts: cashBookPayouts.nullable(),
  closed: z22.object({ closedAt: instant4, closedByName: z22.string(), balance: decimalString }).nullable(),
  /** The first day the month may close: the day after its last. A month still running does not close. */
  closesFrom: isoDate2,
  can: z22.object({ write: z22.boolean(), close: z22.boolean(), reopen: z22.boolean() })
});
var cashBookQuery = z22.object({
  desk: z22.coerce.number().int().positive(),
  year: z22.coerce.number().pipe(yearNumber),
  month: z22.coerce.number().pipe(monthNumber)
});
var optionalText2 = (max) => z22.string().trim().max(max).nullable().default(null).transform((value) => value === "" ? null : value);
var bookEntryFields = z22.object({
  kind: cashBookEntryKind,
  amount: positiveMoney,
  businessDate: isoDate2,
  description: optionalText2(500),
  documentRef: optionalText2(100),
  counterparty: optionalText2(200),
  /** A move's receiving stash B desk; null on any other line. */
  targetDeskId: z22.int().positive().nullable().default(null)
});
var targetNamed = (entry) => entry.kind === "move" === (entry.targetDeskId !== null);
var documentNamed = (entry) => !(entry.kind === "income" || entry.kind === "expense") || entry.documentRef !== null;
var lenderNamed = (entry) => !(entry.kind === "loan" || entry.kind === "repayment") || entry.counterparty !== null;
var addCashBookEntry = bookEntryFields.extend({ deskId: z22.int().positive() }).refine(documentNamed, { message: "cashier.documentRequired", path: ["documentRef"] }).refine(lenderNamed, { message: "cashier.lenderRequired", path: ["counterparty"] }).refine(targetNamed, { message: "cashier.moveTargetRequired", path: ["targetDeskId"] });
var updateCashBookEntry = bookEntryFields.refine(documentNamed, { message: "cashier.documentRequired", path: ["documentRef"] }).refine(lenderNamed, { message: "cashier.lenderRequired", path: ["counterparty"] }).refine(targetNamed, { message: "cashier.moveTargetRequired", path: ["targetDeskId"] });
var setCashBookDate = z22.object({ deskId: z22.int().positive(), businessDate: isoDate2 });
var closeCashMonth = z22.object({
  year: yearNumber,
  month: monthNumber,
  balance: money2
});
var cashPayoutAmount = z22.object({
  now: decimalString,
  paid: decimalString,
  left: decimalString,
  /** Paid before, and payroll moved since. */
  changed: z22.boolean()
});
var cashPayoutRow = z22.object({
  employeeId: z22.int(),
  form: agreementForm,
  name: z22.string(),
  /** Marked ready (Gotowe) in payroll: only then is anything paid. */
  ready: z22.boolean(),
  a: cashPayoutAmount,
  b: cashPayoutAmount,
  bonus: cashPayoutAmount,
  sc: cashPayoutAmount,
  /** Σ left over the parts this desk pays. */
  left: decimalString
});
var cashPayout = z22.object({
  id: z22.int(),
  employeeId: z22.int(),
  form: agreementForm,
  name: z22.string(),
  parts: cashPayoutParts,
  /** Σ of the parts: above 0 paid out (KW), below 0 handed back (KP). */
  total: decimalString,
  businessDate: isoDate2,
  number: z22.string().nullable(),
  at: instant4,
  createdByName: z22.string(),
  can: z22.object({ undo: z22.boolean(), editDate: z22.boolean() })
});
var cashPayoutMonth = z22.object({
  deskId: z22.int(),
  /** The payroll month. */
  year: yearNumber,
  month: monthNumber,
  /** The parts this desk pays: `a` at stash A, `b`, `bonus`, `sc` at stash B. */
  parts: z22.array(cashPayoutPart),
  /** Ready rows with something left to pay or hand back, and rows paid before that are no longer ready. */
  rows: z22.array(cashPayoutRow),
  /** Rows not ready yet, with cash pay; nothing is paid until they are. */
  waiting: z22.array(z22.object({ employeeId: z22.int(), form: agreementForm, name: z22.string(), total: decimalString })),
  payouts: z22.array(cashPayout),
  /** The desk's cash now. */
  balance: decimalString,
  /** The number the next payout will take, `KW/10/006`, and the next return. */
  nextNumber: z22.object({ kw: z22.string(), kp: z22.string() }),
  can: z22.object({ pay: z22.boolean() })
});
var cashPayoutQuery = z22.object({
  desk: z22.coerce.number().int().positive(),
  year: z22.coerce.number().pipe(yearNumber),
  month: z22.coerce.number().pipe(monthNumber)
});
var payCash = z22.object({
  deskId: z22.int().positive(),
  year: yearNumber,
  month: monthNumber,
  employeeId: z22.int().positive(),
  form: agreementForm,
  parts: z22.array(cashPayoutPart).min(1),
  businessDate: isoDate2,
  total: money2
});
var cashierHome = z22.object({
  desks: z22.array(
    z22.object({
      id: z22.int(),
      name: z22.string(),
      kind: cashDeskKind,
      isActive: z22.boolean(),
      /** The next day to close: the day after the last report, or today. Null for a main desk. */
      nextDate: isoDate2.nullable(),
      /** A sales desk's cash still to hand over (`cashOutstanding.outstanding`); 0 on a main desk. */
      outstanding: decimalString,
      /** Transfers waiting for this desk to accept. */
      incoming: z22.int(),
      /** A sales desk: whether its main desk is set, so it can send cash. */
      canSend: z22.boolean(),
      /** A sales desk the user closes days at and sends cash from. */
      canReport: z22.boolean(),
      /** A main desk's company and stash, to group the main desks; null on a sales desk. */
      companyId: z22.int().nullable(),
      companyName: z22.string().nullable(),
      stash: cashStash.nullable(),
      /** A stash A desk's shareholder, whom its loans come from; null when not set, and on any other desk. */
      shareholder: z22.string().nullable(),
      /** A stash B desk the user keeps: where it may send — the other stash B desks and the sales desks. Empty otherwise. */
      destinations: z22.array(z22.object({ id: z22.int(), name: z22.string(), kind: cashDeskKind }))
    })
  ),
  /** Submitted reports the user may accept now — not their own — for the menu's badge. */
  pendingReports: z22.int()
});
var cashOverviewMonth = z22.object({
  month: monthNumber,
  opening: decimalString,
  in: decimalString,
  out: decimalString,
  closing: decimalString
});
var cashOverview = z22.object({
  year: yearNumber,
  desks: z22.array(
    z22.object({
      deskId: z22.int(),
      name: z22.string(),
      companyId: z22.int(),
      companyName: z22.string(),
      stash: cashStash,
      isActive: z22.boolean(),
      months: z22.array(cashOverviewMonth),
      /** Sent to this desk and not accepted yet, now. */
      inTransit: decimalString
    })
  )
});
var cashOverviewQuery = z22.object({ year: z22.coerce.number().pipe(yearNumber) });

// src/catering.ts
import { z as z23 } from "zod";
var cateringSettings = gastronomySettings.extend({
  reasons: z23.array(
    z23.object({
      code: z23.string(),
      name: z23.string().nullable(),
      isCard: z23.boolean(),
      /** Bills of the last year paid by internal settlement for this reason. */
      bills: z23.int().min(0)
    })
  )
});
var setCateringCardReasons = z23.object({
  codes: z23.array(z23.string().min(1).max(20)).max(20)
});
var cateringMonth = z23.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "catering.badMonth");
var cateringSettlementSites = z23.object({
  items: z23.array(
    z23.object({
      code: z23.string(),
      name: z23.string(),
      /** A source of the site has an employee-card reason set; a site without one has nothing to settle. */
      hasCard: z23.boolean()
    })
  )
});
var cateringRateFigures = z23.object({
  rate: z23.int().nullable(),
  gross: decimalString,
  nett: decimalString.nullable(),
  vat: decimalString.nullable()
});
var cateringInvoiceFigures = z23.object({
  gross: decimalString,
  nett: decimalString.nullable(),
  vat: decimalString.nullable(),
  rates: z23.array(cateringRateFigures)
});
var cateringSettlement = z23.object({
  site: z23.object({ code: z23.string(), name: z23.string() }),
  month: cateringMonth,
  /** The card report (`cards.xlsx`): the month's employee-card settlement at the site, by card. */
  report: z23.object({
    /** Its rows: one per card. A card bill with no card number was taken from no card and is not in the report. */
    cards: z23.int().min(0),
    total: decimalString
  }),
  /** The month's card sales by VAT rate: the card bills' lines, not cancelled, at quantity × price. */
  mix: z23.object({
    lines: z23.int().min(0),
    /** Lines with no VAT rate, the `rate: null` row. Not expected. */
    unratedLines: z23.int().min(0),
    rates: z23.array(
      z23.object({
        rate: z23.int().nullable(),
        gross: decimalString,
        /** The rate's part of the card sales, 0 to 1. */
        share: z23.number()
      })
    )
  }),
  /** The companies in the order entered, each split by the mix. */
  companies: z23.array(cateringInvoiceFigures.extend({ name: z23.string() })),
  totals: cateringInvoiceFigures,
  /** The companies' gross less the report's total: not 0 means the employer's split does not add up. */
  difference: decimalString,
  /** When the companies were last saved; null when none are. */
  savedAt: isoDateTime.nullable(),
  /** The companies of the latest earlier month settled at the site, offered when this one has none. */
  previousNames: z23.array(z23.string())
});
var companyGross = z23.string().regex(/^\d{1,12}(\.\d{1,2})?$/, 'expected an amount with at most two decimals, e.g. "12.30"');
var setCateringSettlementCompanies = z23.object({
  companies: z23.array(z23.object({ name: z23.string().trim().min(1).max(100), gross: companyGross })).max(20).refine(
    (companies) => new Set(companies.map((company2) => company2.name.toLocaleLowerCase("pl"))).size === companies.length,
    { message: "catering.companyTwice" }
  )
});

// src/weather.ts
import { z as z24 } from "zod";
var weatherLocation = z24.object({
  name: z24.string().trim().min(1).max(128),
  // The real ranges of the WGS84 grid, not a plausibility box. Open-Meteo
  // silently clamps a latitude outside them to the nearest valid cell, so the
  // wrong request answers 200 with weather for somewhere else — which is worse
  // than a 400.
  latitude: z24.number().min(-90).max(90),
  longitude: z24.number().min(-180).max(180)
});
var weatherLocationResponse = weatherLocation.nullable();

// src/reports.ts
import { z as z25 } from "zod";
var employeeCostQuery = z25.object({
  year: z25.coerce.number().pipe(yearNumber)
});
var employeeCostSource = z25.enum(["payroll", "forecast", "estimate", "none"]);
var payCosts = z25.object({
  payA: z25.number(),
  payB: z25.number(),
  /** `payA + payB`, to the grosz. */
  total: z25.number()
});
var payCostSeries = z25.object({
  payA: z25.array(z25.number()),
  payB: z25.array(z25.number()),
  total: z25.array(z25.number())
});
var employeeCostForecast = z25.object({
  /** Months after the last closed one; 0 when the month is over and its hours are in. */
  ahead: z25.int().min(0),
  /** How far the forecast part may be off, either way, as a fraction. */
  band: z25.number().min(0),
  /** People whose rows payroll has checked: counted as paid, not forecast. */
  checked: z25.int().min(0),
  /** Their cost. Omitted, not nulled, without `canSeeCosts`. */
  checkedCosts: payCosts.optional()
});
var employeeCostMonth = z25.object({
  year: yearNumber,
  month: monthNumber,
  source: employeeCostSource,
  /**
   * A closed month's people its payroll paid; a forecast month's people
   * forecast to be paid; any other month's people with an agreement in force.
   */
  headcount: z25.int().min(0),
  /** Omitted, not nulled, without `canSeeCosts`. */
  costs: payCosts.optional(),
  /** Present only on a `forecast` month. */
  forecast: employeeCostForecast.optional()
});
var employeeCostDepartment = z25.object({
  id: z25.int().nullable(),
  name: z25.string(),
  headcount: z25.array(z25.int().min(0)),
  costs: payCostSeries.optional()
});
var employeeCostPosition = z25.object({
  departmentId: z25.int().nullable(),
  positionId: z25.int(),
  name: z25.string(),
  headcount: z25.array(z25.int().min(0))
});
var employeeCostBasis = z25.enum(["checked", "payroll", "agreement", "rented"]);
var employeeCostPerson = z25.object({
  year: yearNumber,
  month: monthNumber,
  employeeId: z25.int(),
  name: z25.string(),
  /** Null: the person's agreements name no department. */
  departmentId: z25.int().nullable(),
  /** The position of the agreement that places them; null without an agreement. */
  position: z25.string().nullable(),
  /** The forms of their payroll rows that month. */
  forms: z25.array(agreementForm),
  /** This department's part of the person, 0–1, by their agreements' weights; 1 unless split. */
  share: z25.number().min(0).max(1),
  /** Hours worked that month, from the timesheet — absences left out; a forecast month's forecast. */
  hours: z25.number(),
  /**
   * Hours of absence that month, by kind, from the timesheet. Omitted with
   * none, and on a forecast's own rows: their hours are worked hours only.
   */
  absences: z25.object({ holiday: z25.number(), sick: z25.number(), care: z25.number() }).optional(),
  /** Omitted, not nulled, without `canSeeCosts`. */
  costs: payCosts.optional(),
  /** Present only in a forecast month. */
  basis: employeeCostBasis.optional()
});
var employeeCostReport = z25.object({
  /** Oldest first: January of `year − 2` to December of `year`, 36 entries. */
  months: z25.array(employeeCostMonth),
  /** Sorted by name; the null department last. */
  departments: z25.array(employeeCostDepartment),
  positions: z25.array(employeeCostPosition),
  /**
   * The people of the chosen year's closed and forecast months, of the month
   * before January — the first month's comparison — and, with a forecast, of
   * the last closed month, which a forecast month is compared with. Not ordered.
   */
  people: z25.array(employeeCostPerson)
});

// src/logs.ts
import { z as z26 } from "zod";
var instant5 = z26.iso.datetime({ offset: true });
var appLogLevel = z26.enum(["error", "warn", "request"]);
var appLogEntry = z26.object({
  id: z26.int().positive(),
  time: instant5,
  level: appLogLevel,
  /** The logger's context, as `ReaderSynchronizer`; `http` for a request line. */
  context: z26.string().nullable(),
  message: z26.string(),
  stack: z26.string().nullable(),
  /** The request the line was written during; null outside one (a timer, the boot). */
  requestId: z26.string().nullable(),
  method: z26.string().nullable(),
  /** Without the query string, and with a token in the path masked. */
  path: z26.string().nullable(),
  status: z26.int().nullable(),
  durationMs: z26.int().min(0).nullable(),
  /** Who was signed in, as their name is shown elsewhere; null for an anonymous call. */
  user: z26.string().nullable()
});
var appLogPage = z26.object({
  items: z26.array(appLogEntry),
  nextBefore: z26.int().positive().nullable()
});
var appLogStatusClass = z26.enum(["4xx", "5xx"]);
var APP_LOG_PAGE_SIZE = 100;
var appLogQuery = z26.object({
  level: appLogLevel.optional(),
  status: appLogStatusClass.optional(),
  /** Part of the message, the path, the context or the person, any case. */
  q: z26.string().trim().min(1).max(200).optional(),
  requestId: z26.uuid().optional(),
  /** Only lines at or before this time: "what happened at 14:02". */
  to: instant5.optional(),
  /** The `nextBefore` of the previous page. */
  before: z26.coerce.number().int().positive().optional()
});
export {
  APP_LOG_PAGE_SIZE,
  CASH_CHECKS_TO_EXPLAIN,
  CASH_DOCUMENT_SEARCH_LIMIT,
  CASH_SLIP_CHECKS,
  DAY_OFF_TYPES,
  EMPLOYMENT_PAY_FIELDS,
  ERRORS,
  EVERYWHERE_ROLE,
  FISCAL_RATES,
  FLAGGED_FIELDS,
  FREE_FIELDS,
  HISTORY_MONTHS_MAX,
  IMPORTED_FIELDS,
  LONG_HOURS_SECONDS,
  MAX_SHIFT_SECONDS,
  PAYROLL_FORM_ORDER,
  PERMISSIONS,
  REFERENCE_KINDS,
  RENTABLE_FORMS,
  SHORT_HOURS_SECONDS,
  SYSTEM_ROLES,
  UNPAID_BILLS_LISTED,
  WORK_DAY_TYPE_BY_VALUE,
  WORK_LOG_TYPE_BY_VALUE,
  absenceDays,
  absenceSpan,
  acceptCashTransfer,
  accessAssignment,
  accessOptions,
  accessRole,
  accessRoleKey,
  accessRoleList,
  accessScope,
  accessScopeType,
  activeFilter,
  addCashBookEntry,
  addCashComment,
  addMainDesks,
  adminCompany,
  adminCompanyList,
  adminSource,
  adminSourceList,
  agreement,
  agreementDepartment,
  agreementDepartmentInput,
  agreementForm,
  agreementWrite,
  apiError,
  appLogEntry,
  appLogLevel,
  appLogPage,
  appLogQuery,
  appLogStatusClass,
  appModule,
  appUser,
  appUserList,
  assignCard,
  assignGastronomyOutlet,
  assignGastronomyRemark,
  auditEntry,
  bailiffSeizureOf,
  blockKind,
  capabilities,
  cardDecId,
  cardReaderReading,
  cardReaderSource,
  cardReaderSourceList,
  cardReaderState,
  cashBalance,
  cashBook,
  cashBookEntryKind,
  cashBookLine,
  cashBookLineKind,
  cashBookPayouts,
  cashBookQuery,
  cashCheck,
  cashCheckCode,
  cashCheckLevel,
  cashComment,
  cashDayFigures,
  cashDesk,
  cashDeskKind,
  cashDocument,
  cashDocumentLines,
  cashFiscalReport,
  cashFiscalSuggestion,
  cashFloatLevel,
  cashFoundDocument,
  cashHandoverCorrection,
  cashMonth,
  cashMonthHandover,
  cashMonthQuery,
  cashMonthRow,
  cashOutstanding,
  cashOverview,
  cashOverviewMonth,
  cashOverviewQuery,
  cashPaymentMethod,
  cashPaymentSplit,
  cashPayout,
  cashPayoutAmount,
  cashPayoutMonth,
  cashPayoutPart,
  cashPayoutParts,
  cashPayoutQuery,
  cashPayoutRow,
  cashPendingReport,
  cashPendingReports,
  cashReport,
  cashReportDraft,
  cashReportQuery,
  cashReportReason,
  cashReportState,
  cashRevisionSummary,
  cashStash,
  cashTerminalReport,
  cashTransfer,
  cashTransferState,
  cashTransfersQuery,
  cashierDeskSeries,
  cashierHome,
  cashierPaymentKind,
  cashierPaymentType,
  cashierPrinter,
  cashierSalesModule,
  cashierSeriesOption,
  cashierSettings,
  cashierTerminal,
  cashierThresholds,
  cateringMonth,
  cateringRateFigures,
  cateringSettings,
  cateringSettlement,
  cateringSettlementSites,
  closeCashMonth,
  closedShift,
  collectionQuery,
  company,
  costSource,
  createAgreement,
  createAgreementChange,
  createCashDesk,
  createCompany,
  createDayOff,
  createDevice,
  createEmployee,
  createGastronomyCategory,
  createGastronomyOutlet,
  createHotelBlock,
  createHotelMealRate,
  createHotelRent,
  createHotelRoom,
  createHotelRoomGroup,
  createSource,
  createWorkDay,
  dayOff,
  dayOffList,
  dayOffQuery,
  dayOffType,
  dayOffTypeValue,
  deactivateEmployee,
  decimalString,
  departmentNames,
  derivingModule,
  device,
  deviceList,
  deviceQuery,
  deviceTestFailure,
  deviceTestResult,
  employeeCostBasis,
  employeeCostDepartment,
  employeeCostForecast,
  employeeCostMonth,
  employeeCostPerson,
  employeeCostPosition,
  employeeCostQuery,
  employeeCostReport,
  employeeCostSource,
  employeeDetail,
  employeeList,
  employeeListItem,
  employeeMonthQuery,
  employeeQuery,
  employeeSort,
  errorParams,
  etatFraction,
  filterOptions,
  formatError,
  gastronomyAmount,
  gastronomyCategory,
  gastronomyControls,
  gastronomyCorrection,
  gastronomyDay,
  gastronomyDayItem,
  gastronomyDayItems,
  gastronomyEventDocument,
  gastronomyEventKind,
  gastronomyEventLineKind,
  gastronomyEvents,
  gastronomyForward,
  gastronomyItemLines,
  gastronomyOutlet,
  gastronomyOutletFigures,
  gastronomyPaymentGroup,
  gastronomyPayments,
  gastronomyPeriod,
  gastronomyRevenue,
  gastronomySettings,
  gastronomySettingsOutlet,
  gastronomySettingsProducts,
  gastronomySplitProducts,
  generalPreferences,
  grantPiece,
  historyFloor,
  historyMonths,
  hotelAmount,
  hotelAmountInput,
  hotelBlock,
  hotelCounts,
  hotelDay,
  hotelForward,
  hotelMealRate,
  hotelMetrics,
  hotelMoneyMode,
  hotelNightQualification,
  hotelPeriod,
  hotelRent,
  hotelRevenueDetail,
  hotelRoom,
  hotelRoomGroup,
  hotelRoomGroups,
  hotelSettings,
  hotelSettingsCandidates,
  hotelSettingsPreview,
  hotelSettingsPreviewRequest,
  hotelYoyMetrics,
  importMatchedBy,
  importUnmatchedReason,
  inactiveCandidate,
  inactiveCandidates,
  ingestCardLog,
  ingestCardLogs,
  ingestCardReaderDay,
  ingestCardReaderDayResult,
  ingestCardReaderHeartbeat,
  ingestFiscalDocument,
  ingestFiscalDocumentLine,
  ingestFiscalDocumentPayment,
  ingestGastronomyDay,
  ingestGastronomyForward,
  ingestGastronomyReference,
  ingestGastronomyResult,
  ingestHeartbeat,
  ingestHotelDay,
  ingestHotelForward,
  ingestHotelReservation,
  ingestHotelResult,
  ingestMode,
  ingestResult,
  inviteAppUser,
  ipv4,
  isBailiffComponent,
  isCountedCell,
  isErrorCode,
  isPayrollFieldEditable,
  isRentableForm,
  isSickBenefitComponent,
  isoDate,
  isoDateTime,
  leadTimeBucket,
  logsPreview,
  meResponse,
  moduleSource,
  moduleSources,
  money,
  monthCell,
  monthCellLog,
  monthCosts,
  monthDay,
  monthDayLogs,
  monthDayTotal,
  monthEmployeeRow,
  monthLockState,
  monthNumber,
  monthQuery,
  monthResponse,
  monthStatus,
  moveGastronomyGroup,
  moveGastronomyProduct,
  needsExplanation,
  nightQualification,
  nightStatus,
  openShift,
  otbComparison,
  otbLag,
  otbPoint,
  paginated,
  payBSchema,
  payCash,
  payCostSeries,
  payCosts,
  payrollByForm,
  payrollClosePreview,
  payrollCloseRequest,
  payrollComponent,
  payrollEntry,
  payrollHoursBreakdown,
  payrollImportConfirm,
  payrollImportPreview,
  payrollImportRequest,
  payrollImportResult,
  payrollImportRow,
  payrollMonth,
  payrollMonthParams,
  payrollReopenRequest,
  payrollRestorePreview,
  payrollRestoreRequest,
  payrollSummarySplit,
  payrollTotalsOf,
  permission,
  permissionGrant,
  pickup,
  placeGastronomyGroup,
  placeGastronomyProduct,
  positionOption,
  preferencesResponse,
  priceGap,
  priceGapNightRef,
  priceGapPanelRow,
  qualifyHotelNights,
  rateUnit,
  recalculateLogsRequest,
  recalculateLogsResult,
  recalculatePreview,
  recalculatePreviewQuery,
  recalculateRequest,
  recalculateResult,
  referenceInput,
  referenceItem,
  referenceKind,
  referenceList,
  referenceOption,
  referenceQuery,
  refineDisabled,
  refuseCashTransfer,
  rentUnit,
  rentedMargin,
  roomGridCell,
  salesLine,
  saveAccessRole,
  saveCashReportDraft,
  savePrinter,
  saveTerminal,
  scopeTypesOf,
  scopeTypesOfRole,
  screenKey,
  screenPreferences,
  sendCashTransfer,
  setAppUserEnabled,
  setCashBookDate,
  setCashDeskSeries,
  setCashierPaymentType,
  setCateringCardReasons,
  setCateringSettlementCompanies,
  setGastronomySplitProduct,
  setPayrollReady,
  sickBenefitOf,
  source,
  sourceKind,
  sourceTokenIssued,
  submitCashReport,
  synchronizeRequest,
  synchronizeResult,
  systemRoleKey,
  takesHistoryWindow,
  todayResponse,
  updateAgreement,
  updateAppUser,
  updateCashBookEntry,
  updateCashDesk,
  updateCompany,
  updateDayOff,
  updateDevice,
  updateEmployee,
  updateGastronomyCategory,
  updateGastronomyOutlet,
  updateHotelBlock,
  updateHotelMealRate,
  updateHotelRent,
  updateHotelRoom,
  updateHotelRoomGroup,
  updatePayrollEntry,
  updateSource,
  updateWorkDay,
  updateWorkLog,
  warsawDateOf,
  weatherCondition,
  weatherDay,
  weatherLocation,
  weatherLocationResponse,
  workDayDetail,
  workDayType,
  workDayWarning,
  workLog,
  workLogItem,
  workLogList,
  workLogQuery,
  workLogType,
  yearMonth,
  yearNumber
};
//# sourceMappingURL=index.mjs.map